import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Badge, Button, Card, Checkbox, Input, InputNumber, Modal, Select, Switch, Table, Tabs, Tooltip, Upload, message, Tag, Spin, Segmented, type InputRef } from 'antd';
import { CopyOutlined, InfoCircleOutlined, CloseOutlined, ExclamationCircleFilled, EyeOutlined, DeleteOutlined, PrinterOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UploadFile } from 'antd/es/upload/interface';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth/mammoth.browser';
import DOMPurify from 'dompurify';
import { PDFDocument } from 'pdf-lib';
import { getAllWarehouses } from '../../services/warehouse';
import { getCurrentUserProjects } from '../../services/project';
import { getLogisticsProviders, type LogisticsProvider } from '../../services/logistics';
import { lookupLiuUnits } from '../../services/inventory';
import { DataValidator } from '../../utils/dataValidation';
import request from '../../utils/request';
import { auditWaybillFile, type WaybillAuditIssue } from '../../utils/waybillAudit';
import { HelpHintButton } from '../../components/HelpHintButton';
import { useLocation, useNavigate } from 'react-router-dom';

type DropshipRow = {
  key: string;
  is_child?: boolean;
  parent_key?: string;
  waybill_source?: string;
  waybill_submit_mode?: string;
  warehouse?: string;
  serial_no?: string;
  project?: string;
  inventory_unit_tag?: string;
  inventory_unit?: string;
  available_qty?: number;
  quantity?: number;
  need_pack?: boolean;
  has_logistics_package?: boolean;
  pack_preference?: string;
  name?: string;
  company_name?: string;
  carrier?: string;
  tracking_no?: string;
  recipient?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  email?: string;
  buyer_note?: string;
  need_signature?: boolean;
  need_insurance?: boolean;
  insurance_amount?: number;
  preferred_carrier?: string;
  reference_no?: string;
  instruction_name?: string;
  remarks?: string;
  /** 仅作列 key，用于「仅显示必填项」时控制物流运单列显示，实际数据在 waybillFilesByRowKey */
  waybill_attachment?: unknown;
};
type CopyFieldKey = keyof DropshipRow | 'other_attachments' | 'other_attachments_with_print';

type InstructionTemplateItem = {
  id?: number;
  name?: string;
  system_code?: string;
  title?: string;
  display_name?: string;
};

type LiuUnitOption = {
  inventory_unit_id: number;
  inventory_unit_code: string;
  inventory_unit_tag?: string;
  inventory_unit_tags?: string[];
  inventory_unit_tag_source?: string;
  sorting_codes?: string[];
  custom_codes?: string[];
  has_logistics_package?: boolean;
  available_qty?: number;
  project_id?: number;
  project_code?: string;
  project_name?: string;
  sorting_group_id?: number;
  sorting_group_code?: string;
};

const InstructionBulkCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const BULK_DRAFT_KEY = 'bulk_create_draft_v1';
  const BULK_DB_NAME = 'bulk_create_draft';
  const BULK_DB_STORE = 'files';
  const restoreRef = useRef(false);
  const savingRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);
  const savedFileUidRef = useRef<Set<string>>(new Set());
  const openBulkDraftDb = () => new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(BULK_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(BULK_DB_STORE)) {
        db.createObjectStore(BULK_DB_STORE, { keyPath: 'uid' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  const idbPutFiles = async (items: Array<{ uid: string; blob: Blob; name: string; type: string; lastModified: number }>) => {
    if (items.length === 0) return;
    const db = await openBulkDraftDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(BULK_DB_STORE, 'readwrite');
      const store = tx.objectStore(BULK_DB_STORE);
      items.forEach((item) => store.put(item));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  };
  const idbDeleteMissing = async (keepUids: Set<string>) => {
    const db = await openBulkDraftDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(BULK_DB_STORE, 'readwrite');
      const store = tx.objectStore(BULK_DB_STORE);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        (req.result || []).forEach((key) => {
          const uid = String(key || '');
          if (uid && !keepUids.has(uid)) store.delete(uid);
        });
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  };
  const idbGetFiles = async (uids: string[]) => {
    if (uids.length === 0) return new Map<string, Blob>();
    const db = await openBulkDraftDb();
    const result = new Map<string, Blob>();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(BULK_DB_STORE, 'readonly');
      const store = tx.objectStore(BULK_DB_STORE);
      uids.forEach((uid) => {
        const req = store.get(uid);
        req.onsuccess = () => {
          if (req.result?.blob) result.set(uid, req.result.blob as Blob);
        };
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
    return result;
  };
  const [immediateRows, setImmediateRows] = useState<DropshipRow[]>([]);
  const [afterPackRows, setAfterPackRows] = useState<DropshipRow[]>([]);
  const [warehouseRows, setWarehouseRows] = useState<DropshipRow[]>([]);
  const [immediatePage, setImmediatePage] = useState(1);
  const [immediatePageSize, setImmediatePageSize] = useState(20);
  const [afterPackPage, setAfterPackPage] = useState(1);
  const [afterPackPageSize, setAfterPackPageSize] = useState(20);
  const [warehousePage, setWarehousePage] = useState(1);
  const [warehousePageSize, setWarehousePageSize] = useState(20);
  const [immediateRequiredOnly, setImmediateRequiredOnly] = useState(true);
  const [afterPackRequiredOnly, setAfterPackRequiredOnly] = useState(true);
  const [warehouseRequiredOnly, setWarehouseRequiredOnly] = useState(true);
  const [immediateWaybillAuditOn, setImmediateWaybillAuditOn] = useState(false);
  const [afterPackWaybillAuditOn, setAfterPackWaybillAuditOn] = useState(false);
  const [warehouseWaybillAuditOn, setWarehouseWaybillAuditOn] = useState(false);
  const [batchEditOpen, setBatchEditOpen] = useState(false);
  const [batchEditFields, setBatchEditFields] = useState<Array<keyof DropshipRow>>([]);
  const [batchEditActiveKeys, setBatchEditActiveKeys] = useState<Array<keyof DropshipRow>>([]);
  const [batchEditTouchedKeys, setBatchEditTouchedKeys] = useState<Array<keyof DropshipRow>>([]);
  const [batchEditValues, setBatchEditValues] = useState<Partial<DropshipRow>>({});
  const [batchEditLiuKey, setBatchEditLiuKey] = useState('');
  const [batchEditLiuLabel, setBatchEditLiuLabel] = useState('');
  const [batchEditLiuOptions, setBatchEditLiuOptions] = useState<LiuUnitOption[]>([]);
  const [batchEditLiuAllowed, setBatchEditLiuAllowed] = useState(false);
  const [batchEditLiuReason, setBatchEditLiuReason] = useState('');
  const [batchEditPrintConfigEnabled, setBatchEditPrintConfigEnabled] = useState(false);
  const [batchEditPrintConfig, setBatchEditPrintConfig] = useState<{ print: boolean; copies: number; paper_size: string; placement: string }>({ print: false, copies: 1, paper_size: '', placement: '' });
  const [batchEditConfig, setBatchEditConfig] = useState<{
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    selectedKeys: React.Key[];
    rows: DropshipRow[];
    requiredOnly: boolean;
    requiredKeys: Array<keyof DropshipRow>;
  } | null>(null);
  const [trackingBatchOpen, setTrackingBatchOpen] = useState(false);
  const [trackingBatchText, setTrackingBatchText] = useState('');
  const [trackingBatchConfig, setTrackingBatchConfig] = useState<{
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    selectedKeys: React.Key[];
    rows: DropshipRow[];
  } | null>(null);
  const [waybillBatchOpen, setWaybillBatchOpen] = useState(false);
  const [waybillBatchFiles, setWaybillBatchFiles] = useState<UploadFile[]>([]);
  const [waybillBatchUploadMode, setWaybillBatchUploadMode] = useState<'normal' | 'split'>('normal');
  const [waybillBatchConfig, setWaybillBatchConfig] = useState<{
    selectedKeys: React.Key[];
    rows: DropshipRow[];
  } | null>(null);
  const [waybillBatchExistingRowsSnapshot, setWaybillBatchExistingRowsSnapshot] = useState<DropshipRow[]>([]);
  const [otherBatchOpen, setOtherBatchOpen] = useState(false);
  const [otherBatchFiles, setOtherBatchFiles] = useState<UploadFile[]>([]);
  const [otherBatchConfig, setOtherBatchConfig] = useState<{
    selectedKeys: React.Key[];
    rows: DropshipRow[];
  } | null>(null);
  const [otherBatchLeftUnmatchedOnly, setOtherBatchLeftUnmatchedOnly] = useState(false);
  const [otherBatchRightUnmatchedOnly, setOtherBatchRightUnmatchedOnly] = useState(false);
  const [otherBatchMatchedByFileUid, setOtherBatchMatchedByFileUid] = useState<Record<string, string>>({});
  const [otherManualMatchOpen, setOtherManualMatchOpen] = useState(false);
  const [otherManualMatchValue, setOtherManualMatchValue] = useState('');
  const [otherManualMatchFile, setOtherManualMatchFile] = useState<UploadFile | null>(null);
  const [waybillBatchLeftUnmatchedOnly, setWaybillBatchLeftUnmatchedOnly] = useState(false);
  const [waybillBatchRightUnmatchedOnly, setWaybillBatchRightUnmatchedOnly] = useState(false);
  const [waybillBatchMatchedByFileUid, setWaybillBatchMatchedByFileUid] = useState<Record<string, string>>({});
  const [waybillBatchDuplicateFileUidSet, setWaybillBatchDuplicateFileUidSet] = useState<Record<string, boolean>>({});
  const [waybillManualMatchOpen, setWaybillManualMatchOpen] = useState(false);
  const [waybillManualMatchValue, setWaybillManualMatchValue] = useState('');
  const [waybillManualMatchFile, setWaybillManualMatchFile] = useState<UploadFile | null>(null);
  const [waybillPreviewOpen, setWaybillPreviewOpen] = useState(false);
  const [waybillPreviewUrl, setWaybillPreviewUrl] = useState('');
  const [waybillPreviewTitle, setWaybillPreviewTitle] = useState('');
  const [waybillPreviewType, setWaybillPreviewType] = useState<'image' | 'pdf' | 'table' | 'html' | 'file' | 'none'>('none');
  const [waybillPreviewLoading, setWaybillPreviewLoading] = useState(false);
  const [waybillPreviewHtml, setWaybillPreviewHtml] = useState('');
  const [waybillPreviewTable, setWaybillPreviewTable] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const waybillPreviewUrlRef = useRef('');
  const [waybillBatchAuditOn, setWaybillBatchAuditOn] = useState(false);
  const [waybillBatchAuditInfoOpen, setWaybillBatchAuditInfoOpen] = useState(false);
  const [waybillBatchAuditOpen, setWaybillBatchAuditOpen] = useState(false);
  const [waybillBatchAuditResults, setWaybillBatchAuditResults] = useState<Array<{ serial: string; fileName: string; label: string; message: string; fileUid?: string }>>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState<'dropship' | 'destroy'>('dropship');
  const [activeTabKey, setActiveTabKey] = useState<'upload_immediate' | 'upload_after_pack' | 'warehouse_provide'>('upload_immediate');
  type FilterState = {
    warehouses: string[];
    needPack: 'all' | 'yes' | 'no';
    carriers: string[];
    orderType: 'all' | 'single_one' | 'single_multi' | 'multi';
    searchProject: string;
    searchInventoryUnit: string;
    searchInventoryTag: string;
    searchRecipient: string;
    searchTracking: string;
    searchRefNotes: string;
  };
  const defaultFilters: FilterState = {
    warehouses: [],
    needPack: 'all',
    carriers: [],
    orderType: 'all',
    searchProject: '',
    searchInventoryUnit: '',
    searchInventoryTag: '',
    searchRecipient: '',
    searchTracking: '',
    searchRefNotes: '',
  };
  const [filtersByTab, setFiltersByTab] = useState<Record<'IM' | 'AP' | 'WH', FilterState>>({
    IM: { ...defaultFilters },
    AP: { ...defaultFilters },
    WH: { ...defaultFilters },
  });
  const currentFilterKey = activeTabKey === 'upload_immediate' ? 'IM' : activeTabKey === 'upload_after_pack' ? 'AP' : 'WH';
  const currentFilters = filtersByTab[currentFilterKey];
  const setCurrentFilters = (updater: React.SetStateAction<FilterState>) => {
    setFiltersByTab((prev) => ({
      ...prev,
      [currentFilterKey]: typeof updater === 'function' ? (updater as (v: FilterState) => FilterState)(prev[currentFilterKey]) : updater,
    }));
  };
  const hasAnyFilter = (f: FilterState) => (
    f.warehouses.length > 0
    || f.needPack !== 'all'
    || f.carriers.length > 0
    || f.orderType !== 'all'
    || !!f.searchProject
    || !!f.searchInventoryUnit
    || !!f.searchInventoryTag
    || !!f.searchRecipient
    || !!f.searchTracking
    || !!f.searchRefNotes
  );
  const renderFilterTags = (f: FilterState, onUpdate: (updater: React.SetStateAction<FilterState>) => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {f.warehouses.map((wh) => (
        <Tag key={`wh-${wh}`} closable onClose={() => onUpdate((prev) => ({ ...prev, warehouses: prev.warehouses.filter((v) => v !== wh) }))}>
          仓库:{wh}
        </Tag>
      ))}
      {f.needPack !== 'all' ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, needPack: 'all' }))}>
          是否需打包:{f.needPack === 'yes' ? '需要' : '无需'}
        </Tag>
      ) : null}
      {f.carriers.map((c) => (
        <Tag key={`carrier-${c}`} closable onClose={() => onUpdate((prev) => ({ ...prev, carriers: prev.carriers.filter((v) => v !== c) }))}>
          承运商:{c}
        </Tag>
      ))}
      {f.orderType !== 'all' ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, orderType: 'all' }))}>
          订单类型:{f.orderType === 'single_one' ? '单款单件' : f.orderType === 'single_multi' ? '单款多件' : '多款多件'}
        </Tag>
      ) : null}
      {f.searchProject ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, searchProject: '' }))}>
          项目:{f.searchProject}
        </Tag>
      ) : null}
      {f.searchInventoryUnit ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, searchInventoryUnit: '' }))}>
          库存单元:{f.searchInventoryUnit}
        </Tag>
      ) : null}
      {f.searchInventoryTag ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, searchInventoryTag: '' }))}>
          库存单元标识:{f.searchInventoryTag}
        </Tag>
      ) : null}
      {f.searchRecipient ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, searchRecipient: '' }))}>
          收件人:{f.searchRecipient}
        </Tag>
      ) : null}
      {f.searchTracking ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, searchTracking: '' }))}>
          物流跟踪号:{f.searchTracking}
        </Tag>
      ) : null}
      {f.searchRefNotes ? (
        <Tag closable onClose={() => onUpdate((prev) => ({ ...prev, searchRefNotes: '' }))}>
          参考号/命名/备注:{f.searchRefNotes}
        </Tag>
      ) : null}
    </div>
  );
  const [batchCreateLoading, setBatchCreateLoading] = useState(false);
  const [batchCreateProgressOpen, setBatchCreateProgressOpen] = useState(false);
  const [batchCreateProgress, setBatchCreateProgress] = useState({ total: 0, done: 0, failed: 0 });
  const [draftReady, setDraftReady] = useState(false);
  const batchSummaryRef = useRef<HTMLDivElement | null>(null);
  const copyFieldOptions: Array<{ label: string; value: CopyFieldKey }> = [
    { label: '仓库', value: 'warehouse' },
    { label: '项目', value: 'project' },
    { label: '库存单元', value: 'inventory_unit' },
    { label: '库存单元标识', value: 'inventory_unit_tag' },
    { label: '可用库存', value: 'available_qty' },
    { label: '发货数量', value: 'quantity' },
    { label: '是否需打包(系统自动判断)', value: 'need_pack' },
    { label: '包装偏好', value: 'pack_preference' },
    { label: '收件人', value: 'recipient' },
    { label: '公司名称', value: 'company_name' },
    { label: '地址1', value: 'address_line_1' },
    { label: '地址2', value: 'address_line_2' },
    { label: '城市', value: 'city' },
    { label: '州', value: 'state' },
    { label: '邮编', value: 'postal_code' },
    { label: '国家', value: 'country' },
    { label: '电话', value: 'phone' },
    { label: '邮箱', value: 'email' },
    { label: '买家备注', value: 'buyer_note' },
    { label: '是否需要签名', value: 'need_signature' },
    { label: '是否需要保险', value: 'need_insurance' },
    { label: '保险金额', value: 'insurance_amount' },
    { label: '偏好物流承运商', value: 'preferred_carrier' },
    { label: '运单所属物流承运商', value: 'carrier' },
    { label: '参考号', value: 'reference_no' },
    { label: '指令命名', value: 'instruction_name' },
    { label: '指令备注', value: 'remarks' },
    { label: '其他附件', value: 'other_attachments' },
    { label: '其他附件(含打印需求设置)', value: 'other_attachments_with_print' },
  ];
  const defaultCopyFieldKeys: Array<CopyFieldKey> = [
    'warehouse',
    'project',
    'inventory_unit',
    'inventory_unit_tag',
    'available_qty',
    'quantity',
    'need_pack',
    'pack_preference',
    'carrier',
    'waybill_source',
    'waybill_submit_mode',
  ];
  const lockedCopyFieldKeys = new Set<CopyFieldKey>(['need_pack', 'waybill_source', 'waybill_submit_mode']);
  const batchEditableFieldKeys: Array<keyof DropshipRow> = [
    'warehouse',
    'project',
    'inventory_unit_tag',
    'inventory_unit',
    'quantity',
    'pack_preference',
    'recipient',
    'company_name',
    'address_line_1',
    'address_line_2',
    'city',
    'state',
    'postal_code',
    'country',
    'phone',
    'email',
    'buyer_note',
    'need_signature',
    'need_insurance',
    'insurance_amount',
    'preferred_carrier',
    'carrier',
    'reference_no',
    'instruction_name',
    'remarks',
  ];
  const batchEditFieldOrder: Array<keyof DropshipRow> = [
    'warehouse',
    'project',
    'inventory_unit',
    'inventory_unit_tag',
    'quantity',
    'pack_preference',
    'recipient',
    'company_name',
    'address_line_1',
    'address_line_2',
    'city',
    'state',
    'postal_code',
    'country',
    'phone',
    'email',
    'buyer_note',
    'need_signature',
    'need_insurance',
    'insurance_amount',
    'preferred_carrier',
    'carrier',
    'reference_no',
    'instruction_name',
    'remarks',
  ];
  const batchEditableFieldKeySet = new Set<keyof DropshipRow>(batchEditableFieldKeys);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyFieldKeys, setCopyFieldKeys] = useState<Array<CopyFieldKey>>(defaultCopyFieldKeys);
  const [copyCount, setCopyCount] = useState(1);
  const copyFieldGroups: Array<{ title: string; keys: Array<CopyFieldKey> }> = [
    {
      title: '基础信息',
      keys: [
        'warehouse',
        'project',
        'inventory_unit',
        'inventory_unit_tag',
        'available_qty',
        'quantity',
        'need_pack',
        'pack_preference',
      ],
    },
    {
      title: '收件人信息',
      keys: [
        'recipient',
        'company_name',
        'address_line_1',
        'address_line_2',
        'city',
        'state',
        'postal_code',
        'country',
        'phone',
        'email',
        'buyer_note',
        'need_signature',
        'need_insurance',
        'insurance_amount',
      ],
    },
    {
      title: '物流与其他信息',
      keys: [
        'preferred_carrier',
        'carrier',
        'reference_no',
        'instruction_name',
        'remarks',
      ],
    },
    {
      title: '附件',
      keys: ['other_attachments', 'other_attachments_with_print'],
    },
  ];
  const updateCopyFieldKeysForGroup = (groupKeys: Array<CopyFieldKey>, values: Array<CopyFieldKey>) => {
    setCopyFieldKeys((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((key) => next.delete(key));
      let valuesToAdd = values;
      const attachmentPair: CopyFieldKey[] = ['other_attachments', 'other_attachments_with_print'];
      const hasBothInGroup = attachmentPair.every((k) => groupKeys.includes(k));
      const bothSelected = hasBothInGroup && attachmentPair.every((k) => values.includes(k));
      if (bothSelected) {
        const prevInGroup = prev.filter((k) => groupKeys.includes(k));
        const newlyAdded = values.find((k) => attachmentPair.includes(k) && !prevInGroup.includes(k));
        valuesToAdd = newlyAdded
          ? values.filter((k) => k !== (newlyAdded === 'other_attachments' ? 'other_attachments_with_print' : 'other_attachments'))
          : values.filter((k) => k !== 'other_attachments');
      }
      valuesToAdd.forEach((key) => next.add(key));
      lockedCopyFieldKeys.forEach((key) => next.add(key));
      return Array.from(next);
    });
  };
  const [copyConfig, setCopyConfig] = useState<{
    row: DropshipRow;
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    keyPrefix: string;
    keySeqRef: React.MutableRefObject<number>;
    serialPrefix: string;
    serialSeqRef: React.MutableRefObject<number>;
  } | null>(null);
  const [immediateSelectedKeys, setImmediateSelectedKeys] = useState<React.Key[]>([]);
  const [afterPackSelectedKeys, setAfterPackSelectedKeys] = useState<React.Key[]>([]);
  const [warehouseSelectedKeys, setWarehouseSelectedKeys] = useState<React.Key[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [projectOptions, setProjectOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [providerOptions, setProviderOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [liuOptionsByKey, setLiuOptionsByKey] = useState<Record<string, LiuUnitOption[]>>({});
  const [liuLoadingByKey, setLiuLoadingByKey] = useState<Record<string, boolean>>({});
  const inventoryUnitInputRefs = useRef<Record<string, InputRef | null>>({});
  const inventoryUnitTagInputRefs = useRef<Record<string, InputRef | null>>({});
  const skipNextBlurByKey = useRef<Record<string, boolean>>({});
  // 记录每一行"库存单元标识"最近一次由系统自动回填的显示值（分拣码 | 自定义码 这种拼接串）。
  // 它不是真实编码，标识框再次失焦时如果值没变，就不该再拿这个拼接串去后端当编码查询。
  const autoFilledTagByKey = useRef<Record<string, string>>({});
  const skipNextEnterByKey = useRef<Record<string, boolean>>({});
  const pendingEnterTimerByKey = useRef<Record<string, number | undefined>>({});
  const postalCodeInputRefs = useRef<Record<string, InputRef | null>>({});
  const zipLookupTimerByKey = useRef<Record<string, number | undefined>>({});
  const zipInvalidModalOpenByKey = useRef<Record<string, boolean>>({});
  const zipMismatchModalOpenByKey = useRef<Record<string, boolean>>({});
  const zipIgnoreWarningByKey = useRef<Record<string, Set<string>>>({});
  const zipMismatchHintByKey = useRef<Record<string, { zip?: string; ts?: number }>>({});
  const lastZipLookupByKey = useRef<Record<string, string>>({});
  const [addCountOpen, setAddCountOpen] = useState(false);
  const [addCountValue, setAddCountValue] = useState<number>(1);
  const [addCountConfig, setAddCountConfig] = useState<{
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    keyPrefix: string;
    keySeqRef: React.MutableRefObject<number>;
    serialPrefix: string;
    serialSeqRef: React.MutableRefObject<number>;
    defaults?: Partial<DropshipRow>;
  } | null>(null);
  const [waybillFilesByRowKey, setWaybillFilesByRowKey] = useState<Record<string, UploadFile[]>>({});
  const [otherFilesByRowKey, setOtherFilesByRowKey] = useState<Record<string, UploadFile[]>>({});
  const [dropshipTemplateDetail, setDropshipTemplateDetail] = useState<any>(null);
  const [otherAttachPrintConfigMap, setOtherAttachPrintConfigMap] = useState<Record<string, { print?: boolean; copies?: number; paper_size?: string; placement?: string; explicit_no_print?: boolean }>>({});
  const [otherPrintConfigModalFiles, setOtherPrintConfigModalFiles] = useState<UploadFile[]>([]);
  const [waybillExpandedByRowKey, setWaybillExpandedByRowKey] = useState<Record<string, boolean>>({});
  const [otherExpandedByRowKey, setOtherExpandedByRowKey] = useState<Record<string, boolean>>({});
  const PRINT_PAPER_OPTIONS = useMemo(() => [{ value: '4x6_thermal', label: '4×6英寸热敏纸打印' }, { value: 'A4', label: 'A4激光打印' }], []);
  const PRINT_PLACEMENT_OPTIONS = useMemo(() => [{ value: 'inside', label: '放入包裹内' }, { value: 'outside', label: '贴在包裹外' }], []);
  const setOtherAttachPrintConfig = useCallback((fileUid: string, partial: Partial<{ print?: boolean; copies?: number; paper_size?: string; placement?: string; explicit_no_print?: boolean }>) => {
    setOtherAttachPrintConfigMap((prev) => {
      const existing = prev[fileUid] || {};
      const merged = { ...existing, ...partial };
      if (merged.print === false) {
        merged.copies = 0;
        merged.paper_size = '';
        merged.placement = '';
      }
      return { ...prev, [fileUid]: merged };
    });
  }, []);
  const formatPrintConfigForTooltip = useCallback((cfg: { print?: boolean; copies?: number; paper_size?: string; placement?: string; explicit_no_print?: boolean }) => {
    if (cfg?.print === false && cfg?.explicit_no_print) return '明确无需打印';
    if (!cfg?.print) return '未设置打印';
    const parts: string[] = [];
    if (cfg.copies != null && cfg.copies >= 1) parts.push(`份数: ${cfg.copies}`);
    if (cfg.paper_size) parts.push(`纸张: ${PRINT_PAPER_OPTIONS.find((o) => o.value === cfg.paper_size)?.label || cfg.paper_size}`);
    if (cfg.placement) parts.push(`放置: ${PRINT_PLACEMENT_OPTIONS.find((o) => o.value === cfg.placement)?.label || cfg.placement}`);
    return parts.length > 0 ? parts.join('；') : '点击设置打印';
  }, [PRINT_PAPER_OPTIONS, PRINT_PLACEMENT_OPTIONS]);
  const [waybillAuditRunning, setWaybillAuditRunning] = useState(false);
  const waybillAuditRunningRef = useRef(0);
  const [waybillAuditModalOpen, setWaybillAuditModalOpen] = useState(false);
  const [waybillAuditFileName, setWaybillAuditFileName] = useState('');
  const [waybillAuditIssues, setWaybillAuditIssues] = useState<WaybillAuditIssue[]>([]);
  const [waybillAuditWarningByUid, setWaybillAuditWarningByUid] = useState<Record<string, boolean>>({});
  const waybillValidateCacheRef = useRef<Record<string, boolean>>({});

  const getFileExt = (name: string) => {
    const parts = String(name || '').split('.');
    return parts.length > 1 ? String(parts[parts.length - 1] || '').toLowerCase() : '';
  };
  const renderUploadToggle = (files: UploadFile[], expanded: boolean, onToggle: () => void) => {
    if (!files || files.length < 2) return null;
    return (
      <span
        style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
        onClick={onToggle}
      >
        已上传{files.length}个{expanded ? '，点击收起' : '，点击展开'}
      </span>
    );
  };
  const renderFileList = (
    files: UploadFile[],
    expanded: boolean,
    onToggle: () => void,
    onRemove: (file: UploadFile) => void,
    warningByUid?: Record<string, boolean>,
    showPreview?: boolean,
    showPrintConfig?: boolean,
    onPrintConfigClick?: (file: UploadFile) => void,
  ) => {
    if (!files || files.length === 0) return null;
    const showFiles = expanded ? files : files.slice(0, 1);
    return (
      <div style={{ marginTop: 6 }}>
        {showFiles.map((f, idx) => {
          const name = String((f as any)?.name || `附件${idx + 1}`);
          const uid = String((f as any)?.uid || name);
          const hasWarning = !!warningByUid?.[uid];
          const cfg: { print?: boolean } = showPrintConfig ? (otherAttachPrintConfigMap[uid] || {}) : {};
          return (
            <div key={`${name}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {hasWarning ? (
                <Tooltip title="运单审核提示有风险项">
                  <ExclamationCircleFilled style={{ color: '#ff4d4f', fontSize: 12 }} />
                </Tooltip>
              ) : null}
              <Tooltip title={name}>
                <div
                  style={{
                    maxWidth: 110,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                    color: '#595959',
                  }}
                >
                  {name}
                </div>
              </Tooltip>
              {showPreview ? (
                <Tooltip title="预览">
                  <EyeOutlined
                    style={{ color: '#1890ff', cursor: 'pointer', fontSize: 12 }}
                    onClick={() => openWaybillPreview(f)}
                  />
                </Tooltip>
              ) : null}
              {showPrintConfig && onPrintConfigClick ? (
                <Tooltip title={formatPrintConfigForTooltip(cfg)}>
                  <PrinterOutlined
                    style={{ color: !!cfg.print ? '#1890ff' : '#8c8c8c', cursor: 'pointer', fontSize: 12 }}
                    onClick={() => onPrintConfigClick(f)}
                  />
                </Tooltip>
              ) : null}
              <CloseOutlined
                style={{ fontSize: 12, color: '#8c8c8c', cursor: 'pointer' }}
                onClick={() => onRemove(f)}
              />
            </div>
          );
        })}
      </div>
    );
  };
  const beginWaybillRun = () => {
    waybillAuditRunningRef.current += 1;
    setWaybillAuditRunning(true);
  };
  const endWaybillRun = () => {
    waybillAuditRunningRef.current = Math.max(0, waybillAuditRunningRef.current - 1);
    if (waybillAuditRunningRef.current === 0) {
      setWaybillAuditRunning(false);
    }
  };
  const perfNow = () => (typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now());
  const perfLog = (stage: string, startedAt: number, extra: Record<string, any> = {}) => {
    try {
      const ms = Math.max(0, perfNow() - startedAt);
      console.info(`[BulkCreate][Perf] ${stage}`, { ms: Number(ms.toFixed(1)), ...extra });
    } catch {}
  };
  const validateWaybillUploads = async (files: UploadFile[], trackingNo: string) => {
    for (const f of files) {
      const uid = String(f?.uid || f?.name || '');
      if (!uid || waybillValidateCacheRef.current[uid]) {
        continue;
      }
      beginWaybillRun();
      try {
        const result = await auditWaybillFile(f, trackingNo);
        waybillValidateCacheRef.current[uid] = true;
        setWaybillAuditWarningByUid((prev) => ({ ...prev, [uid]: !result.ok }));
        if (!result.ok) {
          setWaybillAuditFileName(result.fileName || String((f as any)?.name || '附件'));
          setWaybillAuditIssues(result.issues || []);
          setWaybillAuditModalOpen(true);
        }
      } finally {
        endWaybillRun();
      }
    }
  };
  const packagingOptions = [
    { value: 'warehouse_free', label: '仓库免费包材' },
    { value: 'warehouse_paid', label: '仓库收费包材' },
    { value: 'customer_box', label: '客户自购包材(纸箱)' },
    { value: 'customer_non_box', label: '客户自购包材(非纸箱)' },
    { value: 'special', label: '特殊包装操作' },
  ];
  const batchFieldConfigMap: Record<
    string,
    { label: string; type: 'input' | 'number' | 'select' | 'switch' | 'boolean_select'; options?: Array<{ value: string; label: string }> }
  > = {
    warehouse: { label: '仓库', type: 'select', options: warehouseOptions },
    project: { label: '项目', type: 'select', options: projectOptions },
    inventory_unit: { label: '库存单元', type: 'select' },
    inventory_unit_tag: { label: '库存单元标识', type: 'select' },
    quantity: { label: '发货数量', type: 'number' },
    pack_preference: { label: '包装偏好', type: 'select', options: packagingOptions },
    recipient: { label: '收件人姓名', type: 'input' },
    company_name: { label: '公司名称', type: 'input' },
    address_line_1: { label: '地址1', type: 'input' },
    address_line_2: { label: '地址2', type: 'input' },
    city: { label: '城市', type: 'input' },
    state: { label: '州', type: 'input' },
    postal_code: { label: '邮编', type: 'input' },
    country: { label: '国家', type: 'input' },
    phone: { label: '电话', type: 'input' },
    email: { label: '邮箱', type: 'input' },
    buyer_note: { label: '买家备注', type: 'input' },
    need_signature: { label: '是否需要签名', type: 'boolean_select' },
    need_insurance: { label: '是否需要保险', type: 'boolean_select' },
    insurance_amount: { label: '保险金额($)', type: 'number' },
    preferred_carrier: { label: '偏好物流承运商', type: 'select', options: providerOptions },
    carrier: { label: '运单所属物流承运商', type: 'select', options: providerOptions },
    reference_no: { label: '参考号', type: 'input' },
    instruction_name: { label: '指令命名', type: 'input' },
    remarks: { label: '指令备注', type: 'input' },
  };
  const liuUnitOptions = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();
    Object.values(liuOptionsByKey).forEach((list) => {
      list.forEach((u) => {
        const code = String(u.inventory_unit_code || '').trim();
        if (code && !map.has(code)) {
          map.set(code, { value: code, label: code });
        }
      });
    });
    return Array.from(map.values());
  }, [liuOptionsByKey]);

  const liuUnitTagOptions = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();
    Object.values(liuOptionsByKey).forEach((list) => {
      list.forEach((u) => {
        const tags = [
          String(u.inventory_unit_tag || '').trim(),
          ...((u.inventory_unit_tags || []).map((t) => String(t || '').trim())),
        ].filter(Boolean);
        tags.forEach((tag) => {
          if (!map.has(tag)) {
            map.set(tag, { value: tag, label: tag });
          }
        });
      });
    });
    return Array.from(map.values());
  }, [liuOptionsByKey]);
  const buildLiuTagMap = (options: LiuUnitOption[]) => {
    const map = new Map<string, string>();
    options.forEach((u) => {
      const unit = String(u.inventory_unit_code || '').trim();
      if (!unit || map.has(unit)) return;
      const tag = String(u.inventory_unit_tag || (u.inventory_unit_tags || [])[0] || '').trim();
      if (tag) map.set(unit, tag);
    });
    return map;
  };
  const buildLiuUnitMapByTag = (options: LiuUnitOption[]) => {
    const map = new Map<string, string>();
    options.forEach((u) => {
      const unit = String(u.inventory_unit_code || '').trim();
      if (!unit) return;
      const tags = [
        String(u.inventory_unit_tag || '').trim(),
        ...((u.inventory_unit_tags || []).map((t) => String(t || '').trim())),
      ].filter(Boolean);
      tags.forEach((tag) => {
        if (!map.has(tag)) map.set(tag, unit);
      });
    });
    return map;
  };
  const buildLiuTagDisplay = (unit: LiuUnitOption) => {
    const sortingCodes = Array.isArray(unit.sorting_codes) ? unit.sorting_codes : [];
    const customCodes = Array.isArray(unit.custom_codes) ? unit.custom_codes : [];
    const sortingText = sortingCodes.filter(Boolean).join(' ').toUpperCase();
    const customText = customCodes.filter(Boolean).join(' ').toUpperCase();
    if (sortingText && customText) return `${sortingText} | ${customText}`;
    if (sortingText) return sortingText;
    if (customText) return customText;
    const fallback = String(unit.inventory_unit_tag || '').trim();
    return fallback ? fallback.toUpperCase() : '';
  };
  const immediateKeySeq = useRef(1);
  const afterPackKeySeq = useRef(1);
  const warehouseKeySeq = useRef(1);
  const immediateSerialSeq = useRef(1);
  const afterPackSerialSeq = useRef(1);
  const warehouseSerialSeq = useRef(1);
  const BULK_DEBUG = (window as any).__BULK_CREATE_DEBUG__ === true;
  const debugRows = (label: string, rows: DropshipRow[], prefix: string, keySeq?: number, serialSeq?: number) => {
    if (!BULK_DEBUG) return;
    try {
      const keys = rows.map((r) => String(r.key || ''));
      const serials = rows.map((r) => String(r.serial_no || '').trim());
      const dupKeys = keys.filter((k, i) => k && keys.indexOf(k) !== i);
      const dupSerials = serials.filter((s, i) => s && serials.indexOf(s) !== i);
      console.groupCollapsed(`[BulkCreate][Debug] ${label}`);
      console.log('prefix', prefix, 'rows', rows.length, 'keySeq', keySeq, 'serialSeq', serialSeq);
      console.log('dupKeys', Array.from(new Set(dupKeys)));
      console.log('dupSerials', Array.from(new Set(dupSerials)));
      console.log('sample', rows.slice(0, 5).map((r) => ({
        key: r.key,
        serial: r.serial_no,
        is_child: r.is_child,
        parent_key: r.parent_key,
      })));
      console.groupEnd();
    } catch (err) {
      console.warn('[BulkCreate][Debug] failed', err);
    }
  };
  const debugSeqMismatch = (label: string, rows: DropshipRow[], prefix: string, keySeq: number, serialSeq: number) => {
    if (!BULK_DEBUG) return;
    const keyPrefix = `${prefix.toLowerCase()}-`;
    const maxKeySeq = rows.reduce((max, r) => {
      const key = String(r.key || '');
      if (!key.startsWith(keyPrefix)) return max;
      const n = Number(key.slice(keyPrefix.length));
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0);
    const maxSerialSeq = rows.reduce((max, r) => {
      const sn = String(r.serial_no || '').trim();
      if (!sn.startsWith(prefix)) return max;
      const n = Number(sn.slice(prefix.length));
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0);
    console.log('[BulkCreate][Debug] seq snapshot', {
      label,
      prefix,
      keySeq,
      serialSeq,
      maxKeySeq,
      maxSerialSeq,
      rowCount: rows.length,
    });
  };
  const getMaxKeySeq = (rows: DropshipRow[], keyPrefix: string) => {
    const prefix = `${keyPrefix}-`;
    return rows.reduce((max, r) => {
      const key = String(r.key || '');
      if (!key.startsWith(prefix)) return max;
      const n = Number(key.slice(prefix.length));
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0);
  };
  const getMaxSerialSeq = (rows: DropshipRow[], serialPrefix: string) => {
    return rows.reduce((max, r) => {
      const sn = String(r.serial_no || '').trim();
      if (!sn.startsWith(serialPrefix)) return max;
      const n = Number(sn.slice(serialPrefix.length));
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0);
  };
  const syncSeqRefs = (
    rows: DropshipRow[],
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    serialPrefix: string,
    serialSeqRef: React.MutableRefObject<number>,
  ) => {
    const maxKeySeq = getMaxKeySeq(rows, keyPrefix);
    const maxSerialSeq = getMaxSerialSeq(rows, serialPrefix);
    if (keySeqRef.current <= maxKeySeq) {
      keySeqRef.current = maxKeySeq + 1;
    }
    if (serialSeqRef.current <= maxSerialSeq) {
      serialSeqRef.current = maxSerialSeq + 1;
    }
  };
  const childEditableFields = new Set<keyof DropshipRow>([
    'project',
    'inventory_unit_tag',
    'inventory_unit',
    'quantity',
  ]);

  const buildLiuKey = (warehouse?: string, project?: string) =>
    `${String(warehouse || '').trim()}__${String(project || '').trim()}`;

  const getLiuOptionsForRow = (row: DropshipRow) => {
    const key = buildLiuKey(row.warehouse, row.project);
    return liuOptionsByKey[key] || [];
  };

  const loadLiuOptions = async (warehouse?: string, project?: string, force = false) => {
    const wh = String(warehouse || '').trim();
    const pj = String(project || '').trim();
    if (!wh || !pj) return;
    const key = buildLiuKey(wh, pj);
    if (!force && liuOptionsByKey[key]) return;
    if (liuLoadingByKey[key]) return;
    console.log('[BulkCreate][LiuLookup] fetch start', { warehouse: wh, project: pj, force });
    setLiuLoadingByKey((prev) => ({ ...prev, [key]: true }));
    try {
      const resp = await lookupLiuUnits({ warehouse: wh, project_code: pj });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      console.log('[BulkCreate][LiuLookup] fetch done', { warehouse: wh, project: pj, count: rows.length });
      setLiuOptionsByKey((prev) => ({ ...prev, [key]: rows }));
    } catch (error: any) {
      console.log('[BulkCreate][LiuLookup] fetch failed', {
        warehouse: wh,
        project: pj,
        detail: error?.response?.data?.detail,
      });
      setLiuOptionsByKey((prev) => ({ ...prev, [key]: [] }));
      message.warning(error?.response?.data?.detail || '获取库存单元失败');
    } finally {
      setLiuLoadingByKey((prev) => ({ ...prev, [key]: false }));
    }
  };

  type NeedPackDecision = {
    ready: boolean;
    needPack?: boolean;
    defaultPreference?: string;
  };

  const buildNeedPackMap = (rows: DropshipRow[]) => {
    const grouped = new Map<string, DropshipRow[]>();
    rows.forEach((row) => {
      const serial = String(row.serial_no || '').trim();
      if (!serial) return;
      const list = grouped.get(serial) || [];
      list.push(row);
      grouped.set(serial, list);
    });
    const map = new Map<string, NeedPackDecision>();
    grouped.forEach((group, serial) => {
      const ready = group.every((r) => {
        const qty = Number(r.quantity ?? 0);
        return (
          !!r.warehouse &&
          !!r.project &&
          !!r.inventory_unit &&
          Number.isFinite(qty) &&
          qty > 0
        );
      });
      if (!ready) {
        map.set(serial, { ready: false });
        return;
      }
      const mainRow = group.find((r) => !r.is_child) || group[0];
      if (!mainRow) {
        map.set(serial, { ready: false });
        return;
      }
      const quantity = Number(mainRow.quantity || 0);
      const multi = group.length > 1;
      let needPack = false;
      if (multi) {
        needPack = true;
      } else if (quantity > 1) {
        needPack = true;
      } else {
        needPack = !(mainRow.has_logistics_package ?? false);
      }
      const defaultPreference = mainRow.pack_preference || 'warehouse_free';
      map.set(serial, { ready: true, needPack, defaultPreference });
    });
    return map;
  };
  const normalizeText = (value: any) => String(value || '').trim().toLowerCase();
  const matchText = (value: any, query: string) => normalizeText(value).includes(query);
  const matchAny = (values: any[], query: string) => values.some((v) => matchText(v, query));
  const buildFilteredRows = (rows: DropshipRow[], filters: FilterState) => {
    const queryProject = normalizeText(filters.searchProject);
    const queryInventoryUnit = normalizeText(filters.searchInventoryUnit);
    const queryInventoryTag = normalizeText(filters.searchInventoryTag);
    const queryRecipient = normalizeText(filters.searchRecipient);
    const queryTracking = normalizeText(filters.searchTracking);
    const queryRefNotes = normalizeText(filters.searchRefNotes);
    const hasFilter = filters.warehouses.length > 0
      || filters.needPack !== 'all'
      || filters.carriers.length > 0
      || filters.orderType !== 'all'
      || queryProject
      || queryInventoryUnit
      || queryInventoryTag
      || queryRecipient
      || queryTracking
      || queryRefNotes;
    if (!hasFilter) return rows;
    const grouped = new Map<string, DropshipRow[]>();
    rows.forEach((row) => {
      const serial = String(row.serial_no || '').trim() || `__row__${String(row.key || '')}`;
      const list = grouped.get(serial) || [];
      list.push(row);
      grouped.set(serial, list);
    });
    const orderTypeBySerial = new Map<string, 'single_one' | 'single_multi' | 'multi'>();
    grouped.forEach((group, serial) => {
      const unitSet = new Set<string>();
      let qtySum = 0;
      group.forEach((r) => {
        const unit = String(r.inventory_unit || '').trim();
        if (unit) unitSet.add(unit);
        qtySum += Number(r.quantity || 0) || 0;
      });
      const unitKinds = unitSet.size;
      if (unitKinds <= 1) {
        orderTypeBySerial.set(serial, qtySum <= 1 ? 'single_one' : 'single_multi');
      } else {
        orderTypeBySerial.set(serial, 'multi');
      }
    });
    const matchedSerials = new Set<string>();
    grouped.forEach((group, serial) => {
      const main = group.find((r) => !r.is_child) || group[0];
      if (!main) return;
      if (filters.warehouses.length > 0) {
        const wh = String(main.warehouse || '').trim();
        if (!filters.warehouses.includes(wh)) return;
      }
      if (filters.needPack === 'yes' && main.need_pack !== true) return;
      if (filters.needPack === 'no' && main.need_pack !== false) return;
      if (filters.carriers.length > 0) {
        const carrier = String(main.carrier || '').trim();
        if (!filters.carriers.includes(carrier)) return;
      }
      if (filters.orderType !== 'all') {
        const orderType = orderTypeBySerial.get(serial);
        if (orderType !== filters.orderType) return;
      }
      if (queryProject && !group.some((r) => matchText(r.project, queryProject))) return;
      if (queryRecipient && !matchText(main.recipient, queryRecipient)) return;
      if (queryTracking && !matchAny([main.tracking_no], queryTracking)) return;
      if (queryRefNotes && !matchAny([main.reference_no, main.instruction_name, main.remarks], queryRefNotes)) return;
      if (queryInventoryUnit && !group.some((r) => matchText(r.inventory_unit, queryInventoryUnit))) return;
      if (queryInventoryTag && !group.some((r) => matchText(r.inventory_unit_tag, queryInventoryTag))) return;
      matchedSerials.add(serial);
    });
    return rows.filter((row) => {
      const serial = String(row.serial_no || '').trim() || `__row__${String(row.key || '')}`;
      return matchedSerials.has(serial);
    });
  };

  const enforceNeedPackForRows = (
    rows: DropshipRow[],
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
  ) => {
    const map = buildNeedPackMap(rows);
    setter((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const serial = String(row.serial_no || '').trim();
        if (!serial) return row;
        const decision = map.get(serial);
        if (!decision || !decision.ready) {
          if (row.need_pack !== undefined || row.pack_preference) {
            changed = true;
            return { ...row, need_pack: undefined, pack_preference: undefined };
          }
          return row;
        }
        const target = decision.needPack;
        const defaultPref = decision.defaultPreference;
        if (row.need_pack === target && (target ? row.pack_preference : true)) {
          return row;
        }
        changed = true;
        const nextRow: DropshipRow = { ...row, need_pack: target };
        if (target && !row.pack_preference) {
          nextRow.pack_preference = defaultPref;
        }
        if (!target) {
          nextRow.pack_preference = undefined;
        }
        return nextRow;
      });
      return changed ? next : prev;
    });
  };

  const applyLiuResult = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    rowKey: string,
    result: LiuUnitOption,
  ) => {
    const buildTagDisplay = (unit: LiuUnitOption) => {
      const sortingCodes = Array.isArray(unit.sorting_codes) ? unit.sorting_codes : [];
      const customCodes = Array.isArray(unit.custom_codes) ? unit.custom_codes : [];
      const sortingText = sortingCodes.filter(Boolean).join(' ').toUpperCase();
      const customText = customCodes.filter(Boolean).join(' ').toUpperCase();
      if (sortingText && customText) return `${sortingText} | ${customText}`;
      if (sortingText) return sortingText;
      if (customText) return customText;
      const fallback = String(unit.inventory_unit_tag || '').trim();
      return fallback ? fallback.toUpperCase() : '';
    };
    if (result.project_code) {
      updateRowCell(setter, rowKey, 'project', result.project_code);
    }
    updateRowCell(setter, rowKey, 'inventory_unit', result.inventory_unit_code || '');
    autoFilledTagByKey.current[rowKey] = buildTagDisplay(result).trim().toUpperCase();
    updateRowCell(setter, rowKey, 'inventory_unit_tag', buildTagDisplay(result));
    updateRowCell(setter, rowKey, 'available_qty', result.available_qty ?? undefined);
    updateRowCell(setter, rowKey, 'has_logistics_package', result.has_logistics_package ?? undefined);
  };

  const showLiuNotFoundModal = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    field: 'inventory_unit' | 'inventory_unit_tag',
    inputValue: string,
    reason?: string,
  ) => {
    const label = field === 'inventory_unit' ? '库存单元系统编码' : '库存单元标识';
    const valueText = String(inputValue || '').trim();
    const reasonText = String(reason || '').trim();
    Modal.info({
      title: (
        <span style={{ color: '#fa541c', fontWeight: 700, fontSize: 16 }}>
          提示:未找到符合条件的库存单元
        </span>
      ),
      centered: true,
      width: 720,
      okText: '知道了',
      content: (
        <div>
          <div style={{ whiteSpace: 'pre-line', marginBottom: 12 }}>
            {`已输入:${label} ${valueText}.
未找到符合条件的库存单元,请确认输入的信息正确.`}
          </div>
          {reasonText ? (
            <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', padding: 12, borderRadius: 6, whiteSpace: 'pre-line', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: '#cf1322', marginBottom: 4 }}>具体原因:</div>
              {reasonText}
            </div>
          ) : null}
          <div style={{ background: '#fff7e6', padding: 12, borderRadius: 6, whiteSpace: 'pre-line' }}>
{`查找的库存单元需满足以下条件:
1 属于当前选定的项目
2 类型为散货库存单元(LIU)
3 所属分拣组中只包含此一种库存单元(若包含多种库存单元,无论其他库存单元是否有可用库存,此库存单元均判定为不可用)
4 在当前选定的仓库可用库存数量>0`}
          </div>
        </div>
      ),
      onOk: () => {
        updateRowCell(setter, row.key, 'inventory_unit', undefined);
        updateRowCell(setter, row.key, 'inventory_unit_tag', undefined);
        updateRowCell(setter, row.key, 'available_qty', undefined);
        setTimeout(() => {
          const refMap = field === 'inventory_unit' ? inventoryUnitInputRefs.current : inventoryUnitTagInputRefs.current;
          refMap[row.key]?.focus?.();
        }, 0);
      },
    });
  };

  const lookupByInventoryUnit = async (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
  ) => {
    const code = String(row.inventory_unit || '').trim();
    if (!code) return;
    const wh = String(row.warehouse || '').trim();
    if (!wh) {
      message.warning('请先选择仓库');
      return;
    }
    try {
      const resp = await lookupLiuUnits({
        warehouse: wh,
        project_code: row.project,
        system_barcode: code,
      });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      if (rows.length === 1) {
        applyLiuResult(setter, row.key, rows[0]);
        if (rows[0]?.project_code) {
          loadLiuOptions(wh, rows[0].project_code);
        }
      } else if (rows.length > 1) {
        message.warning('匹配到多个库存单元,请先选择项目或输入更精确的编码');
      } else {
        showLiuNotFoundModal(setter, row, 'inventory_unit', code);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        showLiuNotFoundModal(setter, row, 'inventory_unit', code, error?.response?.data?.detail);
        return;
      }
      message.warning(error?.response?.data?.detail || '库存单元校验失败');
    }
  };

  const lookupByInventoryUnitTag = async (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
  ) => {
    const tag = String(row.inventory_unit_tag || '').trim();
    if (!tag) return;
    // 标识框里是系统刚自动回填的值（对应的库存单元已经通过后端校验），且没被用户改过：
    // 不用再校验一遍，更不能把“分拣码 | 自定义码”这种拼接串当成一个编码去后端查。
    if (row.inventory_unit && autoFilledTagByKey.current[row.key] === tag.toUpperCase()) return;
    const wh = String(row.warehouse || '').trim();
    if (!wh) {
      message.warning('请先选择仓库');
      return;
    }
    try {
      const options = getLiuOptionsForRow(row);
      const matched = options.some((u) => {
        const sortingCodes = Array.isArray(u.sorting_codes) ? u.sorting_codes : [];
        const customCodes = Array.isArray(u.custom_codes) ? u.custom_codes : [];
        const sortingText = sortingCodes.filter(Boolean).join(' ').toUpperCase();
        const customText = customCodes.filter(Boolean).join(' ').toUpperCase();
        const display = sortingText && customText
          ? `${sortingText} | ${customText}`
          : (sortingText || customText || String(u.inventory_unit_tag || '').trim().toUpperCase());
        return display === String(tag).toUpperCase();
      });
      if (matched) return;
      const resp = await lookupLiuUnits({
        warehouse: wh,
        project_code: row.project,
        unit_tag: tag,
      });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      if (rows.length === 1) {
        applyLiuResult(setter, row.key, rows[0]);
        if (rows[0]?.project_code) {
          loadLiuOptions(wh, rows[0].project_code);
        }
      } else if (rows.length > 1) {
        message.warning('匹配到多个库存单元,请先选择项目或输入更精确的标识');
      } else {
        showLiuNotFoundModal(setter, row, 'inventory_unit_tag', tag);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        showLiuNotFoundModal(setter, row, 'inventory_unit_tag', tag, error?.response?.data?.detail);
        return;
      }
      message.warning(error?.response?.data?.detail || '库存单元标识校验失败');
    }
  };

  const scheduleZipLookup = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    zipRaw: string,
    force = false,
  ) => {
    const zip = String(zipRaw || '').trim();
    if (!zip) return;
    if (!/^\d{5}(?:-\d{4})?$/.test(zip)) return;
    const isZipPlus4 = /^\d{5}-\d{4}$/.test(zip);
    const timerKey = `${row.key}::postal_code`;
    if (!force && lastZipLookupByKey.current[timerKey] === zip) return;
    if (zipLookupTimerByKey.current[timerKey]) {
      window.clearTimeout(zipLookupTimerByKey.current[timerKey]!);
    }
    zipLookupTimerByKey.current[timerKey] = window.setTimeout(async () => {
      try {
        const resp = await request.post('/api/v1/instruction/instructions/address-parse/', { text: zip });
        const parsed = resp?.data?.parsed || {};
        const hasMatch = !!(
          parsed?.city
          || parsed?.state
          || parsed?.zip_lookup_city
          || parsed?.zip_lookup_state
        );
        if (!hasMatch) {
          if (lastZipLookupByKey.current[timerKey] === zip) return;
          lastZipLookupByKey.current[timerKey] = zip;
          try {
            const set = zipIgnoreWarningByKey.current[timerKey];
            if (set && set.has(zip)) return;
          } catch {}
          if (!zipInvalidModalOpenByKey.current[timerKey]) {
            zipInvalidModalOpenByKey.current[timerKey] = true;
            Modal.confirm({
              title: `未查询到匹配的邮编信息 [已输入邮编: ${zip}],请核实邮政编码是否正确.`,
              okText: '继续使用该邮编',
              cancelText: '关闭',
              maskClosable: false,
              keyboard: false,
              onOk: () => {
                try {
                  if (!zipIgnoreWarningByKey.current[timerKey]) {
                    zipIgnoreWarningByKey.current[timerKey] = new Set<string>();
                  }
                  zipIgnoreWarningByKey.current[timerKey].add(zip);
                } catch {}
                zipInvalidModalOpenByKey.current[timerKey] = false;
              },
              onCancel: () => {
                zipInvalidModalOpenByKey.current[timerKey] = false;
                postalCodeInputRefs.current[row.key]?.focus?.();
              },
            });
          }
          return;
        }
        const normalizeCity = (v: string) => String(v || '').trim().toLowerCase().replace(/\./g, '');
        const normalizeState = (v: string) => String(v || '').trim().toUpperCase();
        const zipCity = String(parsed?.zip_lookup_city || parsed?.city || '').trim();
        const zipState = String(parsed?.zip_lookup_state || parsed?.state || '').trim();
        const currCity = String(row.city || '').trim();
        const currState = String(row.state || '').trim();
        const hasZipCheck = !!(zipCity || zipState);
        const mismatch = hasZipCheck && (
          (zipCity && currCity && normalizeCity(zipCity) !== normalizeCity(currCity))
          || (zipState && currState && normalizeState(zipState) !== normalizeState(currState))
        );
        const picked: Record<string, string> = {};
        if (parsed?.city) picked.city = parsed.city;
        if (parsed?.state) picked.state = parsed.state;
        if (parsed?.postal_code && !isZipPlus4) picked.postal_code = parsed.postal_code;
        const applyPicked = () => {
          if (Object.keys(picked).length === 0) return;
          Object.entries(picked).forEach(([k, v]) => {
            updateRowCell(setter, row.key, k as keyof DropshipRow, v);
          });
        };
        if (Object.keys(picked).length > 0) {
          if (mismatch) {
            if (zipMismatchModalOpenByKey.current[timerKey]) return;
            try {
              const now = Date.now();
              const lastZip = zipMismatchHintByKey.current[timerKey]?.zip;
              const lastTs = zipMismatchHintByKey.current[timerKey]?.ts || 0;
              if (lastZip === zip && now - lastTs < 2000) {
                return;
              }
              zipMismatchHintByKey.current[timerKey] = { zip, ts: now };
            } catch {}
            zipMismatchModalOpenByKey.current[timerKey] = true;
            Modal.confirm({
              title: '邮编自动匹配 请确认输入了正确的城市/州/邮编信息',
              width: 620,
              content: (
                <div>
                  <div>已输入: 城市 {currCity || '-'} | 州 {currState || '-'} | 邮编 {zip || '-'}</div>
                  <div>邮编匹配: 城市 {zipCity || '-'} | 州 {zipState || '-'} | 邮编 {zip || '-'}</div>
                </div>
              ),
              okText: '按邮编匹配填入',
              cancelText: '取消',
              onOk: () => applyPicked(),
              onCancel: () => {
                zipMismatchModalOpenByKey.current[timerKey] = false;
              },
              afterClose: () => {
                zipMismatchModalOpenByKey.current[timerKey] = false;
              },
            });
            lastZipLookupByKey.current[timerKey] = zip;
            return;
          }
          applyPicked();
        }
        lastZipLookupByKey.current[timerKey] = zip;
      } catch {}
    }, 300);
  };

  const handleWarehouseChange = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    value: string,
  ) => {
    updateRowCell(setter, row.key, 'warehouse', value);
    updateRowCell(setter, row.key, 'inventory_unit', undefined);
    updateRowCell(setter, row.key, 'inventory_unit_tag', undefined);
    updateRowCell(setter, row.key, 'available_qty', undefined);
    updateRowCell(setter, row.key, 'has_logistics_package', undefined);
    const childProjects = new Set<string>();
    setter((prev) =>
      prev.map((item) => {
        if (item.parent_key !== row.key) return item;
        const project = String(item.project || '').trim();
        if (project) childProjects.add(project);
        return {
          ...item,
          inventory_unit: undefined,
          inventory_unit_tag: undefined,
          available_qty: undefined,
          has_logistics_package: undefined,
        };
      }),
    );
    if (!value || !String(value).trim()) {
      updateRowCell(setter, row.key, 'quantity', undefined);
      updateRowCell(setter, row.key, 'need_pack', undefined);
      updateRowCell(setter, row.key, 'pack_preference', undefined);
      updateRowCell(setter, row.key, 'recipient', undefined);
      updateRowCell(setter, row.key, 'company_name', undefined);
      updateRowCell(setter, row.key, 'address_line_1', undefined);
      updateRowCell(setter, row.key, 'address_line_2', undefined);
      updateRowCell(setter, row.key, 'city', undefined);
      updateRowCell(setter, row.key, 'state', undefined);
      updateRowCell(setter, row.key, 'postal_code', undefined);
      updateRowCell(setter, row.key, 'country', undefined);
      updateRowCell(setter, row.key, 'phone', undefined);
      updateRowCell(setter, row.key, 'email', undefined);
      updateRowCell(setter, row.key, 'buyer_note', undefined);
      updateRowCell(setter, row.key, 'need_signature', undefined);
      updateRowCell(setter, row.key, 'need_insurance', undefined);
      updateRowCell(setter, row.key, 'insurance_amount', undefined);
      updateRowCell(setter, row.key, 'preferred_carrier', undefined);
      updateRowCell(setter, row.key, 'carrier', undefined);
      updateRowCell(setter, row.key, 'tracking_no', undefined);
      updateRowCell(setter, row.key, 'reference_no', undefined);
      updateRowCell(setter, row.key, 'instruction_name', undefined);
      updateRowCell(setter, row.key, 'remarks', undefined);
      setter((prev) =>
        prev.map((item) => {
          if (item.parent_key !== row.key) return item;
          return {
            ...item,
            inventory_unit: undefined,
            inventory_unit_tag: undefined,
            available_qty: undefined,
          };
        }),
      );
    }
    console.log('[BulkCreate][LiuLookup] warehouse changed', { warehouse: value, project: row.project });
    if (row.project) {
      loadLiuOptions(value, row.project, true);
    }
    if (value && String(value).trim()) {
      childProjects.forEach((project) => {
        loadLiuOptions(value, project, true);
      });
    }
  };

  const handleProjectChange = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    value: string,
  ) => {
    updateRowCell(setter, row.key, 'project', value);
    updateRowCell(setter, row.key, 'inventory_unit', undefined);
    updateRowCell(setter, row.key, 'inventory_unit_tag', undefined);
    updateRowCell(setter, row.key, 'available_qty', undefined);
    updateRowCell(setter, row.key, 'has_logistics_package', undefined);
    if (!value || !String(value).trim()) {
      updateRowCell(setter, row.key, 'quantity', undefined);
      updateRowCell(setter, row.key, 'need_pack', undefined);
      updateRowCell(setter, row.key, 'pack_preference', undefined);
      updateRowCell(setter, row.key, 'recipient', undefined);
      updateRowCell(setter, row.key, 'company_name', undefined);
      updateRowCell(setter, row.key, 'address_line_1', undefined);
      updateRowCell(setter, row.key, 'address_line_2', undefined);
      updateRowCell(setter, row.key, 'city', undefined);
      updateRowCell(setter, row.key, 'state', undefined);
      updateRowCell(setter, row.key, 'postal_code', undefined);
      updateRowCell(setter, row.key, 'country', undefined);
      updateRowCell(setter, row.key, 'phone', undefined);
      updateRowCell(setter, row.key, 'email', undefined);
      updateRowCell(setter, row.key, 'buyer_note', undefined);
      updateRowCell(setter, row.key, 'need_signature', undefined);
      updateRowCell(setter, row.key, 'need_insurance', undefined);
      updateRowCell(setter, row.key, 'insurance_amount', undefined);
      updateRowCell(setter, row.key, 'preferred_carrier', undefined);
      updateRowCell(setter, row.key, 'carrier', undefined);
      updateRowCell(setter, row.key, 'tracking_no', undefined);
      updateRowCell(setter, row.key, 'reference_no', undefined);
      updateRowCell(setter, row.key, 'instruction_name', undefined);
      updateRowCell(setter, row.key, 'remarks', undefined);
    }
    console.log('[BulkCreate][LiuLookup] project changed', { warehouse: row.warehouse, project: value });
    if (row.warehouse) {
      loadLiuOptions(row.warehouse, value, true);
    }
  };

  const handleInventoryUnitSelect = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    value: string,
    option: any,
  ) => {
    skipNextEnterByKey.current[`${row.key}::inventory_unit`] = true;
    const tkey = `${row.key}::inventory_unit`;
    const tid = pendingEnterTimerByKey.current[tkey];
    if (tid) {
      window.clearTimeout(tid);
      pendingEnterTimerByKey.current[tkey] = undefined;
    }
    const unit = option?.unit as LiuUnitOption | undefined;
    if (unit) {
      applyLiuResult(setter, row.key, unit);
      if (unit.project_code && row.warehouse) {
        loadLiuOptions(row.warehouse, unit.project_code);
      }
      return;
    }
    updateRowCell(setter, row.key, 'inventory_unit', value);
  };

  const handleInventoryUnitTagSelect = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    value: string,
    option: any,
  ) => {
    skipNextEnterByKey.current[`${row.key}::inventory_unit_tag`] = true;
    const tkey = `${row.key}::inventory_unit_tag`;
    const tid = pendingEnterTimerByKey.current[tkey];
    if (tid) {
      window.clearTimeout(tid);
      pendingEnterTimerByKey.current[tkey] = undefined;
    }
    const unit = option?.unit as LiuUnitOption | undefined;
    if (unit) {
      applyLiuResult(setter, row.key, unit);
      if (unit.project_code && row.warehouse) {
        loadLiuOptions(row.warehouse, unit.project_code);
      }
      return;
    }
    updateRowCell(setter, row.key, 'inventory_unit_tag', value);
  };

  const handleInventoryUnitBlur = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
  ) => {
    const blurKey = `${row.key}::inventory_unit`;
    if (skipNextBlurByKey.current[blurKey]) {
      skipNextBlurByKey.current[blurKey] = false;
      return;
    }
    lookupByInventoryUnit(setter, row);
  };

  const handleInventoryUnitTagBlur = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
  ) => {
    const blurKey = `${row.key}::inventory_unit_tag`;
    if (skipNextBlurByKey.current[blurKey]) {
      skipNextBlurByKey.current[blurKey] = false;
      return;
    }
    lookupByInventoryUnitTag(setter, row);
  };

  const updateRowCell = <K extends keyof DropshipRow>(
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    rowKey: string,
    field: K,
    value: DropshipRow[K],
  ) => {
    setter((prev) =>
      prev.map((row) => {
        if (row.key === rowKey) {
          return { ...row, [field]: value };
        }
        if (row.parent_key === rowKey && !childEditableFields.has(field) && field !== 'available_qty') {
          return { ...row, [field]: value };
        }
        return row;
      }),
    );
  };

  const createRow = (
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    defaults?: Partial<DropshipRow>,
  ) => ({
    key: `${keyPrefix}-${keySeqRef.current++}`,
    ...(defaults || {}),
  });

  const appendRow = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    defaults?: Partial<DropshipRow>,
  ) => {
    setter((prev) => [...prev, createRow(keyPrefix, keySeqRef, defaults)]);
  };

  const insertRowAfter = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    rowKey: string,
    defaults?: Partial<DropshipRow>,
  ) => {
    setter((prev) => {
      const nextRow = createRow(keyPrefix, keySeqRef, defaults);
      const index = prev.findIndex((row) => row.key === rowKey);
      if (index === -1) {
        return [...prev, nextRow];
      }
      return [...prev.slice(0, index + 1), nextRow, ...prev.slice(index + 1)];
    });
  };

  const appendMainRow = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    serialPrefix: string,
    serialSeqRef: React.MutableRefObject<number>,
    defaults?: Partial<DropshipRow>,
  ) => {
    const serialNo = `${serialPrefix}${serialSeqRef.current++}`;
    appendRow(setter, keyPrefix, keySeqRef, {
      serial_no: serialNo,
      is_child: false,
      parent_key: undefined,
      ...(defaults || {}),
    });
  };

  const appendInventoryUnitRow = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    row: DropshipRow,
  ) => {
    setter((prev) => {
      const nextRow = createRow(keyPrefix, keySeqRef, {
        is_child: true,
        parent_key: row.key,
        serial_no: row.serial_no,
        waybill_source: row.waybill_source,
        waybill_submit_mode: row.waybill_submit_mode,
        warehouse: row.warehouse,
        project: undefined,
        recipient: row.recipient,
        company_name: row.company_name,
        name: row.name,
        address_line_1: row.address_line_1,
        address_line_2: row.address_line_2,
        city: row.city,
        state: row.state,
        postal_code: row.postal_code,
        country: row.country,
        phone: row.phone,
        email: row.email,
        buyer_note: row.buyer_note,
        need_pack: row.need_pack,
        pack_preference: row.pack_preference,
        need_signature: row.need_signature,
        need_insurance: row.need_insurance,
        insurance_amount: row.insurance_amount,
        preferred_carrier: row.preferred_carrier,
        carrier: row.carrier,
        tracking_no: row.tracking_no,
        reference_no: row.reference_no,
        instruction_name: row.instruction_name,
        remarks: row.remarks,
        inventory_unit_tag: undefined,
        inventory_unit: undefined,
        available_qty: undefined,
        quantity: 1,
      });

      const serial = String(row.serial_no || '').trim();
      const lastIndex = prev.reduce((idx, r, i) => {
        if (String(r.serial_no || '').trim() === serial) return i;
        return idx;
      }, -1);

      if (lastIndex === -1) {
        return [...prev, nextRow];
      }
      return [...prev.slice(0, lastIndex + 1), nextRow, ...prev.slice(lastIndex + 1)];
    });
  };

  const openAddCountModal = (config: {
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    keyPrefix: string;
    keySeqRef: React.MutableRefObject<number>;
    serialPrefix: string;
    serialSeqRef: React.MutableRefObject<number>;
    defaults?: Partial<DropshipRow>;
  }) => {
    setAddCountValue(1);
    setAddCountConfig(config);
    setAddCountOpen(true);
  };

  const confirmAddCount = () => {
    const count = Math.floor(Number(addCountValue || 0));
    if (!Number.isFinite(count) || count <= 0) {
      message.warning('请填写大于0的新增数量');
      return;
    }
    if (!addCountConfig) {
      setAddCountOpen(false);
      return;
    }
    if (addCountConfig.serialPrefix === 'IM') {
      syncSeqRefs(immediateRows, 'immediate', immediateKeySeq, 'IM', immediateSerialSeq);
    } else if (addCountConfig.serialPrefix === 'AP') {
      syncSeqRefs(afterPackRows, 'after-pack', afterPackKeySeq, 'AP', afterPackSerialSeq);
    } else if (addCountConfig.serialPrefix === 'WH') {
      syncSeqRefs(warehouseRows, 'warehouse', warehouseKeySeq, 'WH', warehouseSerialSeq);
    }
    for (let i = 0; i < count; i += 1) {
      appendMainRow(
        addCountConfig.setter,
        addCountConfig.keyPrefix,
        addCountConfig.keySeqRef,
        addCountConfig.serialPrefix,
        addCountConfig.serialSeqRef,
        addCountConfig.defaults,
      );
    }
    if (addCountConfig.serialPrefix === 'IM') {
      debugSeqMismatch('after add count', immediateRows, 'IM', immediateKeySeq.current, immediateSerialSeq.current);
    } else if (addCountConfig.serialPrefix === 'AP') {
      debugSeqMismatch('after add count', afterPackRows, 'AP', afterPackKeySeq.current, afterPackSerialSeq.current);
    } else if (addCountConfig.serialPrefix === 'WH') {
      debugSeqMismatch('after add count', warehouseRows, 'WH', warehouseKeySeq.current, warehouseSerialSeq.current);
    }
    if (addCountConfig.serialPrefix === 'IM') {
      debugRows('after add count', immediateRows, 'IM', immediateKeySeq.current, immediateSerialSeq.current);
    } else if (addCountConfig.serialPrefix === 'AP') {
      debugRows('after add count', afterPackRows, 'AP', afterPackKeySeq.current, afterPackSerialSeq.current);
    } else if (addCountConfig.serialPrefix === 'WH') {
      debugRows('after add count', warehouseRows, 'WH', warehouseKeySeq.current, warehouseSerialSeq.current);
    }
    setAddCountOpen(false);
  };
  const clampPage = (page: number, pageSize: number, total: number) => {
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    return Math.min(page, maxPage);
  };
  const openCopyModal = (config: {
    row: DropshipRow;
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    keyPrefix: string;
    keySeqRef: React.MutableRefObject<number>;
    serialPrefix: string;
    serialSeqRef: React.MutableRefObject<number>;
  }) => {
    setCopyCount(1);
    setCopyFieldKeys(defaultCopyFieldKeys);
    setCopyConfig(config);
    setCopyModalOpen(true);
  };
  const confirmCopy = () => {
    const count = Math.floor(Number(copyCount || 0));
    if (!Number.isFinite(count) || count <= 0) {
      message.warning('请填写大于0的复制数量');
      return;
    }
    if (!copyConfig) {
      setCopyModalOpen(false);
      return;
    }
    if (copyConfig.serialPrefix === 'IM') {
      syncSeqRefs(immediateRows, 'immediate', immediateKeySeq, 'IM', immediateSerialSeq);
    } else if (copyConfig.serialPrefix === 'AP') {
      syncSeqRefs(afterPackRows, 'after-pack', afterPackKeySeq, 'AP', afterPackSerialSeq);
    } else if (copyConfig.serialPrefix === 'WH') {
      syncSeqRefs(warehouseRows, 'warehouse', warehouseKeySeq, 'WH', warehouseSerialSeq);
    }
    debugSeqMismatch('before copy', copyConfig.setter === setImmediateRows ? immediateRows : copyConfig.setter === setAfterPackRows ? afterPackRows : warehouseRows, copyConfig.serialPrefix, copyConfig.keySeqRef.current, copyConfig.serialSeqRef.current);
    const selected = new Set<CopyFieldKey>(copyFieldKeys as CopyFieldKey[]);
    const pickFields = (row: DropshipRow) => {
      const next: Partial<DropshipRow> = {};
      copyFieldKeys.forEach((key) => {
        if (!selected.has(key)) return;
        if (key === 'other_attachments' || key === 'other_attachments_with_print') return;
        (next as any)[key] = (row as any)[key];
      });
      return next;
    };
    const shouldCopyOtherAttachments = selected.has('other_attachments') || selected.has('other_attachments_with_print');
    const newMainRowKeys: string[] = [];
    copyConfig.setter((prev) => {
      const sourceSerial = String(copyConfig.row.serial_no || '').trim();
      const groupRows = prev.filter((r) => String(r.serial_no || '').trim() === sourceSerial);
      if (groupRows.length === 0) return prev;
      const lastIndex = prev.reduce((idx, r, i) => {
        if (String(r.serial_no || '').trim() === sourceSerial) return i;
        return idx;
      }, -1);
      if (lastIndex === -1) return prev;
      const sourceMain = groupRows.find((r) => !r.is_child) || groupRows[0];
      const children = groupRows.filter((r) => r.is_child);
      const inserted: DropshipRow[] = [];
      for (let i = 0; i < count; i += 1) {
        const serialNo = `${copyConfig.serialPrefix}${copyConfig.serialSeqRef.current++}`;
        const mainRow = createRow(copyConfig.keyPrefix, copyConfig.keySeqRef, {
          ...pickFields(sourceMain),
          serial_no: serialNo,
          is_child: false,
          parent_key: undefined,
          has_logistics_package: sourceMain.has_logistics_package,
          need_pack: undefined,
        });
        if (!String((mainRow as any).country || '').trim()) {
          (mainRow as any).country = 'USA';
        }
        newMainRowKeys.push(mainRow.key);
        inserted.push(mainRow);
        children.forEach((child) => {
          const childRow = createRow(copyConfig.keyPrefix, copyConfig.keySeqRef, {
            ...pickFields(child),
            serial_no: serialNo,
            is_child: true,
            parent_key: mainRow.key,
            has_logistics_package: child.has_logistics_package,
            need_pack: undefined,
          });
          if (!String((childRow as any).country || '').trim()) {
            (childRow as any).country = 'USA';
          }
          inserted.push(childRow);
        });
      }
      const nextRows = [...prev.slice(0, lastIndex + 1), ...inserted, ...prev.slice(lastIndex + 1)];
      const resequenced = resequenceSerials(nextRows, copyConfig.serialPrefix, copyConfig.serialSeqRef);
      debugRows('after copy', resequenced, copyConfig.serialPrefix, copyConfig.keySeqRef.current, copyConfig.serialSeqRef.current);
      return resequenced;
    });
    if (shouldCopyOtherAttachments) {
      const sourceFiles = otherFilesByRowKey[String(copyConfig.row.key || '')] || [];
      if (sourceFiles.length > 0 && newMainRowKeys.length > 0) {
        const seed = Date.now();
        setOtherFilesByRowKey((prev) => {
          const next = { ...prev };
          newMainRowKeys.forEach((rowKey, rowIdx) => {
            const cloned = sourceFiles.map((file, idx) => ({
              ...(file as any),
              uid: `${seed}-${rowIdx}-${idx}-${String((file as any)?.name || 'file')}`,
            })) as UploadFile[];
            next[rowKey] = cloned;
          });
          return next;
        });
        if (selected.has('other_attachments_with_print')) {
          const printUpdates: Record<string, (typeof otherAttachPrintConfigMap)[string]> = {};
          newMainRowKeys.forEach((_, rowIdx) => {
            sourceFiles.forEach((file, fileIdx) => {
              const oldUid = String((file as any)?.uid || '');
              const cfg = otherAttachPrintConfigMap[oldUid];
              if (cfg) {
                const newUid = `${seed}-${rowIdx}-${fileIdx}-${String((file as any)?.name || 'file')}`;
                printUpdates[newUid] = cfg;
              }
            });
          });
          if (Object.keys(printUpdates).length > 0) {
            setOtherAttachPrintConfigMap((prev) => ({ ...prev, ...printUpdates }));
          }
        }
      }
    }
    setCopyModalOpen(false);
  };
  const fetchDropshipTemplateId = async () => {
    try {
      const res = await request.get('/api/v1/instruction/templates/available/');
      const rows: InstructionTemplateItem[] = Array.isArray(res.data)
        ? res.data
        : (Array.isArray(res.data?.results) ? res.data.results : []);
      const targetName = '一件代发(系统级标准指令)';
      const found = rows.find((tpl) => {
        const name = String(tpl?.name || tpl?.title || tpl?.display_name || '').trim();
        return name === targetName;
      });
      const id = Number(found?.id);
      return Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  };
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const templateId = await fetchDropshipTemplateId();
        if (!templateId || !mounted) return;
        const res = await request.get(`/api/v1/instruction/templates/${templateId}/`);
        if (mounted && res?.data) {
          setDropshipTemplateDetail(res.data);
        }
      } catch {
        if (mounted) setDropshipTemplateDetail(null);
      }
    })();
    return () => { mounted = false; };
  }, []);
  const otherAttachmentPrintable = useMemo(() => {
    try {
      const req = (dropshipTemplateDetail?.attachment_requirements || {}) as any;
      const orderArr = Array.isArray(req.order) ? req.order : [];
      return orderArr.some((n: any) => !!n.printable);
    } catch {
      return false;
    }
  }, [dropshipTemplateDetail]);
  const resolveLiuOptionForRow = (row: DropshipRow, options: LiuUnitOption[]) => {
    const unitCode = String(row.inventory_unit || '').trim();
    if (unitCode) {
      return options.find((u) => String(u.inventory_unit_code || '').trim() === unitCode);
    }
    const tagText = String(row.inventory_unit_tag || '').trim();
    if (!tagText) return undefined;
    const normalizedTag = tagText.toUpperCase();
    return options.find((u) => {
      const display = buildLiuTagDisplay(u);
      if (display && display === normalizedTag) return true;
      const tags = [
        String(u.inventory_unit_tag || '').trim(),
        ...((u.inventory_unit_tags || []).map((t) => String(t || '').trim())),
      ]
        .filter(Boolean)
        .map((t) => t.toUpperCase());
      return tags.includes(normalizedTag);
    });
  };
  const buildInstructionAnswers = (row: DropshipRow) => {
    const entries: Array<[string, string | number | boolean]> = [];
    const push = (key: string, value: any) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'string' && value.trim() === '') return;
      entries.push([key, value]);
    };
    push('recipient_name', row.recipient);
    push('company_name', row.company_name);
    push('address_line_1', row.address_line_1);
    push('address_line_2', row.address_line_2);
    push('city', row.city);
    push('state', row.state);
    push('postal_code', row.postal_code);
    push('country', row.country);
    push('phone', row.phone);
    push('email', row.email);
    push('shipping_notes', row.buyer_note);
    push('signature_required', row.need_signature);
    push('insurance_required', row.need_insurance);
    push('insured_amount', row.insurance_amount);
    push('preferred_carrier', row.preferred_carrier);
    push('reference_number', row.reference_no);
    push('waybill_source', row.waybill_source);
    push('waybill_submit_mode', row.waybill_submit_mode);
    push('waybill_carrier', row.carrier);
    push('tracking_number', row.tracking_no);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  };
  const uploadInstructionAttachments = async (
    instructionId: number,
    files: UploadFile[],
    label: string,
    printConfigMap?: Record<string, { print?: boolean; copies?: number; paper_size?: string; placement?: string; explicit_no_print?: boolean }>,
  ) => {
    const startedAt = perfNow();
    let uploadedCount = 0;
    try {
      for (const file of files) {
        const origin = (file as any)?.originFileObj;
        if (!origin) continue;
        const fd = new FormData();
        fd.append('instruction', String(instructionId));
        fd.append('scope', 'order');
        fd.append('label', label);
        fd.append('file', origin as Blob);
        try { fd.append('file_path', String(file.name || '附件')); } catch {}
        if (label === '其他附件' && printConfigMap) {
          const cfg = printConfigMap[String(file.uid || '')];
          if (cfg?.print === true) {
            fd.append('meta', JSON.stringify({
              print_config: {
                print: true,
                copies: Math.max(1, Number(cfg.copies) || 1),
                paper_size: String(cfg.paper_size || ''),
                placement: String(cfg.placement || ''),
                explicit_no_print: false,
              },
            }));
          } else if (cfg?.explicit_no_print === true) {
            fd.append('meta', JSON.stringify({
              print_config: {
                print: false,
                explicit_no_print: true,
              },
            }));
          }
        }
        await request.post('/api/v1/instruction/attachments/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        });
        uploadedCount += 1;
      }
    } finally {
      perfLog('upload_attachments', startedAt, {
        instructionId,
        label,
        fileCount: Array.isArray(files) ? files.length : 0,
        uploadedCount,
      });
    }
  };
  const handleBatchCreate = async (rows: DropshipRow[], requiredKeys: Array<keyof DropshipRow> = []) => {
    if (batchCreateLoading) return;
    if (!rows || rows.length === 0) {
      message.warning('无可创建的指令记录');
      return;
    }
    const totalStartedAt = perfNow();
    setBatchCreateLoading(true);
    try {
      const templateId = await fetchDropshipTemplateId();
      if (!templateId) {
        message.error('未找到指令模板：一件代发(系统级标准指令)');
        return;
      }
      const [warehouseList, projectList] = await Promise.all([
        getAllWarehouses(),
        getCurrentUserProjects(),
      ]);
      const warehouseIdByCode = new Map(
        warehouseList.map((wh: any) => [String(wh.code || '').trim(), Number(wh.id)]),
      );
      const projectIdByCode = new Map(
        projectList.map((pj: any) => [String(pj.project_code || '').trim(), Number(pj.id)]),
      );
      const projectCustomerIdByCode = new Map(
        projectList.map((pj: any) => [String(pj.project_code || '').trim(), Number(pj.customer?.id)]),
      );
      const grouped = new Map<string, DropshipRow[]>();
      rows.forEach((row) => {
        const serial = String(row.serial_no || '').trim();
        if (!serial) return;
        const list = grouped.get(serial) || [];
        list.push(row);
        grouped.set(serial, list);
      });
      if (grouped.size === 0) {
        message.warning('未找到有效的指令编号');
        return;
      }
      perfLog('prepare_grouped', totalStartedAt, {
        activeTabKey,
        rowCount: rows.length,
        instructionCount: grouped.size,
        requiredKeyCount: requiredKeys.length,
      });
      const fieldLabels: Record<string, string> = {
        warehouse: '仓库',
        project: '项目',
        serial_no: '指令编号',
        inventory_unit_tag: '库存单元标识',
        inventory_unit: '库存单元',
        available_qty: '可用库存',
        quantity: '发货数量',
        carrier: '物流承运商',
        tracking_no: '物流跟踪号',
        recipient: '收件人',
        address_line_1: '地址1',
        address_line_2: '地址2',
        city: '城市',
        state: '州',
        postal_code: '邮编',
        country: '国家',
        phone: '电话',
        email: '邮箱',
        need_signature: '是否需要签名',
        need_insurance: '是否需要保险',
        insurance_amount: '保险金额',
      };
      const perRowRequiredKeys = new Set<keyof DropshipRow>([
        'warehouse',
        'project',
        'inventory_unit_tag',
        'inventory_unit',
        'available_qty',
        'quantity',
      ]);
      const isValueValid = (key: keyof DropshipRow, value: any) => {
        if (key === 'quantity') {
          const num = Number(value);
          return Number.isFinite(num) && num > 0;
        }
        if (key === 'insurance_amount') {
          const num = Number(value);
          return Number.isFinite(num) && num > 0;
        }
        if (typeof value === 'boolean') return true;
        if (typeof value === 'number') return Number.isFinite(value);
        return String(value || '').trim() !== '';
      };
      const missingBySerial: Array<{ serial: string; items: string[] }> = [];
      grouped.forEach((groupRows, serial) => {
        const mainRow = groupRows.find((r) => !r.is_child) || groupRows[0];
        const missing: string[] = [];
        const rowMissing: string[] = [];
        requiredKeys.forEach((key) => {
          if (key === 'waybill_attachment') return;
          if (perRowRequiredKeys.has(key)) {
            groupRows.forEach((row) => {
              if (!isValueValid(key, (row as any)[key])) {
                const label = fieldLabels[key as string] || String(key);
                if (!rowMissing.includes(label)) rowMissing.push(label);
              }
            });
            return;
          }
          if (!isValueValid(key, (mainRow as any)[key])) {
            const label = fieldLabels[key as string] || String(key);
            missing.push(label);
          }
        });
        const waybillSource = String(mainRow.waybill_source || '').trim();
        const waybillSubmitMode = String(mainRow.waybill_submit_mode || '').trim();
        const requiresWaybillAttachment = waybillSource === '上传运单' && waybillSubmitMode === '立即上传';
        if (requiresWaybillAttachment) {
          const files = waybillFilesByRowKey[String(mainRow.key || '')] || [];
          if (files.length === 0) {
            missing.push('物流运单附件');
          }
        }
        if ((mainRow as any).need_insurance === true && !isValueValid('insurance_amount', (mainRow as any).insurance_amount)) {
          missing.push('保险金额');
        }
        if (rowMissing.length > 0) {
          missing.push(`明细行缺少：${rowMissing.join('、')}`);
        }
        if (missing.length > 0) {
          missingBySerial.push({ serial: String(serial || '-'), items: missing });
        }
      });
      if (missingBySerial.length > 0) {
        Modal.error({
          title: '创建前检查：以下指令存在必填项缺失',
          content: (
            <div style={{ maxHeight: 260, overflow: 'auto' }}>
              {missingBySerial.map((item, idx) => (
                <div key={`${item.serial}-${idx}`} style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>指令{item.serial}</div>
                  <div>{item.items.join('、')}</div>
                </div>
              ))}
            </div>
          ),
          okText: '知道了',
        });
        return;
      }
      const errors: string[] = [];
      const liuOptionsByKeyLocal = new Map<string, LiuUnitOption[]>();
      const groupRowsList = Array.from(grouped.values());
      let liuLookupCount = 0;
      let liuLookupMissCount = 0;
      let liuLookupMs = 0;
      for (let gi = 0; gi < groupRowsList.length; gi += 1) {
        const groupRows = groupRowsList[gi];
        const mainRow = groupRows.find((r) => !r.is_child) || groupRows[0];
        const whCode = String(mainRow.warehouse || '').trim();
        const pjCode = String(mainRow.project || '').trim();
        if (!whCode || !pjCode) {
          errors.push(`指令${mainRow.serial_no || '-'}缺少仓库或项目`);
          continue;
        }
        if (!warehouseIdByCode.get(whCode)) {
          errors.push(`指令${mainRow.serial_no || '-'}仓库不存在(${whCode})`);
        }
        if (!projectIdByCode.get(pjCode)) {
          errors.push(`指令${mainRow.serial_no || '-'}项目不存在(${pjCode})`);
        }
        if (!projectCustomerIdByCode.get(pjCode)) {
          errors.push(`指令${mainRow.serial_no || '-'}项目未关联客户(${pjCode})`);
        }
        for (const row of groupRows) {
          const rowWh = String(row.warehouse || '').trim();
          const rowPj = String(row.project || '').trim();
          if (!rowWh || !rowPj) {
            errors.push(`指令${mainRow.serial_no || '-'}存在缺少仓库或项目的行`);
            continue;
          }
          const key = buildLiuKey(rowWh, rowPj);
          if (!liuOptionsByKeyLocal.has(key)) {
            liuLookupCount += 1;
            if (liuOptionsByKey[key]) {
              liuOptionsByKeyLocal.set(key, liuOptionsByKey[key]);
            } else {
              try {
                liuLookupMissCount += 1;
                const liuStartedAt = perfNow();
                const resp = await lookupLiuUnits({ warehouse: rowWh, project_code: rowPj });
                const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
                liuOptionsByKeyLocal.set(key, list);
                liuLookupMs += Math.max(0, perfNow() - liuStartedAt);
              } catch {
                liuOptionsByKeyLocal.set(key, []);
              }
            }
          }
        }
      }
      perfLog('liu_lookup_summary', totalStartedAt, {
        liuLookupCount,
        liuLookupMissCount,
        liuLookupMs: Number(liuLookupMs.toFixed(1)),
      });
      const instructionsToCreate: Array<{
        mainRow: DropshipRow;
        rows: DropshipRow[];
        payload: any;
        attachments: { waybill: UploadFile[]; other: UploadFile[] };
      }> = [];
      grouped.forEach((groupRows) => {
        const mainRow = groupRows.find((r) => !r.is_child) || groupRows[0];
        const whCode = String(mainRow.warehouse || '').trim();
        const pjCode = String(mainRow.project || '').trim();
        const whId = warehouseIdByCode.get(whCode);
        const pjId = projectIdByCode.get(pjCode);
        const customerId = projectCustomerIdByCode.get(pjCode);
        if (!whId || !pjId || !customerId) return;
        const targets: any[] = [];
        groupRows.forEach((row) => {
          const rowWh = String(row.warehouse || '').trim();
          const rowPj = String(row.project || '').trim();
          if (!rowWh || !rowPj) return;
          const rowProjectId = projectIdByCode.get(rowPj);
          const key = buildLiuKey(rowWh, rowPj);
          const options = liuOptionsByKeyLocal.get(key) || [];
          const opt = resolveLiuOptionForRow(row, options);
          if (!rowProjectId || !opt?.inventory_unit_id) {
            errors.push(`指令${mainRow.serial_no || '-'}存在未匹配库存单元的行`);
            return;
          }
          targets.push({
            target_type: 'inventory_unit',
            project_id: rowProjectId,
            inventory_unit_id: opt.inventory_unit_id,
            expected_qty: row.quantity ?? undefined,
            use_all_available: false,
          });
        });
        if (targets.length === 0) return;
        const answers = buildInstructionAnswers(mainRow);
        const packPreferenceMap: Record<string, string> = {
          warehouse_free: 'warehouse_free',
          warehouse_paid: 'warehouse_paid',
          customer_box: 'customer_carton',
          customer_non_box: 'customer_non_carton',
          special: 'special_packing',
        };
        const submitModeMap: Record<string, string> = {
          '立即上传': 'immediate',
          '先申请包装数据': 'after_pack',
        };
        const payload: any = {
          project_id: pjId,
          warehouse_id: whId,
          customer_id: customerId,
          template: templateId,
          priority: 'normal',
          name: String(mainRow.instruction_name || '').trim() || undefined,
          remarks: mainRow.remarks || undefined,
          waybill_carrier: mainRow.carrier || undefined,
          waybill_tracking_no: mainRow.tracking_no || undefined,
          reference_no: mainRow.reference_no || undefined,
          targets,
        };
        const submitMode = submitModeMap[String(mainRow.waybill_submit_mode || '').trim()];
        if (submitMode) {
          payload.waybill_submit_mode = submitMode;
        }
        const waybillSourceRaw = String(mainRow.waybill_source || '').trim();
        if (waybillSourceRaw === '仓库提供') {
          payload.waybill_source = 'warehouse';
        } else if (waybillSourceRaw === '上传运单') {
          payload.waybill_source = 'upload';
        }
        if (mainRow.need_pack !== undefined) {
          payload.is_pack_required = !!mainRow.need_pack;
          if (mainRow.need_pack && mainRow.pack_preference) {
            payload.estimated_packing_method = packPreferenceMap[String(mainRow.pack_preference || '')] || undefined;
          }
        }
        if (answers) payload.customer_answers = answers;
        instructionsToCreate.push({
          mainRow,
          rows: groupRows,
          payload,
          attachments: {
            waybill: waybillFilesByRowKey[String(mainRow.key || '')] || [],
            other: otherFilesByRowKey[String(mainRow.key || '')] || [],
          },
        });
      });
      if (otherAttachmentPrintable) {
        instructionsToCreate.forEach((item) => {
          const files = item.attachments.other || [];
          files.forEach((f: any) => {
            const uid = String(f?.uid || '');
            const cfg = otherAttachPrintConfigMap[uid] || {};
            if (!cfg.print) return;
            const valid = typeof cfg.copies === 'number' && cfg.copies >= 1 && !!cfg.paper_size && !!cfg.placement;
            if (!valid) {
              errors.push(`指令${item.mainRow.serial_no || '-'}的其他附件「${String(f?.name || '附件')}」需打印，请填写完整打印设置（打印份数、纸张尺寸、放置位置）`);
            }
          });
        });
      }
      if (errors.length > 0) {
        Modal.error({
          title: '创建失败：数据不完整',
          content: (
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              {errors.map((err, idx) => (
                <div key={`${err}-${idx}`}>{err}</div>
              ))}
            </div>
          ),
          okText: '知道了',
        });
        return;
      }
      type TrackingDupWarning = { provider: string; tracking_number: string; serials: string[] };
      const precheckTrackingDuplicates = async (): Promise<TrackingDupWarning[]> => {
        const startedAt = perfNow();
        const pairs = new Map<string, { carrier: string; tracking: string; payload: any; serials: string[] }>();
        instructionsToCreate.forEach((item) => {
          const carrier = String(item.payload?.waybill_carrier || '').trim();
          const tracking = String(item.payload?.waybill_tracking_no || '').trim();
          const submitMode = String(item.payload?.waybill_submit_mode || '').trim();
          if (!carrier || !tracking || submitMode !== 'immediate') return;
          const key = `${carrier}||${tracking}`;
          const existing = pairs.get(key) || { carrier, tracking, payload: item.payload, serials: [] };
          const serialText = String(item.mainRow.serial_no || '').trim();
          if (serialText) existing.serials.push(serialText);
          pairs.set(key, existing);
        });
        if (pairs.size === 0) {
          perfLog('precheck_tracking_duplicates', startedAt, { pairCount: 0, warningCount: 0 });
          return [];
        }
        const results = await Promise.all(
          Array.from(pairs.values()).map(async (entry) => {
            const precheckResp = await request.post('/api/v1/instruction/instructions/precheck/', {
              template: entry.payload?.template,
              customer_answers: entry.payload?.customer_answers,
              waybill_carrier: entry.carrier,
              waybill_tracking_no: entry.tracking,
            });
            const warnings = Array.isArray((precheckResp as any)?.data?.warnings)
              ? (precheckResp as any).data.warnings
              : (Array.isArray((precheckResp as any)?.warnings) ? (precheckResp as any).warnings : []);
            if (!warnings.length) return [];
            return warnings.map((item: any) => ({
              provider: String(item?.provider || entry.carrier || '').trim(),
              tracking_number: String(item?.tracking_number || entry.tracking || '').trim(),
              serials: Array.from(new Set(entry.serials.filter(Boolean))),
            }));
          }),
        );
        const merged = new Map<string, TrackingDupWarning>();
        results.flat().forEach((item) => {
          const key = `${item.provider}||${item.tracking_number}`;
          const existing = merged.get(key) || { ...item, serials: [] };
          existing.serials = Array.from(new Set([...(existing.serials || []), ...(item.serials || [])].filter(Boolean)));
          merged.set(key, existing);
        });
        const warnings = Array.from(merged.values());
        perfLog('precheck_tracking_duplicates', startedAt, {
          pairCount: pairs.size,
          warningCount: warnings.length,
        });
        return warnings;
      };
      const buildSummary = () => {
        const stats = computeStats(rows);
        const mainRows = Array.from(grouped.values()).map((groupRows) => (
          groupRows.find((r) => !r.is_child) || groupRows[0]
        ));
        const carrierCountMap = new Map<string, number>();
        const carrierQtyMap = new Map<string, number>();
        const trackingCounts = new Map<string, { carrier: string; tracking: string; count: number }>();
        let trackingProvided = 0;
        let waybillAttachmentCount = 0;
        let otherAttachmentCount = 0;
        let needPackOrderCount = 0;
        let needPackQty = 0;
        let noPackOrderCount = 0;
        let noPackQty = 0;
        const unitSummaryMap = new Map<string, { warehouse: string; warehouseLabel: string; unit: string; sortingTag: string; customTag: string; tag: string; available: string; qty: number }>();
        const serialQtyMap = new Map<string, number>();
        const warehouseLabelMap = new Map(
          warehouseOptions.map((opt) => [String(opt.value || '').trim(), String(opt.label || '').trim()]),
        );
        rows.forEach((row) => {
          const serial = String(row.serial_no || '').trim();
          const qty = Number(row.quantity || 0) || 0;
          if (serial) {
            serialQtyMap.set(serial, (serialQtyMap.get(serial) || 0) + qty);
          }
          const warehouse = String(row.warehouse || '').trim();
          const unit = String(row.inventory_unit || '').trim();
          const tag = String(row.inventory_unit_tag || '').trim();
          const available = (row.available_qty === undefined || row.available_qty === null) ? '' : String(row.available_qty);
          if (unit || tag || available) {
            const key = `${warehouse}__${unit}__${tag}__${available}`;
            const [sortingTagRaw = '', customTagRaw = ''] = tag.split('|').map((t) => t.trim());
            const next = unitSummaryMap.get(key) || {
              warehouse: warehouse || '-',
              warehouseLabel: warehouseLabelMap.get(warehouse) || warehouse || '-',
              unit: unit || '-',
              sortingTag: sortingTagRaw,
              customTag: customTagRaw,
              tag: tag || '-',
              available: available || '-',
              qty: 0,
            };
            next.qty += qty;
            unitSummaryMap.set(key, next);
          }
        });
        mainRows.forEach((row) => {
          const serial = String(row.serial_no || '').trim();
          const serialQty = serialQtyMap.get(serial) || 0;
          const carrierLabel = String(row.carrier || '').trim() || '-';
          carrierCountMap.set(carrierLabel, (carrierCountMap.get(carrierLabel) || 0) + 1);
          carrierQtyMap.set(carrierLabel, (carrierQtyMap.get(carrierLabel) || 0) + serialQty);
          const carrierCode = String(row.carrier || '').trim();
          const tracking = String(row.tracking_no || '').trim();
          if (tracking) {
            trackingProvided += 1;
          }
          if (carrierCode && tracking) {
            const key = `${carrierCode}||${tracking}`.toLowerCase();
            const existing = trackingCounts.get(key) || { carrier: carrierCode, tracking, count: 0 };
            trackingCounts.set(key, { ...existing, count: existing.count + 1 });
          }
          const rowKey = String(row.key || '').trim();
          waybillAttachmentCount += (waybillFilesByRowKey[rowKey] || []).length;
          otherAttachmentCount += (otherFilesByRowKey[rowKey] || []).length;
          if (row.need_pack === true) {
            needPackOrderCount += 1;
            needPackQty += serialQty;
          } else if (row.need_pack === false) {
            noPackOrderCount += 1;
            noPackQty += serialQty;
          }
        });
        const trackingDuplicateCount = Array.from(trackingCounts.values())
          .filter((item) => item.count > 1)
          .reduce((sum, item) => sum + item.count, 0);
        const trackingDuplicates = Array.from(trackingCounts.values())
          .filter((item) => item.count > 1)
          .map((item) => ({ carrier: item.carrier, tracking: item.tracking, count: item.count }));
        const insufficientUnits = Array.from(unitSummaryMap.values()).filter((item) => {
          const availableNum = Number(item.available);
          const qtyNum = Number(item.qty);
          if (!Number.isFinite(availableNum) || !Number.isFinite(qtyNum)) return false;
          return availableNum < qtyNum;
        });
        return {
          stats,
          carrierCountMap,
          carrierQtyMap,
          trackingProvided,
          trackingDuplicateCount,
          trackingDuplicates,
          waybillAttachmentCount,
          otherAttachmentCount,
          unitSummary: Array.from(unitSummaryMap.values()),
          needPackOrderCount,
          needPackQty,
          noPackOrderCount,
          noPackQty,
          insufficientUnits,
        };
      };
      const confirmBatchCreateSummary = async () => {
        const summary = buildSummary();
        let trackingDupWarnings: TrackingDupWarning[] = [];
        try {
          trackingDupWarnings = await precheckTrackingDuplicates();
        } catch {
          message.error('唯一性校验失败，请稍后重试');
          return false;
        }
        const trackingDupWarningsBySerial = (() => {
          const map = new Map<string, { serial: string; items: { provider: string; tracking_number: string }[] }>();
          trackingDupWarnings.forEach((item) => {
            const serials = (item.serials || []).map((s) => String(s || '').trim()).filter(Boolean);
            if (serials.length === 0) {
              const key = '-';
              const existing = map.get(key) || { serial: key, items: [] };
              existing.items.push({ provider: item.provider, tracking_number: String(item.tracking_number || '').toUpperCase() });
              map.set(key, existing);
              return;
            }
            serials.forEach((serial) => {
              const existing = map.get(serial) || { serial, items: [] };
              existing.items.push({ provider: item.provider, tracking_number: String(item.tracking_number || '').toUpperCase() });
              map.set(serial, existing);
            });
          });
          map.forEach((v) => {
            const uniq = new Map<string, { provider: string; tracking_number: string }>();
            v.items.forEach((it) => {
              const providerKey = String(it.provider || '').trim().toLowerCase();
              const trackingKey = String(it.tracking_number || '').trim().toLowerCase();
              const k = `${providerKey}||${trackingKey}`;
              if (!uniq.has(k)) uniq.set(k, it);
            });
            v.items = Array.from(uniq.values());
          });
          return Array.from(map.values());
        })();
        const buildSummaryText = () => {
          const lines: string[] = [];
          lines.push(`指令总数量:${summary.stats.orderCount}`);
          lines.push(`发货产品总数量:${summary.stats.totalQty}`);
          lines.push(`涉及仓库数:${summary.stats.warehouseCount} 涉及项目数:${summary.stats.projectCount}`);
          lines.push(`单款单件:${summary.stats.singleOneOrders}单/${summary.stats.singleOneQty}件`);
          lines.push(`单款多件:${summary.stats.singleMultiOrders}单/${summary.stats.singleMultiQty}件`);
          lines.push(`多款多件:${summary.stats.multiOrders}单/${summary.stats.multiQty}件`);
          lines.push('库存单元汇总:');
          summary.unitSummary.forEach((item) => {
            const availableNum = Number(item.available);
            const qtyNum = Number(item.qty);
            const enough = Number.isFinite(availableNum) ? (availableNum >= qtyNum ? '充足' : '不足') : '-';
            lines.push(`仓库:${item.warehouseLabel} 库存单元:${item.unit} 标识:${item.tag} 可用库存:${item.available} 总发货数:${item.qty} 库存是否充足:${enough}`);
          });
          lines.push(`已提供物流跟踪号数量:${summary.trackingProvided}`);
          lines.push(`重复的物流跟踪号:${summary.trackingDuplicates.length}个${summary.trackingDuplicateCount}次`);
          Array.from(summary.carrierCountMap.entries()).forEach(([carrier, count]) => {
            const qty = summary.carrierQtyMap.get(carrier) || 0;
            lines.push(`物流承运商:${carrier} 指令数量:${count} 发货产品数量:${qty}`);
          });
          lines.push(`物流运单附件数量:${summary.waybillAttachmentCount}`);
          lines.push(`其他附件总数量:${summary.otherAttachmentCount}`);
          lines.push(`需打包指令数量:${summary.needPackOrderCount} 需打包产品数量:${summary.needPackQty}`);
          lines.push(`无需打包指令数量:${summary.noPackOrderCount} 无需打包产品数量:${summary.noPackQty}`);
          if (summary.trackingDuplicateCount > 0) {
            lines.push('警告信息:');
            lines.push('存在重复物流信息（可继续提交,但可能影响以后查单）');
            summary.trackingDuplicates.forEach((item) => {
              lines.push(`物流服务商:${item.carrier} 物流跟踪号:${item.tracking} 出现次数:${item.count}`);
            });
          }
          if (trackingDupWarnings.length > 0) {
            if (summary.trackingDuplicateCount === 0) {
              lines.push('警告信息:');
            }
            lines.push('存在已使用的历史物流信息（可继续提交,但可能影响以后查单）');
            trackingDupWarningsBySerial.forEach((item) => {
              const detail = item.items.map((it) => `${it.provider} ${it.tracking_number}`).join('；');
              lines.push(`指令编号:${item.serial || '-'} 物流信息:${detail || '-'}`);
            });
          }
          if (summary.insufficientUnits.length > 0) {
            if (summary.trackingDuplicateCount === 0 && trackingDupWarnings.length === 0) {
              lines.push('警告信息:');
            }
            lines.push('存在数量不足的库存单元（不可继续提交,请返回修改）');
            summary.insufficientUnits.forEach((item) => {
              lines.push(`仓库:${item.warehouseLabel} 库存单元:${item.unit} 标识:${item.tag} 可用库存:${item.available} 总发货数:${item.qty}`);
            });
          }
          return lines.join('\n');
        };
        const copySummary = async () => {
          try {
            await navigator.clipboard.writeText(buildSummaryText());
            message.success('已复制');
          } catch {
            message.error('复制失败');
          }
        };
        const exportSummaryPdf = () => {
          const node = batchSummaryRef.current;
          if (!node) {
            message.warning('未找到可导出的内容');
            return;
          }
          const date = new Date();
          const dateText = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const fileTitle = `批量创建代发指令预审-${summary.stats.orderCount}单${summary.stats.totalQty}件-${dateText}`;
          const win = window.open('', '_blank');
          if (!win) {
            message.error('打开新窗口失败，请检查浏览器拦截设置');
            return;
          }
          win.document.open();
          win.document.write(`
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${fileTitle}</title>
    <style>
      body { margin: 16px; font-family: Arial, sans-serif; }
    </style>
  </head>
  <body>
    ${node.innerHTML}
  </body>
</html>
          `);
          win.document.close();
          win.focus();
          setTimeout(() => {
            win.print();
          }, 300);
        };
        return new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>批量创建确认</span>
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  <Button size="small" onClick={exportSummaryPdf}>导出pdf</Button>
                  <Button size="small" onClick={copySummary}>复制</Button>
                </span>
              </div>
            ),
            width: 1080,
            okText: '确认创建',
            cancelText: '返回修改',
            okButtonProps: { disabled: summary.insufficientUnits.length > 0 },
            content: (
              <div ref={batchSummaryRef} style={{ maxHeight: 520, overflow: 'auto' }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>基础统计</div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px 1fr', border: '1px solid #f0f0f0' }}>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>指令总数量</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>{summary.stats.orderCount}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>发货产品总数量</div>
                  <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{summary.stats.totalQty}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>涉及仓库数</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>{summary.stats.warehouseCount}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>涉及项目数</div>
                  <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{summary.stats.projectCount}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>单款单件</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>{summary.stats.singleOneOrders}单 / {summary.stats.singleOneQty}件</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>单款多件</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>{summary.stats.singleMultiOrders}单 / {summary.stats.singleMultiQty}件</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>多款多件</div>
                  <div style={{ padding: '6px 8px' }}>{summary.stats.multiOrders}单 / {summary.stats.multiQty}件</div>
                </div>

                <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>库存单元汇总</div>
                {summary.unitSummary.length === 0 ? (
                  <div style={{ color: '#999' }}>无</div>
                ) : (
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '180px 140px 1.4fr 120px 120px 120px', background: '#fafafa', fontWeight: 600 }}>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>仓库</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>库存单元</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>库存单元标识</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>可用库存</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>总发货数</div>
                      <div style={{ padding: '6px 8px' }}>库存是否充足</div>
                    </div>
                    {summary.unitSummary.map((item, idx) => {
                      const availableNum = Number(item.available);
                      const qtyNum = Number(item.qty);
                      const enough = Number.isFinite(availableNum) ? (availableNum >= qtyNum ? '充足' : '不足') : '-';
                      return (
                        <div key={`${item.warehouse}-${item.unit}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '180px 140px 1.4fr 120px 120px 120px', borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0' }}>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', whiteSpace: 'pre-line' }}>
                            {(() => {
                              const code = item.warehouse || '';
                              const label = item.warehouseLabel || item.warehouse;
                              const name = label.startsWith(`${code}-`) ? label.slice(code.length + 1) : label;
                              return `${code}\n${name}`;
                            })()}
                          </div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{item.unit}</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>
                            <Tooltip title={item.sortingTag || '-'}>
                              <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.sortingTag || '-'}
                              </div>
                            </Tooltip>
                            <Tooltip title={item.customTag || '-'}>
                              <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.customTag || '-'}
                              </div>
                            </Tooltip>
                          </div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{item.available}</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{item.qty}</div>
                          <div style={{ padding: '6px 8px' }}>
                            {enough === '充足' ? (
                              <span style={{ background: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', padding: '0 6px', borderRadius: 4 }}>充足</span>
                            ) : enough === '不足' ? (
                              <span style={{ background: '#fff1f0', color: '#cf1322', border: '1px solid #ffa39e', padding: '0 6px', borderRadius: 4 }}>不足</span>
                            ) : (
                              '-'
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>物流信息汇总</div>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 200px 1fr', border: '1px solid #f0f0f0' }}>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>已提供物流跟踪号数量</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>{summary.trackingProvided}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>重复的物流跟踪号</div>
                  <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                    {(() => {
                      const dupCount = summary.trackingDuplicates.length;
                      const dupTotal = summary.trackingDuplicates.reduce((sum, item) => sum + Number(item.count || 0), 0);
                      return `${dupCount}个${dupTotal}次`;
                    })()}
                  </div>
                </div>
                {summary.carrierCountMap.size > 0 ? (
                  <div style={{ marginTop: 6, border: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#fafafa', fontWeight: 600 }}>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>物流承运商</div>
                      <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>指令数量</div>
                      <div style={{ padding: '6px 8px' }}>发货产品数量</div>
                    </div>
                    {Array.from(summary.carrierCountMap.entries()).map(([carrier, count], idx) => {
                      const qty = summary.carrierQtyMap.get(carrier) || 0;
                      return (
                        <div key={carrier} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0' }}>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{carrier}</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{count}</div>
                          <div style={{ padding: '6px 8px' }}>{qty}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ color: '#999', marginTop: 6 }}>无</div>
                )}

                <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>附件汇总</div>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px 1fr', border: '1px solid #f0f0f0' }}>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>物流运单附件数量</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{summary.waybillAttachmentCount}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>其他附件总数量</div>
                  <div style={{ padding: '6px 8px' }}>{summary.otherAttachmentCount}</div>
                </div>

                <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>打包汇总</div>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px 1fr', border: '1px solid #f0f0f0' }}>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>需打包指令数量</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>{summary.needPackOrderCount}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>需打包产品数量</div>
                  <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{summary.needPackQty}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>无需打包指令数量</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{summary.noPackOrderCount}</div>
                  <div style={{ padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>无需打包产品数量</div>
                  <div style={{ padding: '6px 8px' }}>{summary.noPackQty}</div>
                </div>
                {(summary.trackingDuplicateCount > 0 || summary.insufficientUnits.length > 0 || trackingDupWarnings.length > 0) ? (
                  <div style={{ marginTop: 12, background: '#fffbe6', border: '1px solid #ffe58f', color: '#000', padding: '10px 12px', borderRadius: 4 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>警告信息:</div>
                    {summary.trackingDuplicateCount > 0 ? (
                      <div style={{ marginBottom: (trackingDupWarnings.length > 0 || summary.insufficientUnits.length > 0) ? 10 : 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          存在重复物流信息
                          <span style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', border: '1px solid #000', borderRadius: 4, fontWeight: 400 }}>
                            可继续提交,但可能影响以后查单
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 120px', border: '1px solid #ffe58f', background: '#fffbe6' }}>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>物流服务商</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>物流跟踪号</div>
                          <div style={{ padding: '6px 8px', fontWeight: 600 }}>出现次数</div>
                          {summary.trackingDuplicates.map((item) => (
                            <React.Fragment key={`${item.carrier}-${item.tracking}`}>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.carrier || '-'}</div>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.tracking}</div>
                              <div style={{ padding: '6px 8px', borderTop: '1px solid #ffe58f' }}>{item.count}</div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {trackingDupWarnings.length > 0 ? (
                      <div style={{ marginBottom: summary.insufficientUnits.length > 0 ? 10 : 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          存在已使用的历史物流信息
                          <span style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', border: '1px solid #000', borderRadius: 4, fontWeight: 400 }}>
                            可继续提交,但可能影响以后查单
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', border: '1px solid #ffe58f', background: '#fffbe6' }}>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>指令编号</div>
                          <div style={{ padding: '6px 8px', fontWeight: 600 }}>物流信息</div>
                          {trackingDupWarningsBySerial.map((item, idx) => (
                            <React.Fragment key={`${item.serial}-${idx}`}>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.serial || '-'}</div>
                              <div style={{ padding: '6px 8px', borderTop: '1px solid #ffe58f' }}>
                                {(item.items || []).map((it) => `${it.provider || '-'} ${it.tracking_number || '-'}`).join('；') || '-'}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {summary.insufficientUnits.length > 0 ? (
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          存在数量不足的库存单元
                          <span style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', border: '1px solid #ff4d4f', borderRadius: 4, color: '#ff4d4f', fontWeight: 400 }}>
                            不可继续提交,请返回修改
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '160px 140px 1fr 120px 120px', border: '1px solid #ffe58f', background: '#fffbe6' }}>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>仓库</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>库存单元</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>库存单元标识</div>
                          <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>可用库存</div>
                          <div style={{ padding: '6px 8px', fontWeight: 600 }}>总发货数</div>
                          {summary.insufficientUnits.map((item, idx) => (
                            <React.Fragment key={`${item.warehouse}-${item.unit}-${idx}`}>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f', whiteSpace: 'pre-line' }}>
                                {(() => {
                                  const code = item.warehouse || '';
                                  const label = item.warehouseLabel || item.warehouse;
                                  const name = label.startsWith(`${code}-`) ? label.slice(code.length + 1) : label;
                                  return `${code}\n${name}`;
                                })()}
                              </div>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.unit}</div>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>
                                <Tooltip title={item.sortingTag || '-'}>
                                  <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.sortingTag || '-'}
                                  </div>
                                </Tooltip>
                                <Tooltip title={item.customTag || '-'}>
                                  <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.customTag || '-'}
                                  </div>
                                </Tooltip>
                              </div>
                              <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.available}</div>
                              <div style={{ padding: '6px 8px', borderTop: '1px solid #ffe58f' }}>{item.qty}</div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
      };
      const summaryStartedAt = perfNow();
      const shouldProceed = await confirmBatchCreateSummary();
      perfLog('confirm_batch_summary', summaryStartedAt, { shouldProceed });
      if (!shouldProceed) {
        return;
      }
      if (instructionsToCreate.length === 0) {
        message.warning('无可创建的指令记录');
        return;
      }
      const created: Array<{ id: number; serial: string; systemCode: string; rowKey: string }> = [];
      const failed: Array<{ serial: string; reason: string }> = [];
      setBatchCreateProgress({ total: instructionsToCreate.length, done: 0, failed: 0 });
      setBatchCreateProgressOpen(true);
      const runWithConcurrency = async (tasks: Array<() => Promise<void>>, limit = 4) => {
        const startedAt = perfNow();
        const pool: Promise<void>[] = [];
        let i = 0;
        const enqueue = async (): Promise<void> => {
          if (i >= tasks.length) return;
          const task = tasks[i++];
          const p = task().finally(() => {
            const idx = pool.indexOf(p);
            if (idx >= 0) pool.splice(idx, 1);
          });
          pool.push(p);
          if (pool.length >= limit) {
            await Promise.race(pool);
          }
          await enqueue();
        };
        await enqueue();
        await Promise.all(pool);
        perfLog('create_with_concurrency', startedAt, { taskCount: tasks.length, concurrency: limit });
      };
      const createTasks = instructionsToCreate.map((item) => async () => {
        const itemStartedAt = perfNow();
        try {
          const resp = await request.post('/api/v1/instruction/instructions/', item.payload);
          const instrId = Number(resp?.data?.id);
          if (!Number.isFinite(instrId)) {
            const respDetail = resp?.data ? JSON.stringify(resp.data) : '';
            failed.push({
              serial: String(item.mainRow.serial_no || ''),
              reason: respDetail ? `创建失败: ${respDetail}` : '创建失败',
            });
            setBatchCreateProgress((prev) => ({ ...prev, done: prev.done + 1, failed: prev.failed + 1 }));
            return;
          }
          const systemCode = String(resp?.data?.system_code || resp?.data?.systemCode || resp?.data?.code || '').trim();
          created.push({ id: instrId, serial: String(item.mainRow.serial_no || ''), systemCode, rowKey: String(item.mainRow.key || '') });
          if (item.attachments.waybill.length > 0) {
            try { await uploadInstructionAttachments(instrId, item.attachments.waybill, '物流运单'); } catch {}
          }
          if (item.attachments.other.length > 0) {
            try {
              await uploadInstructionAttachments(
                instrId,
                item.attachments.other,
                '其他附件',
                otherAttachmentPrintable ? otherAttachPrintConfigMap : undefined,
              );
            } catch {}
          }
          setBatchCreateProgress((prev) => ({ ...prev, done: prev.done + 1 }));
          perfLog('create_item_success', itemStartedAt, {
            serial: String(item.mainRow.serial_no || ''),
            waybillAttachmentCount: item.attachments.waybill.length,
            otherAttachmentCount: item.attachments.other.length,
          });
        } catch (err: any) {
          const respData = err?.response?.data;
          const detail = respData?.detail || respData?.error || respData?.message || '';
          const extra = respData ? JSON.stringify(respData) : '';
          console.error('[BulkCreate][BatchCreate] create failed', {
            serial: item.mainRow.serial_no,
            payload: item.payload,
            status: err?.response?.status,
            data: respData,
          });
          failed.push({
            serial: String(item.mainRow.serial_no || ''),
            reason: detail || (extra ? `创建失败: ${extra}` : '创建失败'),
          });
          setBatchCreateProgress((prev) => ({ ...prev, done: prev.done + 1, failed: prev.failed + 1 }));
          perfLog('create_item_failed', itemStartedAt, {
            serial: String(item.mainRow.serial_no || ''),
            status: err?.response?.status,
          });
        }
      });
      await runWithConcurrency(createTasks, 1);
      const buildBatchCreateResultText = () => {
        const lines: string[] = [];
        lines.push(`批量创建完成：成功${created.length}条，失败${failed.length}条`);
        lines.push('成功记录');
        if (created.length === 0) {
          lines.push('无');
        } else {
          created.forEach((item) => {
            lines.push(`指令${item.serial || '-'}：${item.systemCode || '-'}`);
          });
        }
        lines.push('失败记录');
        if (failed.length === 0) {
          lines.push('无');
        } else {
          failed.forEach((item) => {
            lines.push(`指令${item.serial || '-'}：${item.reason}`);
          });
        }
        return lines.join('\n');
      };
      const copyBatchCreateResult = async () => {
        try {
          await navigator.clipboard.writeText(buildBatchCreateResultText());
          message.success('已复制');
        } catch {
          message.error('复制失败');
        }
      };
      const createdRowKeySet = new Set(created.map((item) => item.rowKey).filter(Boolean));
      const failedSerialSet = new Set(failed.map((item) => String(item.serial || '').trim()).filter(Boolean));
      const applyCleanup = (cleanupRows: DropshipRow[], prefix: string, seqRef: React.MutableRefObject<number>) => {
        if (cleanupRows.length === 0) return;
        const keep = cleanupRows.filter((row) => {
          if (row.is_child) {
            return row.parent_key && !createdRowKeySet.has(String(row.parent_key || ''));
          }
          return !createdRowKeySet.has(String(row.key || ''));
        });
        const nextRows = resequenceSerials(keep, prefix, seqRef);
        if (createdRowKeySet.size > 0) {
          cleanupAttachmentsByRowKeys(Array.from(createdRowKeySet));
        }
        return nextRows;
      };
      Modal.info({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{`批量创建完成：成功${created.length}条，失败${failed.length}条`}</span>
            <Button size="small" onClick={copyBatchCreateResult}>复制</Button>
          </div>
        ),
        content: (
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>成功记录</div>
            {created.length === 0 ? (
              <div style={{ color: '#999', marginBottom: 12 }}>无</div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                {created.map((item, idx) => (
                  <div key={`${item.id}-${idx}`}>
                    {`指令${item.serial || '-'}：${item.systemCode || '-'}`}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>失败记录</div>
            {failed.length === 0 ? (
              <div style={{ color: '#999' }}>无</div>
            ) : (
              failed.map((item, idx) => (
                <div key={`${item.serial}-${idx}`}>{`指令${item.serial || '-'}：${item.reason}`}</div>
              ))
            )}
          </div>
        ),
        width: 720,
        okText: '知道了',
        onOk: () => {
          if (createdRowKeySet.size === 0) return;
          const prefix = rows === immediateRows ? 'IM' : rows === afterPackRows ? 'AP' : 'WH';
          if (rows === immediateRows) {
            setImmediateRows((prev) => applyCleanup(prev, prefix, immediateSerialSeq) || prev);
            setImmediateSelectedKeys((prev) => prev.filter((k) => !createdRowKeySet.has(String(k))));
          } else if (rows === afterPackRows) {
            setAfterPackRows((prev) => applyCleanup(prev, prefix, afterPackSerialSeq) || prev);
            setAfterPackSelectedKeys((prev) => prev.filter((k) => !createdRowKeySet.has(String(k))));
          } else {
            setWarehouseRows((prev) => applyCleanup(prev, prefix, warehouseSerialSeq) || prev);
            setWarehouseSelectedKeys((prev) => prev.filter((k) => !createdRowKeySet.has(String(k))));
          }
          if (failedSerialSet.size > 0) {
            message.info(`失败记录已保留，请修正后再次创建`);
          }
        },
      });
      perfLog('handle_batch_create_total', totalStartedAt, {
        activeTabKey,
        rowCount: rows.length,
        instructionToCreateCount: instructionsToCreate.length,
        createdCount: created.length,
        failedCount: failed.length,
      });
    } finally {
      setBatchCreateProgressOpen(false);
      setBatchCreateLoading(false);
    }
  };
  const openBatchEditModal = (config: {
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    selectedKeys: React.Key[];
    rows: DropshipRow[];
    requiredOnly: boolean;
    requiredKeys: Array<keyof DropshipRow>;
  }) => {
    const visibleKeys = (config.requiredOnly ? config.requiredKeys : batchEditableFieldKeys)
      .filter((key) => batchEditableFieldKeySet.has(key));
    const orderedKeys = batchEditFieldOrder.filter((key) => visibleKeys.includes(key));
    setBatchEditFields(orderedKeys.length ? orderedKeys : visibleKeys);
    setBatchEditActiveKeys([]);
    setBatchEditTouchedKeys([]);
    setBatchEditValues({});
    const keySet = new Set(config.selectedKeys.map((k) => String(k)));
    const selectedRows = config.rows.filter((row) => keySet.has(String(row.key)));
    const warehouses = new Set(selectedRows.map((row) => String(row.warehouse || '').trim()).filter(Boolean));
    const projects = new Set(selectedRows.map((row) => String(row.project || '').trim()).filter(Boolean));
    const allHaveWarehouseProject = selectedRows.length > 0
      && selectedRows.every((row) => !!String(row.warehouse || '').trim() && !!String(row.project || '').trim());
    if (allHaveWarehouseProject && warehouses.size === 1 && projects.size === 1) {
      const wh = Array.from(warehouses)[0];
      const pj = Array.from(projects)[0];
      const liuKey = buildLiuKey(wh, pj);
      const whLabel = warehouseOptions.find((opt) => opt.value === wh)?.label || wh;
      const pjLabel = projectOptions.find((opt) => opt.value === pj)?.label || pj;
      setBatchEditLiuKey(liuKey);
      setBatchEditLiuLabel(`${whLabel} / ${pjLabel}`);
      setBatchEditLiuOptions(liuOptionsByKey[liuKey] || []);
      setBatchEditLiuAllowed(true);
      setBatchEditLiuReason('');
    } else {
      setBatchEditLiuKey('');
      setBatchEditLiuLabel('');
      setBatchEditLiuOptions([]);
      setBatchEditLiuAllowed(false);
      if (!allHaveWarehouseProject) {
        setBatchEditLiuReason('选中的记录必须已选择仓库和项目');
      } else {
        setBatchEditLiuReason('选中的记录需保持仓库一致且项目一致');
      }
    }
    setBatchEditConfig(config);
    setBatchEditPrintConfigEnabled(false);
    setBatchEditPrintConfig({ print: false, copies: 1, paper_size: '', placement: '' });
    setBatchEditOpen(true);
  };
  const ensureBatchEditFieldVisible = (field: keyof DropshipRow) => {
    setBatchEditFields((prev) => {
      if (prev.includes(field)) return prev;
      const merged = Array.from(new Set<keyof DropshipRow>([...prev, field]));
      const ordered = batchEditFieldOrder.filter((k) => merged.includes(k));
      const tails = merged.filter((k) => !ordered.includes(k));
      return [...ordered, ...tails];
    });
  };
  const hideInsuranceAmountFieldIfConditional = () => {
    if (!batchEditConfig?.requiredOnly) return;
    const requiredByBase = (batchEditConfig.requiredKeys || []).includes('insurance_amount' as keyof DropshipRow);
    if (requiredByBase) return;
    setBatchEditFields((prev) => prev.filter((k) => k !== 'insurance_amount'));
  };
  const toggleBatchEditKey = (key: keyof DropshipRow, checked: boolean) => {
    if (key === 'inventory_unit' && batchEditActiveKeys.includes('inventory_unit_tag')) return;
    if (key === 'inventory_unit_tag' && batchEditActiveKeys.includes('inventory_unit')) return;
    if ((key === 'inventory_unit' || key === 'inventory_unit_tag')
      && (batchEditActiveKeys.includes('warehouse') || batchEditActiveKeys.includes('project'))) return;
    if ((key === 'warehouse' || key === 'project')
      && (batchEditActiveKeys.includes('inventory_unit') || batchEditActiveKeys.includes('inventory_unit_tag'))) return;
    if (key === 'quantity'
      && (batchEditActiveKeys.includes('inventory_unit')
        || batchEditActiveKeys.includes('inventory_unit_tag')
        || batchEditActiveKeys.includes('warehouse')
        || batchEditActiveKeys.includes('project'))) return;
    if ((key === 'inventory_unit' || key === 'inventory_unit_tag' || key === 'warehouse' || key === 'project')
      && batchEditActiveKeys.includes('quantity')) return;
    setBatchEditActiveKeys((prev) => {
      if (checked) return Array.from(new Set([...prev, key]));
      return prev.filter((k) => k !== key);
    });
    if (!checked) {
      setBatchEditValues((prev) => ({ ...prev, [key]: undefined }));
      setBatchEditTouchedKeys((prev) => prev.filter((k) => k !== key));
      if (key === 'inventory_unit') {
        setBatchEditValues((prev) => ({ ...prev, inventory_unit_tag: undefined }));
        setBatchEditTouchedKeys((prev) => prev.filter((k) => k !== 'inventory_unit_tag'));
      }
      if (key === 'inventory_unit_tag') {
        setBatchEditValues((prev) => ({ ...prev, inventory_unit: undefined }));
        setBatchEditTouchedKeys((prev) => prev.filter((k) => k !== 'inventory_unit'));
      }
      if (key === 'need_insurance') {
        setBatchEditValues((prev) => ({ ...prev, insurance_amount: undefined }));
        setBatchEditTouchedKeys((prev) => prev.filter((k) => k !== 'insurance_amount'));
        setBatchEditActiveKeys((prev) => prev.filter((k) => k !== 'insurance_amount'));
        hideInsuranceAmountFieldIfConditional();
      }
    }
  };
  const updateBatchEditValue = (key: keyof DropshipRow, value: any) => {
    setBatchEditValues((prev) => ({ ...prev, [key]: value }));
    setBatchEditTouchedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setBatchEditActiveKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    if (key === 'need_insurance' && value === true) {
      ensureBatchEditFieldVisible('insurance_amount');
      setBatchEditActiveKeys((prev) => (prev.includes('insurance_amount') ? prev : [...prev, 'insurance_amount']));
    }
    if (key === 'need_insurance' && value !== true) {
      setBatchEditValues((prev) => ({ ...prev, insurance_amount: undefined }));
      setBatchEditTouchedKeys((prev) => prev.filter((k) => k !== 'insurance_amount'));
      setBatchEditActiveKeys((prev) => prev.filter((k) => k !== 'insurance_amount'));
      hideInsuranceAmountFieldIfConditional();
    }
    if (key === 'inventory_unit') {
      const option = batchEditLiuOptions.find((u) => String(u.inventory_unit_code || '').trim() === String(value || '').trim());
      if (option) {
        setBatchEditValues((prev) => ({ ...prev, inventory_unit_tag: buildLiuTagDisplay(option) }));
      }
    }
    if (key === 'inventory_unit_tag') {
      const map = buildLiuUnitMapByTag(batchEditLiuOptions);
      const unit = map.get(String(value || '').trim());
      if (unit) {
        setBatchEditValues((prev) => ({ ...prev, inventory_unit: unit }));
      }
    }
  };
  const confirmBatchEdit = async () => {
    if (!batchEditConfig) {
      setBatchEditOpen(false);
      return;
    }
    if (batchEditActiveKeys.length === 0 && !batchEditPrintConfigEnabled) {
      message.warning('请选择需要批量赋值/修改的字段');
      return;
    }
    if (batchEditPrintConfigEnabled && batchEditPrintConfig.print) {
      const valid = typeof batchEditPrintConfig.copies === 'number' && batchEditPrintConfig.copies >= 1
        && !!batchEditPrintConfig.paper_size && !!batchEditPrintConfig.placement;
      if (!valid) {
        message.warning('打印需求设置已开启「是否打印」，请填写完整打印设置（打印份数、纸张尺寸、放置位置）');
        return;
      }
    }
    const isBatchValueValid = (key: keyof DropshipRow, value: any) => {
      const config = batchFieldConfigMap[key];
      if (!config) return false;
      if (config.type === 'switch' || config.type === 'boolean_select') return value === true || value === false;
      if (config.type === 'number') return Number.isFinite(Number(value));
      return !!String(value || '').trim();
    };
    if (batchEditActiveKeys.length > 0) {
      const invalidKey = batchEditActiveKeys.find((key) => !isBatchValueValid(key, (batchEditValues as any)[key]));
      if (invalidKey) {
        message.warning('存在已选字段未填写有效值');
        return;
      }
    }
    const keySet = new Set(batchEditConfig.selectedKeys.map((key) => String(key)));
    const touchedSet = new Set(batchEditTouchedKeys);
    const activeSet = new Set(batchEditActiveKeys);
    const hasRowUpdates = batchEditActiveKeys.length > 0;
    const unitMode = activeSet.has('inventory_unit');
    const tagMode = activeSet.has('inventory_unit_tag');
    if (hasRowUpdates && (unitMode || tagMode)) {
      let newUnit: string;
      if (unitMode) {
        newUnit = String((batchEditValues as any).inventory_unit || '').trim();
      } else {
        const tag = String((batchEditValues as any).inventory_unit_tag || '').trim();
        newUnit = tag ? (buildLiuUnitMapByTag(batchEditLiuOptions).get(tag) ?? '') : '';
      }
      const rowsBySerial = new Map<string, DropshipRow[]>();
      (batchEditConfig.rows || []).forEach((row) => {
        const serial = String(row.serial_no || '').trim();
        if (!rowsBySerial.has(serial)) rowsBySerial.set(serial, []);
        rowsBySerial.get(serial)!.push(row);
      });
      const conflictSerials: string[] = [];
      rowsBySerial.forEach((rows, serial) => {
        const selectedInGroup = rows.filter((r) => keySet.has(r.key));
        if (selectedInGroup.length > 1) {
          conflictSerials.push(serial || '(无编号)');
        } else if (selectedInGroup.length === 1) {
          const unselectedInGroup = rows.filter((r) => !keySet.has(r.key));
          const hasSameUnit = unselectedInGroup.some((r) => String(r.inventory_unit || '').trim() === newUnit);
          if (hasSameUnit) conflictSerials.push(serial || '(无编号)');
        }
      });
      if (conflictSerials.length > 0) {
        Modal.warning({
          centered: true,
          title: '禁止批量修改',
          content: `指令编号相同的多行记录表示同一个指令中的不同库存单元,同一个指令中的多行不能使用相同的库存单元(即每个库存单元必须单独一行),以下指令在修改后的多行记录中会出现重复库存单元,禁止批量修改,请调整选择或修改内容：${conflictSerials.join('、')}`,
        });
        return;
      }
    }
    let batchZipCity: string | undefined;
    let batchZipState: string | undefined;
    if (hasRowUpdates && touchedSet.has('postal_code')) {
      const zip = String((batchEditValues as any).postal_code || '').trim();
      if (zip && /^\d{5}(?:-\d{4})?$/.test(zip)) {
        try {
          const resp = await request.post('/api/v1/instruction/instructions/address-parse/', { text: zip });
          const parsed = resp?.data?.parsed || {};
          const hasMatch = !!(parsed?.city || parsed?.state || parsed?.zip_lookup_city || parsed?.zip_lookup_state);
          if (hasMatch) {
            batchZipCity = String(parsed?.zip_lookup_city || parsed?.city || '').trim() || undefined;
            batchZipState = String(parsed?.zip_lookup_state || parsed?.state || '').trim() || undefined;
          }
        } catch {}
        if (batchZipCity === undefined && batchZipState === undefined) {
          Modal.warning({
            centered: true,
            title: `未查询到匹配的邮编信息 [已输入邮编: ${zip}],请核实邮政编码是否正确.`,
            okText: '关闭',
          });
        }
      } else if (zip) {
        // 邮编已填写但格式无效（非 5 位/9 位数字），仍会写入邮编列但不更新城市和州，弹一次无效提醒
        Modal.warning({
          centered: true,
          title: `邮编格式无效 [已输入: ${zip}]，请使用 5 位或 9 位数字格式（如 12345 或 12345-6789）.`,
          okText: '关闭',
        });
      }
    }
    const nextLiuPairs = new Set<string>();
    const extraClearKeys: Array<keyof DropshipRow> = [
      'quantity',
      'need_pack',
      'pack_preference',
      'recipient',
      'company_name',
      'address_line_1',
      'address_line_2',
      'city',
      'state',
      'postal_code',
      'country',
      'phone',
      'email',
      'buyer_note',
      'need_signature',
      'need_insurance',
      'insurance_amount',
      'preferred_carrier',
      'carrier',
      'tracking_no',
      'reference_no',
      'instruction_name',
      'remarks',
    ];
    if (hasRowUpdates) {
      batchEditConfig.setter((prev) => {
      const parentWarehouseMap = new Map<string, string>();
      const parentProjectMap = new Map<string, string>();
      if (touchedSet.has('warehouse')) {
        prev.forEach((row) => {
          if (!keySet.has(row.key) || row.is_child) return;
          const nextWarehouse = String((batchEditValues as any).warehouse || '');
          if (nextWarehouse !== String(row.warehouse || '')) {
            parentWarehouseMap.set(row.key, nextWarehouse);
          }
        });
      }
      if (touchedSet.has('project')) {
        prev.forEach((row) => {
          if (!keySet.has(row.key) || row.is_child) return;
          const nextProject = String((batchEditValues as any).project || '');
          if (nextProject !== String(row.project || '')) {
            parentProjectMap.set(row.key, nextProject);
          }
        });
      }
      return prev.map((row) => {
        const childWarehouse = row.parent_key && parentWarehouseMap.has(row.parent_key)
          ? parentWarehouseMap.get(row.parent_key)
          : undefined;
        const childProject = row.parent_key && parentProjectMap.has(row.parent_key)
          ? parentProjectMap.get(row.parent_key)
          : undefined;
        let baseRow = row;
        let childWarehouseChanged = false;
        let childProjectChanged = false;
        if (childWarehouse !== undefined && childWarehouse !== String(row.warehouse || '')) {
          baseRow = { ...baseRow, warehouse: childWarehouse };
          childWarehouseChanged = true;
        }
        if (childProject !== undefined && childProject !== String(baseRow.project || '')) {
          baseRow = { ...baseRow, project: childProject };
          childProjectChanged = true;
        }
        if (!keySet.has(baseRow.key)) {
          if (childWarehouseChanged || childProjectChanged) {
            return {
              ...baseRow,
              inventory_unit: undefined,
              inventory_unit_tag: undefined,
              available_qty: undefined,
              has_logistics_package: undefined,
            };
          }
          return baseRow;
        }
        let changed = baseRow !== row;
        const next: DropshipRow = { ...baseRow };
        const nextWarehouse = (!baseRow.is_child && touchedSet.has('warehouse'))
          ? String((batchEditValues as any).warehouse || '')
          : String(baseRow.warehouse || '');
        const nextProject = touchedSet.has('project') ? String((batchEditValues as any).project || '') : String(baseRow.project || '');
        const warehouseChanged = !baseRow.is_child && touchedSet.has('warehouse') && nextWarehouse !== String(baseRow.warehouse || '');
        const projectChanged = touchedSet.has('project') && nextProject !== String(baseRow.project || '');
        batchEditActiveKeys.forEach((key) => {
          if (!touchedSet.has(key)) return;
          if (key === 'warehouse' && baseRow.is_child) return;
          (next as any)[key] = (batchEditValues as any)[key];
          changed = true;
        });
        if (touchedSet.has('postal_code') && batchZipCity !== undefined) {
          (next as any).city = batchZipCity;
          changed = true;
        }
        if (touchedSet.has('postal_code') && batchZipState !== undefined) {
          (next as any).state = batchZipState;
          changed = true;
        }
        if (!baseRow.is_child && (warehouseChanged || projectChanged)) {
          next.inventory_unit = undefined;
          next.inventory_unit_tag = undefined;
          next.available_qty = undefined;
          next.has_logistics_package = undefined;
          changed = true;
        }
        if (!baseRow.is_child && warehouseChanged && !String(nextWarehouse || '').trim()) {
          extraClearKeys.forEach((key) => {
            (next as any)[key] = undefined;
          });
          changed = true;
        }
        if (!baseRow.is_child && projectChanged && !String(nextProject || '').trim()) {
          extraClearKeys.forEach((key) => {
            (next as any)[key] = undefined;
          });
          changed = true;
        }
        if (!baseRow.is_child && (warehouseChanged || projectChanged) && String(nextWarehouse || '').trim() && String(nextProject || '').trim()) {
          nextLiuPairs.add(`${String(nextWarehouse).trim()}__${String(nextProject).trim()}`);
        }
        let resolvedLiu: LiuUnitOption | undefined;
        if (unitMode) {
          const unit = String((batchEditValues as any).inventory_unit || '').trim();
          if (unit) {
            resolvedLiu = batchEditLiuOptions.find((u) => String(u.inventory_unit_code || '').trim() === unit);
            if (resolvedLiu) {
              (next as any).inventory_unit_tag = buildLiuTagDisplay(resolvedLiu);
              changed = true;
            }
          }
        }
        if (tagMode) {
          const tag = String((batchEditValues as any).inventory_unit_tag || '').trim();
          if (tag) {
            const map = buildLiuUnitMapByTag(batchEditLiuOptions);
            const unit = map.get(tag);
            if (unit) {
              (next as any).inventory_unit = unit;
              changed = true;
            }
            if (!resolvedLiu) {
              const normalizedTag = tag.toUpperCase();
              resolvedLiu = batchEditLiuOptions.find((u) => {
                const display = buildLiuTagDisplay(u);
                if (display && display === normalizedTag) return true;
                const tags = [
                  String(u.inventory_unit_tag || '').trim(),
                  ...((u.inventory_unit_tags || []).map((t) => String(t || '').trim())),
                ]
                  .filter(Boolean)
                  .map((t) => t.toUpperCase());
                return tags.includes(normalizedTag);
              });
            }
            if (resolvedLiu) {
              (next as any).inventory_unit_tag = buildLiuTagDisplay(resolvedLiu);
              changed = true;
            }
          }
        }
        if (resolvedLiu && typeof resolvedLiu.available_qty !== 'undefined') {
          next.available_qty = resolvedLiu.available_qty;
          changed = true;
        }
        if (resolvedLiu) {
          next.has_logistics_package = resolvedLiu.has_logistics_package ?? undefined;
          changed = true;
        }
        if (childWarehouseChanged || childProjectChanged || (baseRow.is_child && (warehouseChanged || projectChanged))) {
          next.inventory_unit = undefined;
          next.inventory_unit_tag = undefined;
          next.available_qty = undefined;
          next.has_logistics_package = undefined;
          changed = true;
        }
        return changed ? next : baseRow;
      });
    });
    }
    const nextChildPairs = new Set<string>();
    if (touchedSet.has('warehouse') || touchedSet.has('project')) {
      const keySetRows = new Set(batchEditConfig.selectedKeys.map((k) => String(k)));
      (batchEditConfig.rows || []).forEach((row) => {
        if (!row.parent_key || !keySetRows.has(String(row.parent_key))) return;
        const wh = touchedSet.has('warehouse') ? String((batchEditValues as any).warehouse || '') : String(row.warehouse || '');
        const pj = touchedSet.has('project') ? String((batchEditValues as any).project || '') : String(row.project || '');
        if (String(wh || '').trim() && String(pj || '').trim()) {
          nextChildPairs.add(`${String(wh).trim()}__${String(pj).trim()}`);
        }
      });
    }
    nextLiuPairs.forEach((key) => {
      const [wh, pj] = key.split('__');
      if (wh && pj) loadLiuOptions(wh, pj, true);
    });
    nextChildPairs.forEach((key) => {
      const [wh, pj] = key.split('__');
      if (wh && pj) loadLiuOptions(wh, pj, true);
    });
    if (batchEditPrintConfigEnabled) {
      const cfg = batchEditPrintConfig.print
        ? {
            print: true,
            copies: Math.max(1, Number(batchEditPrintConfig.copies) || 1),
            paper_size: String(batchEditPrintConfig.paper_size || ''),
            placement: String(batchEditPrintConfig.placement || ''),
            explicit_no_print: false,
          }
        : { print: false, copies: 0, paper_size: '', placement: '', explicit_no_print: true };
      const mainRowKeys = new Set<string>();
      (batchEditConfig.rows || []).forEach((row) => {
        if (keySet.has(row.key)) {
          mainRowKeys.add(String(row.key));
        } else if (row.parent_key && keySet.has(row.parent_key)) {
          mainRowKeys.add(String(row.parent_key));
        }
      });
      setOtherAttachPrintConfigMap((prev) => {
        const next = { ...prev };
        mainRowKeys.forEach((rowKey) => {
          const files = otherFilesByRowKey[rowKey] || [];
          files.forEach((f) => {
            const uid = String((f as any)?.uid || '');
            if (uid) next[uid] = cfg;
          });
        });
        return next;
      });
    }
    setBatchEditOpen(false);
  };
  const openTrackingBatchModal = (config: {
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>;
    selectedKeys: React.Key[];
    rows: DropshipRow[];
  }) => {
    setTrackingBatchText('');
    setTrackingBatchConfig(config);
    setTrackingBatchOpen(true);
  };
  const openWaybillBatchModal = (config: {
    selectedKeys: React.Key[];
    rows: DropshipRow[];
  }) => {
    const rows = getSelectedMainRows(config.rows, config.selectedKeys);
    const missingTrackingRows = rows.filter((row) => !String(row.tracking_no || '').trim());
    const trackingKeyCount = new Map<string, number>();
    rows.forEach((row) => {
      const key = String(row.tracking_no || '').trim().toLowerCase();
      if (!key) return;
      trackingKeyCount.set(key, (trackingKeyCount.get(key) || 0) + 1);
    });
    const duplicateTrackingRows = rows.filter((row) => {
      const key = String(row.tracking_no || '').trim().toLowerCase();
      if (!key) return false;
      return (trackingKeyCount.get(key) || 0) > 1;
    });
    if (missingTrackingRows.length > 0 || duplicateTrackingRows.length > 0) {
      const missingIntro = `以下${missingTrackingRows.length}条记录未提供物流跟踪号,请先补充后再批量上传运单.`;
      const duplicateIntro = `以下${duplicateTrackingRows.length}条记录物流跟踪号重复,请修改后再批量上传运单.`;
      Modal.error({
        width: 760,
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>物流跟踪号校验失败</span>
            <Button
              size="small"
              onClick={() => {
                const parts: string[] = [];
                if (missingTrackingRows.length > 0) {
                  parts.push(`${missingIntro}\n${buildWaybillWarningLines(missingTrackingRows).join('\n')}`);
                }
                if (duplicateTrackingRows.length > 0) {
                  parts.push(`${duplicateIntro}\n${buildWaybillWarningLines(duplicateTrackingRows).join('\n')}`);
                }
                if (parts.length === 0) {
                  message.warning('无可复制的记录');
                  return;
                }
                navigator.clipboard.writeText(parts.join('\n\n'))
                  .then(() => message.success('复制成功'))
                  .catch(() => message.error('复制失败'));
              }}
            >
              复制
            </Button>
          </div>
        ),
        okText: '返回修改',
        content: (
          <div>
            {missingTrackingRows.length > 0 ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 8 }}>
                  {missingIntro}
                </div>
                <div style={{ maxHeight: 200, overflow: 'auto', background: '#fafafa', padding: 8, borderRadius: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
                    <span>指令编号</span>
                    <span>物流承运商</span>
                  </div>
                  {missingTrackingRows.map((row, idx) => (
                    <div key={`${row.key}-missing-${idx}`} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ justifySelf: 'start' }}>
                        <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                          {row.serial_no || '-'}
                        </span>
                      </span>
                      <span>{row.carrier || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {duplicateTrackingRows.length > 0 ? (
              <div>
                <div style={{ marginBottom: 8 }}>
                  {duplicateIntro}
                </div>
                <div style={{ maxHeight: 200, overflow: 'auto', background: '#fafafa', padding: 8, borderRadius: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
                    <span>指令编号</span>
                    <span>物流承运商</span>
                    <span>物流跟踪号</span>
                  </div>
                  {duplicateTrackingRows.map((row, idx) => (
                    <div key={`${row.key}-dup-${idx}`} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ justifySelf: 'start' }}>
                        <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                          {row.serial_no || '-'}
                        </span>
                      </span>
                      <span>{row.carrier || '-'}</span>
                      <span>{row.tracking_no || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ),
      });
      return;
    }
    setWaybillBatchFiles([]);
    setWaybillBatchLeftUnmatchedOnly(false);
    setWaybillBatchRightUnmatchedOnly(false);
    setWaybillBatchMatchedByFileUid({});
    setWaybillBatchDuplicateFileUidSet({});
    setWaybillBatchUploadMode('normal');
    setWaybillBatchAuditOn(false);
    setWaybillBatchExistingRowsSnapshot(rows.filter((row) => (waybillFilesByRowKey[row.key] || []).length > 0));
    setWaybillBatchConfig(config);
    setWaybillBatchOpen(true);
  };
  const openOtherBatchModal = (config: { selectedKeys: React.Key[]; rows: DropshipRow[] }) => {
    setOtherBatchFiles([]);
    setOtherBatchLeftUnmatchedOnly(false);
    setOtherBatchRightUnmatchedOnly(false);
    setOtherBatchMatchedByFileUid({});
    setOtherBatchConfig(config);
    setOtherBatchOpen(true);
  };
  const getSelectedMainRows = (rows: DropshipRow[], selectedKeys: React.Key[]) => {
    const keySet = new Set(selectedKeys.map((key) => String(key)));
    return rows.filter((row) => keySet.has(row.key) && !row.is_child);
  };
  const waybillBatchSelectedRows = useMemo(() => {
    if (!waybillBatchConfig) return [];
    return getSelectedMainRows(waybillBatchConfig.rows, waybillBatchConfig.selectedKeys);
  }, [waybillBatchConfig]);
  const waybillBatchMatchedRowKeySet = useMemo(() => {
    return new Set(Object.values(waybillBatchMatchedByFileUid));
  }, [waybillBatchMatchedByFileUid]);
  const waybillBatchMatchedFileUidSet = useMemo(() => {
    return new Set(Object.keys(waybillBatchMatchedByFileUid));
  }, [waybillBatchMatchedByFileUid]);
  const waybillBatchRowKeyToSerial = useMemo(() => {
    const map = new Map<string, string>();
    waybillBatchSelectedRows.forEach((row) => {
      map.set(row.key, String(row.serial_no || '').trim());
    });
    return map;
  }, [waybillBatchSelectedRows]);
  const waybillBatchRowKeyToFileName = useMemo(() => {
    const map = new Map<string, string>();
    waybillBatchFiles.forEach((file, idx) => {
      const rowKey = waybillBatchMatchedByFileUid[file.uid];
      if (rowKey) {
        map.set(rowKey, String(file.name || `文件${idx + 1}`));
      }
    });
    return map;
  }, [waybillBatchFiles, waybillBatchMatchedByFileUid]);
  const waybillBatchRowKeyToFile = useMemo(() => {
    const map = new Map<string, UploadFile>();
    waybillBatchFiles.forEach((file) => {
      const rowKey = waybillBatchMatchedByFileUid[file.uid];
      if (rowKey) {
        map.set(rowKey, file);
      }
    });
    return map;
  }, [waybillBatchFiles, waybillBatchMatchedByFileUid]);
  const waybillBatchLeftMatchedCount = useMemo(() => {
    return waybillBatchSelectedRows.filter((row) => waybillBatchMatchedRowKeySet.has(row.key)).length;
  }, [waybillBatchSelectedRows, waybillBatchMatchedRowKeySet]);
  const waybillBatchRightMatchedCount = useMemo(() => {
    return waybillBatchFiles.filter((file) => Boolean(waybillBatchMatchedByFileUid[file.uid])).length;
  }, [waybillBatchFiles, waybillBatchMatchedByFileUid]);
  const waybillBatchLeftRowsToShow = useMemo(() => {
    if (!waybillBatchLeftUnmatchedOnly) return waybillBatchSelectedRows;
    return waybillBatchSelectedRows.filter((row) => !waybillBatchMatchedRowKeySet.has(row.key));
  }, [waybillBatchLeftUnmatchedOnly, waybillBatchSelectedRows, waybillBatchMatchedRowKeySet]);
  const waybillBatchRightFilesToShow = useMemo(() => {
    if (!waybillBatchRightUnmatchedOnly) return waybillBatchFiles;
    return waybillBatchFiles.filter((file) => !waybillBatchMatchedByFileUid[file.uid]);
  }, [waybillBatchRightUnmatchedOnly, waybillBatchFiles, waybillBatchMatchedByFileUid]);
  const waybillBatchRightFileById = useMemo(() => {
    const map = new Map<string, UploadFile>();
    waybillBatchFiles.forEach((file) => map.set(file.uid, file));
    return map;
  }, [waybillBatchFiles]);
  const otherBatchSelectedRows = useMemo(() => {
    if (!otherBatchConfig) return [];
    return getSelectedMainRows(otherBatchConfig.rows, otherBatchConfig.selectedKeys);
  }, [otherBatchConfig]);
  const otherBatchSerialInfoMap = useMemo(() => {
    const map = new Map<string, { products: Array<{ unit: string; tag: string; qty: string }>; tracking: string }>();
    const rowsAll = otherBatchConfig?.rows || [];
    rowsAll.forEach((row) => {
      const serial = String(row.serial_no || '').trim();
      if (!serial) return;
      const prev = map.get(serial) || { products: [], tracking: '' };
      const unit = String(row.inventory_unit || '').trim();
      const tag = String(row.inventory_unit_tag || '').trim();
      const qty = String(row.quantity ?? '').trim();
      if (unit || tag || qty) {
        prev.products.push({ unit, tag, qty });
      }
      const tracking = String(row.tracking_no || '').trim();
      if (tracking && !prev.tracking) prev.tracking = tracking;
      map.set(serial, prev);
    });
    return map;
  }, [otherBatchConfig]);
  const otherBatchMatchedRowKeySet = useMemo(() => {
    return new Set(Object.values(otherBatchMatchedByFileUid));
  }, [otherBatchMatchedByFileUid]);
  const otherBatchRowKeyToFiles = useMemo(() => {
    const map = new Map<string, UploadFile[]>();
    otherBatchFiles.forEach((file) => {
      const rowKey = otherBatchMatchedByFileUid[file.uid];
      if (!rowKey) return;
      const list = map.get(rowKey) || [];
      list.push(file);
      map.set(rowKey, list);
    });
    return map;
  }, [otherBatchFiles, otherBatchMatchedByFileUid]);
  const otherBatchLeftMatchedCount = useMemo(() => {
    return otherBatchSelectedRows.filter((row) => otherBatchMatchedRowKeySet.has(row.key)).length;
  }, [otherBatchSelectedRows, otherBatchMatchedRowKeySet]);
  const otherBatchRightMatchedCount = useMemo(() => {
    return otherBatchFiles.filter((file) => Boolean(otherBatchMatchedByFileUid[file.uid])).length;
  }, [otherBatchFiles, otherBatchMatchedByFileUid]);
  const otherBatchLeftRowsToShow = useMemo(() => {
    if (!otherBatchLeftUnmatchedOnly) return otherBatchSelectedRows;
    return otherBatchSelectedRows.filter((row) => !otherBatchMatchedRowKeySet.has(row.key));
  }, [otherBatchLeftUnmatchedOnly, otherBatchSelectedRows, otherBatchMatchedRowKeySet]);
  const otherBatchRightFilesToShow = useMemo(() => {
    if (!otherBatchRightUnmatchedOnly) return otherBatchFiles;
    return otherBatchFiles.filter((file) => !otherBatchMatchedByFileUid[file.uid]);
  }, [otherBatchRightUnmatchedOnly, otherBatchFiles, otherBatchMatchedByFileUid]);
  const otherBatchRightFileById = useMemo(() => {
    const map = new Map<string, UploadFile>();
    otherBatchFiles.forEach((file) => map.set(file.uid, file));
    return map;
  }, [otherBatchFiles]);
  const waybillBatchRightSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const handleWaybillFileDragEnd = ({ active, over }: { active: { id: string | number }; over?: { id: string | number } | null }) => {
    if (!over || active.id === over.id) return;
    const subsetIds = waybillBatchRightFilesToShow.map((file) => file.uid);
    const fromIndex = subsetIds.indexOf(String(active.id));
    const toIndex = subsetIds.indexOf(String(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    const reorderedIds = arrayMove(subsetIds, fromIndex, toIndex);
    setWaybillBatchFiles((prev) => {
      const reorderedFiles = reorderedIds.map((id) => waybillBatchRightFileById.get(id)).filter(Boolean) as UploadFile[];
      let cursor = 0;
      return prev.map((file) => {
        if (!subsetIds.includes(file.uid)) return file;
        const nextFile = reorderedFiles[cursor];
        cursor += 1;
        return nextFile || file;
      });
    });
  };
  const handleOtherFileDragEnd = ({ active, over }: { active: { id: string | number }; over?: { id: string | number } | null }) => {
    if (!over || active.id === over.id) return;
    const subsetIds = otherBatchRightFilesToShow.map((file) => file.uid);
    const fromIndex = subsetIds.indexOf(String(active.id));
    const toIndex = subsetIds.indexOf(String(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    const reorderedIds = arrayMove(subsetIds, fromIndex, toIndex);
    setOtherBatchFiles((prev) => {
      const reorderedFiles = reorderedIds.map((id) => otherBatchRightFileById.get(id)).filter(Boolean) as UploadFile[];
      let cursor = 0;
      return prev.map((file) => {
        if (!subsetIds.includes(file.uid)) return file;
        const nextFile = reorderedFiles[cursor];
        cursor += 1;
        return nextFile || file;
      });
    });
  };
  const SortableWaybillFileRow = ({ file, idx }: { file: UploadFile; idx: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: file.uid });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.7 : 1,
    };
    return (
      <div
        ref={setNodeRef}
        style={{
          ...style,
          display: 'grid',
          gridTemplateColumns: '260px 120px 200px',
          fontSize: 12,
          color: '#595959',
          padding: '4px 0',
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'center',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
          <Tooltip title={file.name || `文件${idx + 1}`}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name || `文件${idx + 1}`}
            </span>
          </Tooltip>
          {waybillBatchDuplicateFileUidSet[file.uid] ? (
            <Tooltip title="有重复值">
              <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />
            </Tooltip>
          ) : null}
        </span>
        {(() => {
          const matchedSerial = waybillBatchMatchedByFileUid[file.uid]
            ? waybillBatchRowKeyToSerial.get(waybillBatchMatchedByFileUid[file.uid])
            : '';
          if (!matchedSerial) return <span>-</span>;
          return (
            <span style={{ justifySelf: 'start' }}>
              <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                {matchedSerial}
              </span>
            </span>
          );
        })()}
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <Button size="small" onClick={() => openWaybillManualMatch(file)}>手动匹配</Button>
          <Button size="small" onClick={() => openWaybillPreview(file)}>预览</Button>
          <Button size="small" {...attributes} {...listeners} style={{ cursor: 'grab' }}>
            按住拖拽
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setWaybillBatchFiles((prev) => prev.filter((item) => item.uid !== file.uid));
              setWaybillBatchMatchedByFileUid((prev) => {
                const next = { ...prev };
                delete next[file.uid];
                return next;
              });
              setWaybillBatchDuplicateFileUidSet((prev) => {
                const next = { ...prev };
                delete next[file.uid];
                return next;
              });
            }}
          >
            删除
          </Button>
        </span>
      </div>
    );
  };
  const SortableOtherFileRow = ({ file, idx }: { file: UploadFile; idx: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: file.uid });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.7 : 1,
    };
    return (
      <div
        ref={setNodeRef}
        style={{
          ...style,
          display: 'grid',
          gridTemplateColumns: '0.9fr 140px 260px',
          fontSize: 12,
          color: '#595959',
          padding: '4px 0',
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'center',
        }}
      >
        <Tooltip title={file.name || `文件${idx + 1}`}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name || `文件${idx + 1}`}
          </span>
        </Tooltip>
        {otherBatchMatchedByFileUid[file.uid]
          ? (
            <span style={{ justifySelf: 'start', display: 'flex', alignItems: 'center' }}>
              <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                {otherBatchSelectedRows.find((row) => row.key === otherBatchMatchedByFileUid[file.uid])?.serial_no || '-'}
              </span>
            </span>
          )
          : (
            <span style={{ justifySelf: 'start', display: 'flex', alignItems: 'center' }}>-</span>
          )}
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <Button size="small" onClick={() => {
            setOtherManualMatchFile(file);
            setOtherManualMatchValue('');
            setOtherManualMatchOpen(true);
          }}>
            手动匹配
          </Button>
          <Button size="small" onClick={() => openWaybillPreview(file)}>预览</Button>
          {otherAttachmentPrintable ? (
            <Tooltip title={formatPrintConfigForTooltip(otherAttachPrintConfigMap[file.uid] || {})}>
              <Button size="small" onClick={() => setOtherPrintConfigModalFiles([file])}>打印需求</Button>
            </Tooltip>
          ) : null}
          <Button size="small" {...attributes} {...listeners} style={{ cursor: 'grab' }}>按住拖拽</Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setOtherBatchFiles((prev) => prev.filter((item) => item.uid !== file.uid));
              setOtherBatchMatchedByFileUid((prev) => {
                const next = { ...prev };
                delete next[file.uid];
                return next;
              });
            }}
          >
            删除
          </Button>
        </span>
      </div>
    );
  };
  const confirmTrackingBatch = () => {
    if (!trackingBatchConfig) {
      setTrackingBatchOpen(false);
      return;
    }
    const rows = getSelectedMainRows(trackingBatchConfig.rows, trackingBatchConfig.selectedKeys);
    if (rows.length === 0) {
      message.warning('未选中可填写物流跟踪号的主行记录');
      return;
    }
    const lines = trackingBatchText
      .split('\n')
      .map((l) => String(l || '').trim())
      .filter(Boolean);
    if (lines.length === 0) {
      message.warning('请填写物流跟踪号');
      return;
    }
    if (lines.length < rows.length) {
      message.warning('物流跟踪号数量不足,将仅覆盖前N条记录');
    }
    if (lines.length > rows.length) {
      message.warning('物流跟踪号数量超出,多余内容将被忽略');
    }
    const linesToUse = lines.slice(0, rows.length);
    const assignMap = new Map<string, string>();
    rows.forEach((row, idx) => {
      if (linesToUse[idx]) {
        assignMap.set(row.key, linesToUse[idx]);
      }
    });
    trackingBatchConfig.setter((prev) =>
      prev.map((row) => {
        if (!assignMap.has(row.key)) return row;
        return { ...row, tracking_no: assignMap.get(row.key) };
      }),
    );
    setTrackingBatchOpen(false);
  };
  const copyWaybillWarningText = (
    rows: DropshipRow[],
    intro?: string,
    includeAttachment?: boolean,
  ) => {
    const lines = rows.map((row) => {
      const base = `指令编号:${row.serial_no || '-'} | 物流承运商:${row.carrier || '-'} | 物流跟踪号:${row.tracking_no || '-'}`;
      if (!includeAttachment) return base;
      const attachments = (waybillFilesByRowKey[row.key] || [])
        .map((f) => String((f as any)?.name || '').trim())
        .filter(Boolean)
        .join(', ');
      return `${base} | 运单附件:${attachments || '-'}`;
    });
    if (lines.length === 0) {
      message.warning('无可复制的记录');
      return;
    }
    const content = intro ? `${intro}\n${lines.join('\n')}` : lines.join('\n');
    navigator.clipboard.writeText(content)
      .then(() => message.success('复制成功'))
      .catch(() => message.error('复制失败'));
  };
  const buildWaybillWarningLines = (rows: DropshipRow[]) => {
    return rows.map((row) => (
      `指令编号:${row.serial_no || '-'} | 物流承运商:${row.carrier || '-'} | 物流跟踪号:${row.tracking_no || '-'}`
    ));
  };
  const copyOtherAttachmentWarningText = (rows: DropshipRow[], intro: string) => {
    const lines = rows.map((row) => {
      const attachments = (otherFilesByRowKey[row.key] || [])
        .map((f) => String((f as any)?.name || '').trim())
        .filter(Boolean)
        .join(', ');
      return `指令编号:${row.serial_no || '-'} | 其他附件:${attachments || '-'}`;
    });
    if (lines.length === 0) {
      message.warning('无可复制的记录');
      return;
    }
    const content = `${intro}\n${lines.join('\n')}`;
    navigator.clipboard.writeText(content)
      .then(() => message.success('复制成功'))
      .catch(() => message.error('复制失败'));
  };
  const buildOtherBatchFileKey = (file: UploadFile) => {
    const name = String(file.name || '').trim();
    const size = (file as any)?.size ?? (file as any)?.originFileObj?.size ?? '';
    const lastModified = (file as any)?.originFileObj?.lastModified ?? '';
    return `${name}__${size}__${lastModified}`;
  };
  const confirmOtherBatch = () => {
    if (!otherBatchConfig) {
      setOtherBatchOpen(false);
      return;
    }
    if (otherAttachmentPrintable) {
      const printLacks: string[] = [];
      otherBatchFiles.forEach((file) => {
        const rowKey = otherBatchMatchedByFileUid[file.uid];
        if (!rowKey) return;
        const cfg = otherAttachPrintConfigMap[String(file.uid || '')] || {};
        if (!cfg.print) return;
        const valid = typeof cfg.copies === 'number' && cfg.copies >= 1 && !!cfg.paper_size && !!cfg.placement;
        if (!valid) {
          printLacks.push(String(file.name || '附件'));
        }
      });
      if (printLacks.length > 0) {
        message.error(`需打印附件请填写完整打印设置（打印份数、纸张尺寸、放置位置）：${printLacks.slice(0, 2).join('、')}${printLacks.length > 2 ? ' 等' : ''}`);
        return;
      }
    }
    const rowKeyToFiles = new Map<string, UploadFile[]>();
    otherBatchFiles.forEach((file) => {
      const rowKey = otherBatchMatchedByFileUid[file.uid];
      if (!rowKey) return;
      if (!rowKeyToFiles.has(rowKey)) {
        rowKeyToFiles.set(rowKey, []);
      }
      rowKeyToFiles.get(rowKey)?.push(file);
    });
    if (rowKeyToFiles.size === 0) {
      setOtherBatchOpen(false);
      return;
    }
    setOtherFilesByRowKey((prev) => {
      const next = { ...prev };
      rowKeyToFiles.forEach((files, rowKey) => {
        const existing = next[rowKey] || [];
        const existingKeys = new Set(existing.map(buildOtherBatchFileKey));
        const merged = [...existing];
        files.forEach((file) => {
          const key = buildOtherBatchFileKey(file);
          if (existingKeys.has(key)) return;
          existingKeys.add(key);
          merged.push(file);
        });
        next[rowKey] = merged;
      });
      return next;
    });
    setOtherBatchOpen(false);
  };
  const confirmWaybillBatch = async () => {
    if (!waybillBatchConfig) {
      setWaybillBatchOpen(false);
      return;
    }
    const rows = getSelectedMainRows(waybillBatchConfig.rows, waybillBatchConfig.selectedKeys);
    const assignMap = new Map<string, UploadFile>();
    Object.entries(waybillBatchMatchedByFileUid).forEach(([uid, rowKey]) => {
      const file = waybillBatchRightFileById.get(uid);
      if (!file) return;
      assignMap.set(rowKey, file);
    });
    setWaybillFilesByRowKey((prev) => {
      const next = { ...prev };
      assignMap.forEach((file, key) => {
        next[key] = [file];
      });
      return next;
    });
    if (waybillBatchAuditOn) {
      beginWaybillRun();
      try {
        const results: Array<{ serial: string; fileName: string; label: string; message: string; fileUid?: string }> = [];
        const matchedRowKeySet = new Set(assignMap.keys());
        for (const row of rows) {
          if (!matchedRowKeySet.has(row.key)) continue;
          const file = assignMap.get(row.key);
          if (!file) continue;
          const tracking = String(row.tracking_no || '').trim();
          const audit = await auditWaybillFile(file, tracking);
          if (!audit.ok) {
            (audit.issues || []).forEach((issue) => {
              results.push({
                serial: String(row.serial_no || '').trim(),
                fileName: audit.fileName || String((file as any)?.name || '附件'),
                label: issue.label || '',
                message: issue.message || '',
                fileUid: String((file as any)?.uid || ''),
              });
            });
          }
        }
        setWaybillBatchAuditResults(results);
        if (results.length > 0) {
          setWaybillBatchAuditOpen(true);
        }
      } finally {
        endWaybillRun();
      }
    }
    setWaybillBatchOpen(false);
  };
  const splitWaybillPdfFile = async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const total = pdfDoc.getPageCount();
    if (total <= 1) return [file];
    const baseName = file.name.replace(/\.pdf$/i, '');
    const outputFiles: File[] = [];
    for (let i = 0; i < total; i += 1) {
      const pageDoc = await PDFDocument.create();
      const [page] = await pageDoc.copyPages(pdfDoc, [i]);
      pageDoc.addPage(page);
      const bytes = await pageDoc.save();
      const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
      outputFiles.push(new File([blob], `${baseName}-第${i + 1}页.pdf`, { type: 'application/pdf' }));
    }
    return outputFiles;
  };
  const buildWaybillUploadFiles = (files: File[]) => {
    const seed = Date.now();
    return files.map((file, index) => ({
      uid: `${seed}-${index}-${file.name}`,
      name: file.name,
      status: 'done',
      originFileObj: file,
    })) as UploadFile[];
  };
  const handleWaybillBatchUploadChange = (fileList: UploadFile[]) => {
    if (waybillBatchUploadMode === 'split') return;
    setWaybillBatchFiles(fileList);
    setWaybillBatchMatchedByFileUid((prev) => {
      const allowed = new Set(fileList.map((f) => f.uid));
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([uid, rowKey]) => {
        if (allowed.has(uid)) next[uid] = rowKey;
      });
      return next;
    });
    setWaybillBatchDuplicateFileUidSet((prev) => {
      const allowed = new Set(fileList.map((f) => f.uid));
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((uid) => {
        if (allowed.has(uid)) next[uid] = true;
      });
      return next;
    });
  };
  const handleWaybillBatchSplitFile = async (origin: File, _fileList?: UploadFile[]) => {
    if (!/\.pdf$/i.test(origin.name)) {
      message.warning('多页Pdf自动拆分模式仅支持上传pdf文件');
      return;
    }
    message.loading({ content: '正在拆分Pdf...', key: 'waybill-split' });
    try {
      const pages = await splitWaybillPdfFile(origin);
      const uploadFiles = buildWaybillUploadFiles(pages);
      setWaybillBatchFiles((prev) => [...prev, ...uploadFiles]);
      message.success({ content: `已拆分为${uploadFiles.length}个单页文件`, key: 'waybill-split' });
    } catch (error) {
      message.error({ content: 'Pdf拆分失败', key: 'waybill-split' });
    }
  };
  const getWaybillFileMatchKey = (name: string) => {
    const trimmed = String(name || '').trim().toLowerCase();
    if (!trimmed) return '';
    const base = trimmed.replace(/\.[^/.]+$/, '');
    return base.trim();
  };
  const getWaybillSerialMatchKey = (serial?: string | number) => {
    const cleaned = String(serial || '').trim().toUpperCase().replace(/^(IM|AP|WH)[-_]?/, '');
    const digits = cleaned.replace(/\D+/g, '');
    return digits;
  };
  const handleMatchBySerialFileName = () => {
    setWaybillBatchDuplicateFileUidSet({});
    if (!waybillBatchConfig) {
      message.warning('未选择指令记录');
      return;
    }
    const rows = waybillBatchSelectedRows;
    if (rows.length === 0 || waybillBatchFiles.length === 0) {
      message.warning('未找到可匹配的指令或附件');
      return;
    }
    const fileKeyCounts = new Map<string, number>();
    const fileKeyFiles = new Map<string, UploadFile[]>();
    waybillBatchFiles.forEach((file) => {
      const key = getWaybillFileMatchKey(String(file.name || ''));
      if (!key) return;
      fileKeyCounts.set(key, (fileKeyCounts.get(key) || 0) + 1);
      const list = fileKeyFiles.get(key) || [];
      list.push(file);
      fileKeyFiles.set(key, list);
    });
    const duplicateKeys = Array.from(fileKeyCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([key]) => key);
    if (duplicateKeys.length > 0) {
      showWaybillDuplicateNameModal(
        duplicateKeys.map((key) => ({ key, files: fileKeyFiles.get(key) || [] })),
      );
      return;
    }
    const rowBuckets = new Map<string, DropshipRow[]>();
    rows.forEach((row) => {
      const key = getWaybillSerialMatchKey(String(row.serial_no || ''));
      if (!key) return;
      const list = rowBuckets.get(key) || [];
      list.push(row);
      rowBuckets.set(key, list);
    });
    const matchedRowKeys = new Set<string>();
    const nextMap: Record<string, string> = {};
    waybillBatchFiles.forEach((file) => {
      const fileKey = getWaybillFileMatchKey(String(file.name || ''));
      if (!fileKey) return;
      const candidates = rowBuckets.get(fileKey) || [];
      const target = candidates.find((row) => !matchedRowKeys.has(row.key));
      if (!target) return;
      matchedRowKeys.add(target.key);
      nextMap[file.uid] = target.key;
    });
    setWaybillBatchMatchedByFileUid(nextMap);
    const total = waybillBatchFiles.length;
    const matched = Object.keys(nextMap).length;
    const unmatched = Math.max(0, total - matched);
    Modal.info({
      title: '匹配结果',
      content: (
        <div style={{ lineHeight: 1.8 }}>
          <div>共尝试匹配{total}个附件文件</div>
          <div>成功匹配{matched}条指令记录</div>
          <div>未实现匹配{unmatched}个附件文件</div>
        </div>
      ),
      okText: '知道了',
    });
  };
  const handleMatchByTrackingFileName = () => {
    setWaybillBatchDuplicateFileUidSet({});
    if (!waybillBatchConfig) {
      message.warning('未选择指令记录');
      return;
    }
    const rows = waybillBatchSelectedRows;
    if (rows.length === 0 || waybillBatchFiles.length === 0) {
      message.warning('未找到可匹配的指令或附件');
      return;
    }
    const fileKeyCounts = new Map<string, number>();
    const fileKeyFiles = new Map<string, UploadFile[]>();
    waybillBatchFiles.forEach((file) => {
      const key = getWaybillFileMatchKey(String(file.name || ''));
      if (!key) return;
      fileKeyCounts.set(key, (fileKeyCounts.get(key) || 0) + 1);
      const list = fileKeyFiles.get(key) || [];
      list.push(file);
      fileKeyFiles.set(key, list);
    });
    const duplicateKeys = Array.from(fileKeyCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([key]) => key);
    if (duplicateKeys.length > 0) {
      showWaybillDuplicateNameModal(
        duplicateKeys.map((key) => ({ key, files: fileKeyFiles.get(key) || [] })),
      );
      return;
    }
    const rowBuckets = new Map<string, DropshipRow[]>();
    rows.forEach((row) => {
      const key = String(row.tracking_no || '').trim().toLowerCase();
      if (!key) return;
      const list = rowBuckets.get(key) || [];
      list.push(row);
      rowBuckets.set(key, list);
    });
    const matchedRowKeys = new Set<string>();
    const nextMap: Record<string, string> = {};
    waybillBatchFiles.forEach((file) => {
      const fileKey = getWaybillFileMatchKey(String(file.name || ''));
      if (!fileKey) return;
      const candidates = rowBuckets.get(fileKey) || [];
      const target = candidates.find((row) => !matchedRowKeys.has(row.key));
      if (!target) return;
      matchedRowKeys.add(target.key);
      nextMap[file.uid] = target.key;
    });
    setWaybillBatchMatchedByFileUid(nextMap);
    const total = waybillBatchFiles.length;
    const matched = Object.keys(nextMap).length;
    const unmatched = Math.max(0, total - matched);
    Modal.info({
      title: '匹配结果',
      content: (
        <div style={{ lineHeight: 1.8 }}>
          <div>共尝试匹配{total}个附件文件</div>
          <div>成功匹配{matched}条指令记录</div>
          <div>未实现匹配{unmatched}个附件文件</div>
        </div>
      ),
      okText: '知道了',
    });
  };
  const handleMatchByListOrder = () => {
    setWaybillBatchDuplicateFileUidSet({});
    if (!waybillBatchConfig) {
      message.warning('未选择指令记录');
      return;
    }
    const files = waybillBatchRightFilesToShow;
    const rows = waybillBatchLeftRowsToShow;
    if (files.length === 0 || rows.length === 0) {
      message.warning('未找到可匹配的指令或附件');
      return;
    }
    setWaybillBatchMatchedByFileUid((prev) => {
      const next = { ...prev };
      const limit = Math.min(files.length, rows.length);
      for (let i = 0; i < limit; i += 1) {
        next[files[i].uid] = rows[i].key;
      }
      return next;
    });
    const total = files.length;
    const matched = Math.min(files.length, rows.length);
    const unmatched = Math.max(0, total - matched);
    Modal.info({
      title: '匹配结果',
      content: (
        <div style={{ lineHeight: 1.8 }}>
          <div>共尝试匹配{total}个附件文件</div>
          <div>成功匹配{matched}条指令记录</div>
          <div>未实现匹配{unmatched}个附件文件</div>
        </div>
      ),
      okText: '知道了',
    });
  };
  const handleOtherMatchByListOrder = () => {
    if (!otherBatchConfig) {
      message.warning('未选择指令记录');
      return;
    }
    const files = otherBatchRightFilesToShow;
    const rows = otherBatchLeftRowsToShow;
    if (files.length === 0 || rows.length === 0) {
      message.warning('未找到可匹配的指令或附件');
      return;
    }
    setOtherBatchMatchedByFileUid((prev) => {
      const next = { ...prev };
      const limit = Math.min(files.length, rows.length);
      for (let i = 0; i < limit; i += 1) {
        next[files[i].uid] = rows[i].key;
      }
      return next;
    });
    const total = files.length;
    const matched = Math.min(files.length, rows.length);
    const unmatched = Math.max(0, total - matched);
    Modal.info({
      title: '匹配结果',
      content: (
        <div style={{ lineHeight: 1.8 }}>
          <div>共尝试匹配{total}个附件文件</div>
          <div>成功匹配{matched}条指令记录</div>
          <div>未实现匹配{unmatched}个附件文件</div>
        </div>
      ),
      okText: '知道了',
    });
  };
  const getOtherAttachmentSerialFromFileName = (name: string) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    const withoutExt = trimmed.replace(/\.[^/.]+$/, '').trim();
    const match = withoutExt.match(/^(\d+)-/);
    return match ? match[1] : '';
  };
  const handleOtherMatchBySerialFileName = () => {
    if (!otherBatchConfig) {
      message.warning('未选择指令记录');
      return;
    }
    const files = otherBatchRightFilesToShow;
    const rows = otherBatchLeftRowsToShow;
    if (files.length === 0 || rows.length === 0) {
      message.warning('未找到可匹配的指令或附件');
      return;
    }
    const rowKeyBySerial = new Map<string, string>();
    rows.forEach((row) => {
      const serialKey = getWaybillSerialMatchKey(String(row.serial_no || ''));
      if (!serialKey) return;
      if (!rowKeyBySerial.has(serialKey)) {
        rowKeyBySerial.set(serialKey, row.key);
      }
    });
    const updates: Record<string, string> = {};
    files.forEach((file) => {
      const serialKey = getOtherAttachmentSerialFromFileName(String(file.name || ''));
      if (!serialKey) return;
      const rowKey = rowKeyBySerial.get(serialKey);
      if (!rowKey) return;
      updates[file.uid] = rowKey;
    });
    setOtherBatchMatchedByFileUid((prev) => ({ ...prev, ...updates }));
    const total = files.length;
    const matched = Object.keys(updates).length;
    const unmatched = Math.max(0, total - matched);
    Modal.info({
      title: '匹配结果',
      content: (
        <div style={{ lineHeight: 1.8 }}>
          <div>共尝试匹配{total}个附件文件</div>
          <div>成功匹配{matched}条指令记录</div>
          <div>未实现匹配{unmatched}个附件文件</div>
        </div>
      ),
      okText: '知道了',
    });
  };
  const showWaybillDuplicateNameModal = (groups: Array<{ key: string; files: UploadFile[] }>) => {
    const duplicateUids = groups.flatMap((group) => group.files.map((file) => file.uid));
    Modal.confirm({
      icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
      title: '处理运单文件名后(移除首尾空格,忽略大小写)存在同名的文件,无法执行批量匹配,请修改后再试.',
      width: 720,
      okText: '知道了',
      cancelText: '标记重复值',
      onCancel: () => {
        setWaybillBatchDuplicateFileUidSet((prev) => {
          const next = { ...prev };
          duplicateUids.forEach((uid) => {
            next[uid] = true;
          });
          return next;
        });
      },
      content: (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
            <span>文件名</span>
            <span>处理后名称</span>
          </div>
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            {groups.map((group, idx) => (
              <div
                key={`${group.key}-${idx}`}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  background: idx % 2 === 0 ? '#fafafa' : '#f5f5f5',
                  marginBottom: 8,
                }}
              >
                {group.files.map((file, fIdx) => (
                  <div
                    key={`${group.key}-file-${fIdx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 220px',
                      fontSize: 12,
                      color: '#595959',
                      padding: '2px 0',
                    }}
                  >
                    <span>{String(file.name || '-')}</span>
                    <span>{group.key}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ),
    });
  };
  const openWaybillManualMatch = (file: UploadFile) => {
    setWaybillManualMatchFile(file);
    setWaybillManualMatchValue('');
    setWaybillManualMatchOpen(true);
  };
  const confirmWaybillManualMatch = () => {
    if (!waybillManualMatchFile) {
      setWaybillManualMatchOpen(false);
      return;
    }
    const input = String(waybillManualMatchValue || '').trim();
    const digits = input.replace(/\D+/g, '');
    if (!digits) {
      message.warning('请输入指令编号的数字部分');
      return;
    }
    const rows = waybillBatchSelectedRows;
    const target = rows.find((row) => getWaybillSerialMatchKey(String(row.serial_no || '')) === digits);
    if (!target) {
      message.warning('未找到对应的指令编号');
      return;
    }
    setWaybillBatchMatchedByFileUid((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((uid) => {
        if (next[uid] === target.key) {
          delete next[uid];
        }
      });
      next[waybillManualMatchFile.uid] = target.key;
      return next;
    });
    setWaybillManualMatchOpen(false);
    setWaybillManualMatchFile(null);
    setWaybillManualMatchValue('');
  };
  const buildWaybillBatchAuditCopyText = () => {
    const lines: string[] = [];
    lines.push('序号\t指令编号\t文件\t风险项\t详情');
    (waybillBatchAuditResults || []).forEach((item, idx) => {
      const serial = String(item.serial || '-');
      const fileName = String(item.fileName || '-');
      const label = String(item.label || '-');
      const messageText = String(item.message || '-').replace(/\n/g, ' ');
      lines.push(`${idx + 1}\t${serial}\t${fileName}\t${label}\t${messageText}`);
    });
    return lines.join('\n');
  };
  const copyWaybillBatchAudit = async () => {
    try {
      await navigator.clipboard.writeText(buildWaybillBatchAuditCopyText());
      message.success('已复制');
    } catch {
      message.error('复制失败,请重试');
    }
  };
  const applyWaybillBatchAuditWarnings = () => {
    const uids = Array.from(new Set((waybillBatchAuditResults || [])
      .map((item) => String(item.fileUid || '').trim())
      .filter(Boolean)));
    if (uids.length === 0) return;
    setWaybillAuditWarningByUid((prev) => {
      const next = { ...prev };
      uids.forEach((uid) => {
        next[uid] = true;
      });
      return next;
    });
  };
  const cancelWaybillMatchForRow = (rowKey: string) => {
    setWaybillBatchMatchedByFileUid((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((uid) => {
        if (next[uid] === rowKey) {
          delete next[uid];
        }
      });
      return next;
    });
  };
  const openWaybillPreview = (file: UploadFile) => {
    const name = String((file as any)?.name || '运单文件');
    const ext = getFileExt(name);
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
    const isPdf = ext === 'pdf';
    const isTable = ['xlsx', 'csv'].includes(ext);
    const isDocx = ext === 'docx';
    const isDoc = ext === 'doc';
    setWaybillPreviewTitle(name);
    setWaybillPreviewLoading(false);
    setWaybillPreviewHtml('');
    setWaybillPreviewTable(null);
    if (waybillPreviewUrlRef.current) {
      URL.revokeObjectURL(waybillPreviewUrlRef.current);
      waybillPreviewUrlRef.current = '';
    }
    const origin = (file as any)?.originFileObj as File | undefined;
    if (isImage || isPdf || isDoc) {
      const url = origin ? URL.createObjectURL(origin) : (file.url ? String(file.url) : '');
      if (!url) {
        message.warning('无法预览该文件');
        return;
      }
      if (origin) {
        waybillPreviewUrlRef.current = url;
      }
      setWaybillPreviewUrl(url);
      setWaybillPreviewType(isImage ? 'image' : isPdf ? 'pdf' : 'file');
      setWaybillPreviewOpen(true);
      return;
    }
    if (isTable && origin) {
      setWaybillPreviewLoading(true);
      setWaybillPreviewOpen(true);
      origin.arrayBuffer()
        .then((buffer) => {
          const wb = XLSX.read(buffer, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
          const rows = data.slice(0, 101);
          const headers = (rows[0] || []).map((v) => String(v ?? ''));
          const body = rows.slice(1).map((row) => row.slice(0, 20).map((v) => String(v ?? '')));
          setWaybillPreviewTable({ headers, rows: body });
          setWaybillPreviewType('table');
        })
        .catch(() => {
          message.warning('无法预览该文件');
        })
        .finally(() => {
          setWaybillPreviewLoading(false);
        });
      return;
    }
    if (isDocx && origin) {
      setWaybillPreviewLoading(true);
      setWaybillPreviewOpen(true);
      origin.arrayBuffer()
        .then((buffer) => mammoth.convertToHtml({ arrayBuffer: buffer }))
        .then((result) => {
          setWaybillPreviewHtml(DOMPurify.sanitize(result?.value || ''));
          setWaybillPreviewType('html');
        })
        .catch(() => {
          message.warning('无法预览该文件');
        })
        .finally(() => {
          setWaybillPreviewLoading(false);
        });
      return;
    }
    message.warning('无法预览该文件');
  };
  const getSelectedWarehouseProjectCounts = (rows: DropshipRow[], selectedKeys: React.Key[]) => {
    const keySet = new Set(selectedKeys.map((key) => String(key)));
    const warehouses = new Set<string>();
    const projects = new Set<string>();
    rows.forEach((row) => {
      if (!keySet.has(row.key)) return;
      const wh = String(row.warehouse || '').trim();
      const pj = String(row.project || '').trim();
      if (wh) warehouses.add(wh);
      if (pj) projects.add(pj);
    });
    return { warehouseCount: warehouses.size, projectCount: projects.size };
  };
  const getSelectedMinAvailableQty = (rows: DropshipRow[], selectedKeys: React.Key[]) => {
    const keySet = new Set(selectedKeys.map((key) => String(key)));
    const values = rows
      .filter((row) => keySet.has(row.key))
      .map((row) => Number(row.available_qty))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (values.length === 0) return undefined;
    return Math.min(...values);
  };
  const selectedRowsHaveUnitAndTag = (rows: DropshipRow[], selectedKeys: React.Key[]) => {
    const keySet = new Set(selectedKeys.map((key) => String(key)));
    const selectedRows = rows.filter((row) => keySet.has(row.key));
    if (selectedRows.length === 0) return false;
    return selectedRows.every((row) => {
      const unit = String(row.inventory_unit || '').trim();
      const tag = String(row.inventory_unit_tag || '').trim();
      return !!unit && !!tag;
    });
  };
  const getBatchFieldOptions = (key: keyof DropshipRow) => {
    if (key === 'inventory_unit') {
      return batchEditLiuOptions.map((u) => ({
        value: u.inventory_unit_code,
        label: u.inventory_unit_code,
      }));
    }
    if (key === 'inventory_unit_tag') {
      const formatTagLabel = (u: LiuUnitOption) => {
        const sortingText = (u.sorting_codes || []).filter(Boolean).join(' ') || '-';
        const customText = (u.custom_codes || []).filter(Boolean).join(' ') || '-';
        return `${sortingText} | ${customText}`;
      };
      const map = new Map<string, { value: string; label: string }>();
      batchEditLiuOptions.forEach((u) => {
        const tags = [
          String(u.inventory_unit_tag || '').trim(),
          ...((u.inventory_unit_tags || []).map((t) => String(t || '').trim())),
        ].filter(Boolean);
        tags.forEach((tag) => {
          if (!map.has(tag)) map.set(tag, { value: tag, label: formatTagLabel(u) });
        });
      });
      const options = Array.from(map.values());
      const byLabel = new Map<string, { value: string; label: string }>();
      options.forEach((opt) => {
        const lab = opt.label;
        const existing = byLabel.get(lab);
        if (!existing) {
          byLabel.set(lab, opt);
        } else if (opt.value === lab) {
          byLabel.set(lab, opt);
        }
      });
      return Array.from(byLabel.values());
    }
    return batchFieldConfigMap[key]?.options || [];
  };
  const cleanupAttachmentsByRowKeys = (rowKeys: string[]) => {
    const keySet = new Set(rowKeys.filter(Boolean));
    if (keySet.size === 0) return;
    setWaybillFilesByRowKey((prev) => {
      const next = { ...prev };
      keySet.forEach((key) => delete next[key]);
      return next;
    });
    setOtherFilesByRowKey((prev) => {
      const next = { ...prev };
      keySet.forEach((key) => delete next[key]);
      return next;
    });
    setWaybillExpandedByRowKey((prev) => {
      const next = { ...prev };
      keySet.forEach((key) => delete next[key]);
      return next;
    });
    setOtherExpandedByRowKey((prev) => {
      const next = { ...prev };
      keySet.forEach((key) => delete next[key]);
      return next;
    });
    setWaybillAuditWarningByUid((prev) => {
      let changed = false;
      const next = { ...prev };
      keySet.forEach((key) => {
        const files = waybillFilesByRowKey[key] || [];
        files.forEach((f) => {
          const uid = String((f as any)?.uid || f?.name || '');
          if (uid && next[uid]) {
            delete next[uid];
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  };

  const removeRow = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    row: DropshipRow,
    serialPrefix: string,
    serialSeqRef: React.MutableRefObject<number>,
  ) => {
    if (!row.is_child) {
      const rowKey = String(row.key || '').trim();
      cleanupAttachmentsByRowKeys([rowKey]);
    }
    setter((prev) => {
      const filtered = row.is_child
        ? prev.filter((item) => item.key !== row.key)
        : prev.filter((item) => item.key !== row.key && item.parent_key !== row.key);
      const resequenced = resequenceSerials(filtered, serialPrefix, serialSeqRef);
      debugRows('after remove row', resequenced, serialPrefix, undefined, serialSeqRef.current);
      return resequenced;
    });
  };

  const removeSelectedRows = (
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    selectedKeys: React.Key[],
    clearSelected: React.Dispatch<React.SetStateAction<React.Key[]>>,
    serialPrefix: string,
    serialSeqRef: React.MutableRefObject<number>,
  ) => {
    const keySet = new Set(selectedKeys.map((key) => String(key)));
    let removedMainKeys: string[] = [];
    setter((prev) => {
      removedMainKeys = prev
        .filter((row) => !row.is_child && keySet.has(row.key))
        .map((row) => String(row.key || '').trim())
        .filter(Boolean);
      const filtered = prev.filter(
        (row) => !keySet.has(row.key) && !keySet.has(String(row.parent_key || '')),
      );
      const resequenced = resequenceSerials(filtered, serialPrefix, serialSeqRef);
      debugRows('after remove selected', resequenced, serialPrefix, undefined, serialSeqRef.current);
      return resequenced;
    });
    cleanupAttachmentsByRowKeys(removedMainKeys);
    clearSelected([]);
  };

  const resequenceSerials = (
    rows: DropshipRow[],
    prefix: string,
    seqRef: React.MutableRefObject<number>,
  ) => {
    let counter = 1;
    const serialByParent = new Map<string, string>();
    const nextRows = rows.map((row) => {
      if (!row.is_child) {
        const serial = `${prefix}${counter++}`;
        serialByParent.set(row.key, serial);
        return { ...row, serial_no: serial };
      }
      const parentSerial = row.parent_key ? serialByParent.get(row.parent_key) : row.serial_no;
      return { ...row, serial_no: parentSerial };
    });
    seqRef.current = counter;
    return nextRows;
  };

  const countSerials = (rows: DropshipRow[]) => {
    const set = new Set<string>();
    rows.forEach((row) => {
      const sn = String(row.serial_no || '').trim();
      if (sn) set.add(sn);
    });
    return set.size;
  };

  const computeStats = (rows: DropshipRow[]) => {
    const warehouseSet = new Set<string>();
    const projectSet = new Set<string>();
    const unitSet = new Set<string>();
    const serialGroups = new Map<string, { unitSet: Set<string>; qtySum: number }>();
    let totalQty = 0;

    rows.forEach((row) => {
      const warehouse = String(row.warehouse || '').trim();
      if (warehouse) warehouseSet.add(warehouse);
      const project = String(row.project || '').trim();
      if (project) projectSet.add(project);
      const unit = String(row.inventory_unit || '').trim();
      if (unit) unitSet.add(unit);
      const serial = String(row.serial_no || '').trim();
      const qty = Number(row.quantity || 0) || 0;
      totalQty += qty;
      if (serial) {
        if (!serialGroups.has(serial)) {
          serialGroups.set(serial, { unitSet: new Set<string>(), qtySum: 0 });
        }
        const group = serialGroups.get(serial)!;
        if (unit) group.unitSet.add(unit);
        group.qtySum += qty;
      }
    });

    let singleOneOrders = 0;
    let singleOneQty = 0;
    let singleMultiOrders = 0;
    let singleMultiQty = 0;
    let multiOrders = 0;
    let multiQty = 0;

    serialGroups.forEach((group) => {
      const unitKinds = group.unitSet.size;
      if (unitKinds <= 1) {
        if (group.qtySum <= 1) {
          singleOneOrders += 1;
          singleOneQty += group.qtySum;
        } else {
          singleMultiOrders += 1;
          singleMultiQty += group.qtySum;
        }
      } else {
        multiOrders += 1;
        multiQty += group.qtySum;
      }
    });

    return {
      rowCount: rows.length,
      warehouseCount: warehouseSet.size,
      projectCount: projectSet.size,
      unitKindCount: unitSet.size,
      orderCount: serialGroups.size,
      totalQty,
      singleOneOrders,
      singleOneQty,
      singleMultiOrders,
      singleMultiQty,
      multiOrders,
      multiQty,
    };
  };

  const immediateFilteredRows = useMemo(() => buildFilteredRows(immediateRows, filtersByTab.IM), [immediateRows, filtersByTab.IM]);
  const afterPackFilteredRows = useMemo(() => buildFilteredRows(afterPackRows, filtersByTab.AP), [afterPackRows, filtersByTab.AP]);
  const warehouseFilteredRows = useMemo(() => buildFilteredRows(warehouseRows, filtersByTab.WH), [warehouseRows, filtersByTab.WH]);
  const immediateSerialCount = useMemo(() => countSerials(immediateRows), [immediateRows]);
  const afterPackSerialCount = useMemo(() => countSerials(afterPackRows), [afterPackRows]);
  const warehouseSerialCount = useMemo(() => countSerials(warehouseRows), [warehouseRows]);
  const dropshipMainCount = useMemo(
    () => Number(immediateSerialCount || 0) + Number(afterPackSerialCount || 0) + Number(warehouseSerialCount || 0),
    [afterPackSerialCount, immediateSerialCount, warehouseSerialCount],
  );
  const buildTabPillStyle = (
    active: boolean,
    tone: { color: string; border: string; bg: string },
    compact = false,
  ): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: compact ? '4px 10px' : '6px 10px',
    borderRadius: 6,
    border: active ? `1px solid ${tone.border}` : '1px solid #d9d9d9',
    background: active ? tone.bg : '#ffffff',
    color: active ? tone.color : '#595959',
    fontWeight: active ? 700 : 500,
    lineHeight: 1.2,
  });
  const immediateStats = useMemo(() => computeStats(immediateRows), [immediateRows]);
  const afterPackStats = useMemo(() => computeStats(afterPackRows), [afterPackRows]);
  const warehouseStats = useMemo(() => computeStats(warehouseRows), [warehouseRows]);
  const buildSerialClassMap = (rows: DropshipRow[]) => {
    const map = new Map<string, string>();
    let idx = 0;
    rows.forEach((row) => {
      const serial = String(row.serial_no || '').trim();
      if (!serial || map.has(serial)) return;
      map.set(serial, `serial-bg-${idx % 2}`);
      idx += 1;
    });
    return map;
  };
  const immediateSerialClassMap = useMemo(() => buildSerialClassMap(immediateRows), [immediateRows]);
  const afterPackSerialClassMap = useMemo(() => buildSerialClassMap(afterPackRows), [afterPackRows]);
  const warehouseSerialClassMap = useMemo(() => buildSerialClassMap(warehouseRows), [warehouseRows]);
  const isChildReadOnly = (row: DropshipRow, field: keyof DropshipRow) =>
    !!row.is_child && !childEditableFields.has(field);
  const defaultWarehouseCode = useMemo(() => {
    const target = warehouseOptions.find((item) => item.value === 'US-1-NH');
    if (target) return target.value;
    return warehouseOptions[0]?.value;
  }, [warehouseOptions]);

  const loadLiuOptionsForRows = (rows: DropshipRow[]) => {
    const seen = new Set<string>();
    rows.forEach((row) => {
      const wh = String(row.warehouse || '').trim();
      const pj = String(row.project || '').trim();
      if (!wh || !pj) return;
      const key = buildLiuKey(wh, pj);
      if (seen.has(key)) return;
      seen.add(key);
      loadLiuOptions(wh, pj);
    });
  };

  useEffect(() => {
    let active = true;
    const loadWarehouses = async () => {
      try {
        const warehouses = await getAllWarehouses();
        if (!active) return;
        setWarehouseOptions(
          warehouses.map((item) => ({
            value: item.code,
            label: `${item.code}-${item.name}`,
          })),
        );
      } catch (error) {
        if (!active) return;
        setWarehouseOptions([]);
      }
    };
    const loadProjects = async () => {
      try {
        const projects = await getCurrentUserProjects();
        if (!active) return;
        setProjectOptions(
          projects.map((item) => ({
            value: item.project_code,
            label: [
              String(item.project_code || '').trim(),
              String((item as any).name || (item as any).project_name || '').trim(),
            ]
              .filter(Boolean)
              .join(' - '),
          })),
        );
      } catch (error) {
        if (!active) return;
        setProjectOptions([]);
      }
    };
    const loadProviders = async () => {
      try {
        const providersResp = await getLogisticsProviders({ page_size: 1000 } as any);
        if (!active) return;
        const validated = DataValidator.validatePaginatedResponse<LogisticsProvider>(providersResp?.data);
        const providerItems = validated.results;
        const normalizeCode = (v: any) => String(v || '').trim().toUpperCase();
        const firstFixed = ['USPS', 'FEDEX', 'UPS', 'DHL'];
        const lastFixed = ['LTL/FTL CARRIER', 'OTHERS'];
        const options = (providerItems || []).map((item) => {
          const codeRaw = String(item?.code || '').trim();
          const nameRaw = String(item?.name || '').trim();
          const code = normalizeCode(codeRaw);
          return {
            value: codeRaw,
            label: codeRaw && nameRaw ? `${codeRaw} - ${nameRaw}` : (codeRaw || nameRaw),
            __sortCode: code,
            __sortCodeRaw: codeRaw,
          };
        }).filter((o) => String(o.value || '').trim() !== '');
        options.sort((a, b) => {
          const ca = String(a?.__sortCode || '');
          const cb = String(b?.__sortCode || '');
          const iaFirst = firstFixed.indexOf(ca);
          const ibFirst = firstFixed.indexOf(cb);
          if (iaFirst >= 0 || ibFirst >= 0) {
            if (iaFirst < 0) return 1;
            if (ibFirst < 0) return -1;
            return iaFirst - ibFirst;
          }
          const iaLast = lastFixed.indexOf(ca);
          const ibLast = lastFixed.indexOf(cb);
          if (iaLast >= 0 || ibLast >= 0) {
            if (iaLast < 0) return -1;
            if (ibLast < 0) return 1;
            return iaLast - ibLast;
          }
          return String(a?.__sortCodeRaw || '').localeCompare(String(b?.__sortCodeRaw || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });
        setProviderOptions(
          options.map((o) => ({
            value: String(o.value || ''),
            label: String(o.label || ''),
          })),
        );
      } catch (error) {
        if (!active) return;
        setProviderOptions([]);
      }
    };
    loadWarehouses();
    loadProjects();
    loadProviders();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (restoreRef.current) return;
    restoreRef.current = true;
    const restoreDraft = async () => {
      try {
        const raw = localStorage.getItem(BULK_DRAFT_KEY);
        if (!raw) {
          setDraftReady(true);
          return;
        }
        const draft = JSON.parse(raw);
        const nextImmediate = Array.isArray(draft?.immediateRows) ? draft.immediateRows : [];
        const nextAfter = Array.isArray(draft?.afterPackRows) ? draft.afterPackRows : [];
        const nextWarehouse = Array.isArray(draft?.warehouseRows) ? draft.warehouseRows : [];
        const waybillMeta = draft?.waybillFilesMeta || {};
        const otherMeta = draft?.otherFilesMeta || {};
        const expandedWaybill = draft?.waybillExpandedByRowKey || {};
        const expandedOther = draft?.otherExpandedByRowKey || {};
        const allUids = [
          ...Object.values(waybillMeta || {}).flatMap((arr: any) => Array.isArray(arr) ? arr.map((i: any) => String(i.uid || '')) : []),
          ...Object.values(otherMeta || {}).flatMap((arr: any) => Array.isArray(arr) ? arr.map((i: any) => String(i.uid || '')) : []),
        ].filter(Boolean);
        const uniqueUids = Array.from(new Set(allUids));
        const blobMap = await idbGetFiles(uniqueUids);
        savedFileUidRef.current = new Set(uniqueUids);
        const buildFileList = (metaList: any[]) => {
          return (metaList || []).map((meta: any) => {
            const uid = String(meta.uid || '');
            const name = String(meta.name || '附件');
            const type = String(meta.type || '');
            const lastModified = Number(meta.lastModified || Date.now());
            const blob = blobMap.get(uid);
            const originFileObj = blob ? new File([blob], name, { type, lastModified }) : undefined;
            return {
              uid,
              name,
              status: 'done',
              originFileObj,
            } as UploadFile;
          });
        };
        const nextWaybillFiles: Record<string, UploadFile[]> = {};
        Object.entries(waybillMeta || {}).forEach(([rowKey, list]) => {
          nextWaybillFiles[rowKey] = buildFileList(list as any[]);
        });
        const nextOtherFiles: Record<string, UploadFile[]> = {};
        Object.entries(otherMeta || {}).forEach(([rowKey, list]) => {
          nextOtherFiles[rowKey] = buildFileList(list as any[]);
        });
        savingRef.current = true;
        setImmediateRows(nextImmediate);
        setAfterPackRows(nextAfter);
        setWarehouseRows(nextWarehouse);
        syncSeqRefs(nextImmediate, 'immediate', immediateKeySeq, 'IM', immediateSerialSeq);
        syncSeqRefs(nextAfter, 'after-pack', afterPackKeySeq, 'AP', afterPackSerialSeq);
        syncSeqRefs(nextWarehouse, 'warehouse', warehouseKeySeq, 'WH', warehouseSerialSeq);
        debugRows('after restore draft (immediate)', nextImmediate, 'IM', immediateKeySeq.current, immediateSerialSeq.current);
        debugRows('after restore draft (afterPack)', nextAfter, 'AP', afterPackKeySeq.current, afterPackSerialSeq.current);
        debugRows('after restore draft (warehouse)', nextWarehouse, 'WH', warehouseKeySeq.current, warehouseSerialSeq.current);
        debugSeqMismatch('after restore draft', nextImmediate, 'IM', immediateKeySeq.current, immediateSerialSeq.current);
        debugSeqMismatch('after restore draft', nextAfter, 'AP', afterPackKeySeq.current, afterPackSerialSeq.current);
        debugSeqMismatch('after restore draft', nextWarehouse, 'WH', warehouseKeySeq.current, warehouseSerialSeq.current);
        setWaybillFilesByRowKey(nextWaybillFiles);
        setOtherFilesByRowKey(nextOtherFiles);
        setWaybillExpandedByRowKey(expandedWaybill);
        setOtherExpandedByRowKey(expandedOther);
      } catch {
        // ignore restore errors
      } finally {
        savingRef.current = false;
        setDraftReady(true);
      }
    };
    void restoreDraft();
  }, []);

  useEffect(() => {
    if (!draftReady) return;
    if (savingRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      try {
        const buildMeta = (map: Record<string, UploadFile[]>) => {
          const result: Record<string, Array<{ uid: string; name: string; type: string; size?: number; lastModified?: number }>> = {};
          Object.entries(map || {}).forEach(([rowKey, list]) => {
            result[rowKey] = (list || []).map((file) => ({
              uid: String(file.uid || ''),
              name: String(file.name || ''),
              type: String((file as any)?.type || (file as any)?.originFileObj?.type || ''),
              size: Number((file as any)?.size || (file as any)?.originFileObj?.size || 0) || undefined,
              lastModified: Number((file as any)?.originFileObj?.lastModified || 0) || undefined,
            }));
          });
          return result;
        };
        const waybillMeta = buildMeta(waybillFilesByRowKey);
        const otherMeta = buildMeta(otherFilesByRowKey);
        const draft = {
          version: 1,
          immediateRows,
          afterPackRows,
          warehouseRows,
          waybillFilesMeta: waybillMeta,
          otherFilesMeta: otherMeta,
          waybillExpandedByRowKey,
          otherExpandedByRowKey,
        };
        localStorage.setItem(BULK_DRAFT_KEY, JSON.stringify(draft));
        if (BULK_DEBUG) {
          console.log('[BulkCreate][Debug] draft saved', {
            immediate: immediateRows.length,
            afterPack: afterPackRows.length,
            warehouse: warehouseRows.length,
          });
        }
        const allFiles: UploadFile[] = [
          ...Object.values(waybillFilesByRowKey || {}).flat(),
          ...Object.values(otherFilesByRowKey || {}).flat(),
        ];
        const keepUids = new Set<string>();
        const toSave: Array<{ uid: string; blob: Blob; name: string; type: string; lastModified: number }> = [];
        allFiles.forEach((file) => {
          const uid = String(file.uid || '');
          if (!uid) return;
          keepUids.add(uid);
          const origin = (file as any)?.originFileObj as File | undefined;
          if (origin && !savedFileUidRef.current.has(uid)) {
            toSave.push({ uid, blob: origin, name: origin.name, type: origin.type, lastModified: origin.lastModified });
            savedFileUidRef.current.add(uid);
          }
        });
        await idbPutFiles(toSave);
        await idbDeleteMissing(keepUids);
      } catch {
        // ignore save errors
      }
    }, 400);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [
    draftReady,
    immediateRows,
    afterPackRows,
    warehouseRows,
    waybillFilesByRowKey,
    otherFilesByRowKey,
    waybillExpandedByRowKey,
    otherExpandedByRowKey,
  ]);

  useEffect(() => {
    loadLiuOptionsForRows(immediateRows);
  }, [immediateRows]);

  useEffect(() => {
    loadLiuOptionsForRows(afterPackRows);
  }, [afterPackRows]);

  useEffect(() => {
    loadLiuOptionsForRows(warehouseRows);
  }, [warehouseRows]);
  useEffect(() => {
    const next = clampPage(immediatePage, immediatePageSize, immediateFilteredRows.length);
    if (next !== immediatePage) setImmediatePage(next);
  }, [immediateFilteredRows.length, immediatePage, immediatePageSize]);
  useEffect(() => {
    const next = clampPage(afterPackPage, afterPackPageSize, afterPackFilteredRows.length);
    if (next !== afterPackPage) setAfterPackPage(next);
  }, [afterPackFilteredRows.length, afterPackPage, afterPackPageSize]);
  useEffect(() => {
    const next = clampPage(warehousePage, warehousePageSize, warehouseFilteredRows.length);
    if (next !== warehousePage) setWarehousePage(next);
  }, [warehouseFilteredRows.length, warehousePage, warehousePageSize]);

  useEffect(() => {
    enforceNeedPackForRows(immediateRows, setImmediateRows);
  }, [immediateRows]);

  useEffect(() => {
    enforceNeedPackForRows(afterPackRows, setAfterPackRows);
  }, [afterPackRows]);

  useEffect(() => {
    enforceNeedPackForRows(warehouseRows, setWarehouseRows);
  }, [warehouseRows]);

  useEffect(() => {
    setImmediatePage((prev) => clampPage(prev, immediatePageSize, immediateRows.length));
  }, [immediateRows.length, immediatePageSize]);

  useEffect(() => {
    setAfterPackPage((prev) => clampPage(prev, afterPackPageSize, afterPackRows.length));
  }, [afterPackRows.length, afterPackPageSize]);

  useEffect(() => {
    setWarehousePage((prev) => clampPage(prev, warehousePageSize, warehouseRows.length));
  }, [warehouseRows.length, warehousePageSize]);

  useEffect(() => {
    if (!defaultWarehouseCode) return;
    const fillDefaultWarehouse = (
      setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    ) => {
      setter((prev) =>
        prev.map((row) =>
          row.warehouse ? row : { ...row, warehouse: defaultWarehouseCode },
        ),
      );
    };
    fillDefaultWarehouse(setImmediateRows);
    fillDefaultWarehouse(setAfterPackRows);
    fillDefaultWarehouse(setWarehouseRows);
  }, [defaultWarehouseCode]);

  const buildColumns = (
    rows: DropshipRow[],
    requiredKeys: string[],
    requiredOnly: boolean,
    setter: React.Dispatch<React.SetStateAction<DropshipRow[]>>,
    onAddInventoryUnit: (row: DropshipRow) => void,
    keyPrefix: string,
    keySeqRef: React.MutableRefObject<number>,
    serialPrefix: string,
    serialSeqRef: React.MutableRefObject<number>,
    waybillAuditOn: boolean,
  ) => {
    const shouldShow = (key: string) => !requiredOnly || requiredKeys.includes(key);
    const hideWaybillColumns = keyPrefix !== 'immediate';
    const nonRequiredKeys = new Set(['serial_no']);
    const titleWithRequired = (key: string, label: React.ReactNode, infoNode?: React.ReactNode) => {
      const required = requiredKeys.includes(key) && !nonRequiredKeys.has(key);
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>{label}</span>
          {required ? <span style={{ color: '#ff4d4f' }}>*</span> : null}
          {infoNode || null}
        </span>
      );
    };
    const visibleChildColumns = new Set([
      'serial_no',
      'project',
      'inventory_unit',
      'inventory_unit_tag',
      'available_qty',
      'quantity',
      'actions',
    ]);
    const columns = [
      {
        title: titleWithRequired(
          'serial_no',
          '指令序号',
          <Tooltip title="由系统自动生成,相同序号表示同一个指令中的多款库存单元记录.当新增/删除记录时,将自动重排序号">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'serial_no',
        width: 120,
        fixed: 'left' as const,
        hidden: !shouldShow('serial_no'),
        render: (_: string, row: DropshipRow) => {
          const style = row.is_child
            ? undefined
            : { fontWeight: 700, fontSize: 14, backgroundColor: '#fff7b0', color: '#fa541c' };
          return <Input value={row.serial_no} readOnly disabled={row.is_child} style={style} />;
        },
      },
      {
        title: titleWithRequired(
          'warehouse',
          '仓库',
          <Tooltip title="必须先选定仓库和项目,才能输入其他内容">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'warehouse',
        width: 200,
        hidden: !shouldShow('warehouse'),
        render: (_: string, row: DropshipRow) => (
          <Select
            showSearch
            allowClear
            value={row.warehouse}
            disabled={isChildReadOnly(row, 'warehouse')}
            options={warehouseOptions}
            optionFilterProp="label"
            onChange={(value) => handleWarehouseChange(setter, row, value)}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        title: titleWithRequired(
          'project',
          '项目',
          <Tooltip title="必须先选定仓库和项目,才能输入其他内容">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'project',
        width: 260,
        hidden: !shouldShow('project'),
        render: (_: string, row: DropshipRow) => (
          <Select
            showSearch
            allowClear
            value={row.project}
            disabled={isChildReadOnly(row, 'project')}
            options={projectOptions}
            optionFilterProp="label"
            onChange={(value) => handleProjectChange(setter, row, value)}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            dropdownStyle={{ minWidth: 420 }}
          />
        ),
      },
      {
        title: titleWithRequired(
          'inventory_unit',
          '库存单元',
          <Tooltip
            title={
              <div style={{ width: 380, lineHeight: 1.5 }}>
                <div>库存单元需满足以下条件:</div>
                <div>1 属于当前选定的项目</div>
                <div>2 类型为散货库存单元(LIU)</div>
                <div>3 所属分拣组中只包含此一种库存单元(若包含多种库存单元,无论其他库存单元是否有可用库存,此库存单元均判定为不可用)</div>
                <div>4 在当前选定的仓库可用库存数量&gt;0</div>
                <div>可从下拉菜单中选择或者直接输入库存单元系统编码,系统会自动匹配符合条件的库存单元.</div>
              </div>
            }
            overlayStyle={{ maxWidth: 420 }}
          >
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'inventory_unit',
        width: 180,
        hidden: !shouldShow('inventory_unit'),
        render: (_: string, row: DropshipRow) => (
          (() => {
            const isUnitReady = !!row.warehouse && !!row.project;
            const serial = String(row.serial_no || '').trim();
            const usedCodes = new Set(
              rows
                .filter((r) => r.key !== row.key && String(r.serial_no || '').trim() === serial)
                .map((r) => String(r.inventory_unit || '').trim())
                .filter(Boolean),
            );
            return (
          <AutoComplete
            value={row.inventory_unit}
            disabled={isChildReadOnly(row, 'inventory_unit') || !isUnitReady}
            options={getLiuOptionsForRow(row).map((u) => ({
              value: u.inventory_unit_code,
              label: usedCodes.has(String(u.inventory_unit_code || '').trim()) && String(row.inventory_unit || '').trim() !== String(u.inventory_unit_code || '').trim()
                ? (
                  <Tooltip title="已添加过此库存单元">
                    <span style={{ color: '#bfbfbf' }}>{u.inventory_unit_code}</span>
                  </Tooltip>
                )
                : u.inventory_unit_code,
              disabled: usedCodes.has(String(u.inventory_unit_code || '').trim())
                && String(row.inventory_unit || '').trim() !== String(u.inventory_unit_code || '').trim(),
              unit: u,
            }))}
            onSelect={(value, option) => handleInventoryUnitSelect(setter, row, value, option)}
            onChange={(value) => {
              updateRowCell(setter, row.key, 'inventory_unit', value);
              if (!String(value || '').trim()) {
                updateRowCell(setter, row.key, 'inventory_unit_tag', undefined);
                updateRowCell(setter, row.key, 'available_qty', undefined);
                updateRowCell(setter, row.key, 'has_logistics_package', undefined);
                updateRowCell(setter, row.key, 'quantity', undefined);
              } else {
                updateRowCell(setter, row.key, 'quantity', 1);
              }
            }}
            onBlur={() => handleInventoryUnitBlur(setter, row)}
            onFocus={() => loadLiuOptions(row.warehouse, row.project)}
            filterOption={(inputValue, option) =>
              String(option?.value || '').toLowerCase().includes(String(inputValue || '').toLowerCase())
            }
          >
            <Input
              allowClear
              ref={(node) => {
                inventoryUnitInputRefs.current[row.key] = node;
              }}
              suffix={
                <Tooltip title="复制">
                  <CopyOutlined
                    onClick={() => {
                      const text = String(row.inventory_unit || '');
                      if (!text) return;
                      navigator.clipboard.writeText(text);
                      message.success('已复制');
                    }}
                    style={{ color: '#8c8c8c' }}
                  />
                </Tooltip>
              }
              onPressEnter={() => {
                const enterKey = `${row.key}::inventory_unit`;
                if (pendingEnterTimerByKey.current[enterKey]) {
                  window.clearTimeout(pendingEnterTimerByKey.current[enterKey]!);
                }
                pendingEnterTimerByKey.current[enterKey] = window.setTimeout(() => {
                  if (skipNextEnterByKey.current[enterKey]) {
                    skipNextEnterByKey.current[enterKey] = false;
                    return;
                  }
                  skipNextBlurByKey.current[enterKey] = true;
                  lookupByInventoryUnit(setter, row);
                }, 0);
              }}
            />
          </AutoComplete>
            );
          })()
        ),
      },
      {
        title: titleWithRequired(
          'inventory_unit_tag',
          '库存单元标识',
          <Tooltip title="库存单元标识指的是产品的分拣条码(如“X00123ABCD”)或客户自定义编码(如“ABC-123”),将自动查找满足条件的库存单元并回填到库存单元列.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'inventory_unit_tag',
        width: 350,
        hidden: !shouldShow('inventory_unit_tag'),
        render: (_: string, row: DropshipRow) => (
          (() => {
            const isUnitReady = !!row.warehouse && !!row.project;
            const buildTagDisplay = (unit: LiuUnitOption) => {
              const sortingCodes = Array.isArray(unit.sorting_codes) ? unit.sorting_codes : [];
              const customCodes = Array.isArray(unit.custom_codes) ? unit.custom_codes : [];
              const sortingText = sortingCodes.filter(Boolean).join(' ').toUpperCase();
              const customText = customCodes.filter(Boolean).join(' ').toUpperCase();
              if (sortingText && customText) return `${sortingText} | ${customText}`;
              if (sortingText) return sortingText;
              if (customText) return customText;
              const fallback = String(unit.inventory_unit_tag || '').trim();
              return fallback ? fallback.toUpperCase() : '';
            };
            const tagTitle = String(row.inventory_unit_tag || '').trim();
            return (
              <Tooltip title={tagTitle || undefined}>
                <AutoComplete
                  allowClear
                  value={row.inventory_unit_tag}
                  disabled={isChildReadOnly(row, 'inventory_unit_tag') || !isUnitReady}
                  style={{ width: '100%' }}
                  options={getLiuOptionsForRow(row).map((u) => {
                    const label = buildTagDisplay(u);
                    return {
                      value: label,
                      label,
                      unit: u,
                    };
                  })}
                  onSelect={(value, option) => handleInventoryUnitTagSelect(setter, row, value, option)}
                  onChange={(value) => {
                    updateRowCell(setter, row.key, 'inventory_unit_tag', value);
                    if (!String(value || '').trim()) {
                      updateRowCell(setter, row.key, 'inventory_unit', undefined);
                      updateRowCell(setter, row.key, 'available_qty', undefined);
                      updateRowCell(setter, row.key, 'has_logistics_package', undefined);
                      updateRowCell(setter, row.key, 'quantity', undefined);
                    } else {
                      updateRowCell(setter, row.key, 'quantity', 1);
                    }
                  }}
                  onBlur={() => handleInventoryUnitTagBlur(setter, row)}
                  onFocus={() => loadLiuOptions(row.warehouse, row.project)}
                  filterOption={(inputValue, option) =>
                    String(option?.value || '').toLowerCase().includes(String(inputValue || '').toLowerCase())
                  }
                >
                  <Input
                    ref={(node) => {
                      inventoryUnitTagInputRefs.current[row.key] = node;
                    }}
                    onPressEnter={() => {
                      const enterKey = `${row.key}::inventory_unit_tag`;
                      if (pendingEnterTimerByKey.current[enterKey]) {
                        window.clearTimeout(pendingEnterTimerByKey.current[enterKey]!);
                      }
                      pendingEnterTimerByKey.current[enterKey] = window.setTimeout(() => {
                        if (skipNextEnterByKey.current[enterKey]) {
                          skipNextEnterByKey.current[enterKey] = false;
                          return;
                        }
                        skipNextBlurByKey.current[enterKey] = true;
                        lookupByInventoryUnitTag(setter, row);
                      }, 0);
                    }}
                    style={{ width: '100%' }}
                  />
                </AutoComplete>
              </Tooltip>
            );
          })()
        ),
      },
      {
        title: titleWithRequired(
          'available_qty',
          '可用库存',
          <Tooltip title="由系统自动计算,仅显示当前仓库中的可用库存,不包含锁定库存.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'available_qty',
        width: 160,
        hidden: !shouldShow('available_qty'),
        render: (_: number, row: DropshipRow) => (
          (() => {
            const isUnitReady = !!row.warehouse && !!row.project;
            return (
              <Input
                value={row.available_qty ?? ''}
                readOnly
                disabled={!isUnitReady}
                style={isUnitReady ? { color: 'rgba(0, 0, 0, 0.88)' } : undefined}
              />
            );
          })()
        ),
      },
      {
        title: titleWithRequired(
          'quantity',
          '发货数量',
          <Tooltip title="最小值1,不可超过可用库存数量.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'quantity',
        width: 110,
        hidden: !shouldShow('quantity'),
        render: (_: string, row: DropshipRow) => {
          const available = Number(row.available_qty);
          const hasAvailable = Number.isFinite(available) && available > 0;
          const isReady = !!row.warehouse && !!row.project;
          return (
            <InputNumber
              min={1}
              max={hasAvailable ? available : undefined}
              precision={0}
              step={1}
              value={row.quantity}
              disabled={isChildReadOnly(row, 'quantity') || !hasAvailable || !isReady}
              onChange={(value) => updateRowCell(setter, row.key, 'quantity', value || 1)}
              style={{ width: '100%' }}
            />
          );
        },
      },
      {
        title: titleWithRequired(
          'need_pack',
          '是否需打包',
          <Tooltip title="由系统自动判断,单件且无物流包装或者一票多件,均视为“需打包”">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'need_pack',
        width: 120,
        hidden: !shouldShow('need_pack'),
        render: (_: string, row: DropshipRow) => {
          const text =
            row.need_pack === undefined
              ? ''
              : row.need_pack
              ? '需打包'
              : '无需打包';
          return (
            <Tag
              color={text === '需打包' ? 'red' : text === '无需打包' ? 'green' : undefined}
              style={{ opacity: row.is_child ? 0.6 : 1 }}
            >
              {text || '自动判断中'}
            </Tag>
          );
        },
      },
      {
        title: titleWithRequired(
          'pack_preference',
          '包装偏好',
          <Tooltip title="若需打包且未选择包装偏好,仓库默认使用免费包材打包.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'pack_preference',
        width: 250,
        hidden: !shouldShow('pack_preference'),
        onCell: () => ({ style: { minWidth: 250, maxWidth: 250 } }),
        onHeaderCell: () => ({ style: { minWidth: 250, maxWidth: 250 } }),
        render: (_: string, row: DropshipRow) => (
          <Select
            showSearch
            allowClear
            value={row.pack_preference}
            disabled={isChildReadOnly(row, 'pack_preference') || !row.need_pack}
            options={packagingOptions}
            onChange={(value) => updateRowCell(setter, row.key, 'pack_preference', value)}
            style={{ width: '100%', maxWidth: 250 }}
          />
        ),
      },
      {
        title: titleWithRequired('recipient', '收件人姓名'),
        dataIndex: 'recipient',
        width: 140,
        hidden: !shouldShow('recipient'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.recipient}
            disabled={isChildReadOnly(row, 'recipient') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'recipient', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('company_name', '公司名称'),
        dataIndex: 'company_name',
        width: 160,
        hidden: !shouldShow('company_name'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.company_name}
            disabled={isChildReadOnly(row, 'company_name') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'company_name', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('address_line_1', '地址第一行'),
        dataIndex: 'address_line_1',
        width: 220,
        hidden: !shouldShow('address_line_1'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.address_line_1}
            disabled={isChildReadOnly(row, 'address_line_1') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'address_line_1', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('address_line_2', '地址第二行'),
        dataIndex: 'address_line_2',
        width: 180,
        hidden: !shouldShow('address_line_2'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.address_line_2}
            disabled={isChildReadOnly(row, 'address_line_2') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'address_line_2', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('city', '城市'),
        dataIndex: 'city',
        width: 120,
        hidden: !shouldShow('city'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.city}
            disabled={isChildReadOnly(row, 'city') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'city', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('state', '州/省'),
        dataIndex: 'state',
        width: 100,
        hidden: !shouldShow('state'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.state}
            disabled={isChildReadOnly(row, 'state') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'state', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired(
          'postal_code',
          '邮编',
          <Tooltip title="当输入邮编,将自动补填城市和州/省.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'postal_code',
        width: 140,
        hidden: !shouldShow('postal_code'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.postal_code}
            disabled={isChildReadOnly(row, 'postal_code') || !row.warehouse || !row.project}
            ref={(node) => {
              postalCodeInputRefs.current[row.key] = node;
            }}
            onChange={(e) => {
              const v = e.target.value;
              updateRowCell(setter, row.key, 'postal_code', v);
              scheduleZipLookup(setter, row, v);
            }}
            onBlur={(e) => scheduleZipLookup(setter, row, e.target.value, true)}
          />
        ),
      },
      {
        title: titleWithRequired('country', '国家'),
        dataIndex: 'country',
        width: 100,
        hidden: !shouldShow('country'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.country}
            disabled={isChildReadOnly(row, 'country') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'country', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('phone', '电话'),
        dataIndex: 'phone',
        width: 140,
        hidden: !shouldShow('phone'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.phone}
            disabled={isChildReadOnly(row, 'phone') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'phone', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('email', 'Email'),
        dataIndex: 'email',
        width: 200,
        hidden: !shouldShow('email'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.email}
            disabled={isChildReadOnly(row, 'email') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'email', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('buyer_note', '买家发货备注'),
        dataIndex: 'buyer_note',
        width: 200,
        hidden: !shouldShow('buyer_note'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.buyer_note}
            disabled={isChildReadOnly(row, 'buyer_note') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'buyer_note', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired('need_signature', '是否需要签名服务'),
        dataIndex: 'need_signature',
        width: 140,
        hidden: !shouldShow('need_signature'),
        render: (_: string, row: DropshipRow) => (
          <Select
            allowClear
            value={row.need_signature === undefined ? undefined : (row.need_signature ? 'yes' : 'no')}
            style={{ width: 110 }}
            options={[
              { value: 'yes', label: '是' },
              { value: 'no', label: '否' },
            ]}
            onChange={(v) =>
              updateRowCell(
                setter,
                row.key,
                'need_signature',
                v === undefined ? undefined : v === 'yes',
              )
            }
            disabled={isChildReadOnly(row, 'need_signature') || !row.warehouse || !row.project}
          />
        ),
      },
      {
        title: titleWithRequired('need_insurance', '是否需要保险'),
        dataIndex: 'need_insurance',
        width: 120,
        hidden: !shouldShow('need_insurance'),
        render: (_: string, row: DropshipRow) => (
          <Select
            allowClear
            value={row.need_insurance === undefined ? undefined : (row.need_insurance ? 'yes' : 'no')}
            style={{ width: 100 }}
            options={[
              { value: 'yes', label: '是' },
              { value: 'no', label: '否' },
            ]}
            onChange={(v) =>
              (() => {
                updateRowCell(
                  setter,
                  row.key,
                  'need_insurance',
                  v === undefined ? undefined : v === 'yes',
                );
                if (v === 'no') {
                  updateRowCell(setter, row.key, 'insurance_amount', undefined);
                }
              })()
            }
            disabled={isChildReadOnly(row, 'need_insurance') || !row.warehouse || !row.project}
          />
        ),
      },
      {
        title: titleWithRequired(
          'insurance_amount',
          '保险金额$',
          <Tooltip title="最低$100,最高$1000">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'insurance_amount',
        width: 120,
        hidden: keyPrefix === 'warehouse' ? false : !shouldShow('insurance_amount'),
        render: (_: string, row: DropshipRow) => {
          const insuranceEnabled = row.need_insurance === true;
          return (
            <InputNumber
              min={100}
              max={1000}
              precision={0}
              step={1}
              value={row.insurance_amount}
              disabled={isChildReadOnly(row, 'insurance_amount') || !insuranceEnabled || !row.warehouse || !row.project}
              onChange={(value) => updateRowCell(setter, row.key, 'insurance_amount', value ?? undefined)}
              style={{ width: '100%' }}
            />
          );
        },
      },
      {
        title: titleWithRequired(
          'preferred_carrier',
          '偏好物流承运商',
          <Tooltip title="若需仓库提供运单且未填写偏好物流承运商,仓库将默认使用运费最低的物流渠道发货.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'preferred_carrier',
        width: 320,
        hidden: !shouldShow('preferred_carrier'),
        onCell: () => ({ style: { minWidth: 320, maxWidth: 320 } }),
        onHeaderCell: () => ({ style: { minWidth: 320, maxWidth: 320 } }),
        render: (_: string, row: DropshipRow) => (
          <Select
            showSearch
            allowClear
            value={row.preferred_carrier}
            disabled={isChildReadOnly(row, 'preferred_carrier') || !row.warehouse || !row.project}
            options={providerOptions}
            optionFilterProp="label"
            onChange={(value) => updateRowCell(setter, row.key, 'preferred_carrier', value)}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 400 }}
          />
        ),
      },
      {
        title: titleWithRequired('carrier', '运单所属物流承运商'),
        dataIndex: 'carrier',
        width: 320,
        hidden: hideWaybillColumns || !shouldShow('carrier'),
        onCell: () => ({ style: { minWidth: 320, maxWidth: 320 } }),
        onHeaderCell: () => ({ style: { minWidth: 320, maxWidth: 320 } }),
        render: (_: string, row: DropshipRow) => (
          <Select
            showSearch
            allowClear
            value={row.carrier}
            disabled={isChildReadOnly(row, 'carrier') || !row.warehouse || !row.project}
            options={providerOptions}
            optionFilterProp="label"
            onChange={(value) => updateRowCell(setter, row.key, 'carrier', value)}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 400 }}
          />
        ),
      },
      {
        title: titleWithRequired('tracking_no', '物流跟踪号'),
        dataIndex: 'tracking_no',
        width: 220,
        hidden: hideWaybillColumns || !shouldShow('tracking_no'),
        render: (_: string, row: DropshipRow) => {
          const trackingText = String(row.tracking_no || '');
          const isReady = !!row.warehouse && !!row.project;
          return (
            <Tooltip title={trackingText ? trackingText : ''}>
              <Input
                allowClear
                className="ellipsis-input"
                value={row.tracking_no}
                disabled={isChildReadOnly(row, 'tracking_no') || !isReady}
                onChange={(e) => updateRowCell(setter, row.key, 'tracking_no', e.target.value)}
                suffix={
                  <Tooltip title="复制">
                    <CopyOutlined
                      onClick={() => {
                        const text = String(row.tracking_no || '');
                        if (!text) return;
                        navigator.clipboard.writeText(text);
                        message.success('已复制');
                      }}
                      style={{ color: '#8c8c8c' }}
                    />
                  </Tooltip>
                }
              />
            </Tooltip>
          );
        },
      },
      {
        title: titleWithRequired(
          'reference_no',
          '参考号',
          <Tooltip title="可添加销售订单号等参考号,便于后续查单.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'reference_no',
        width: 140,
        hidden: !shouldShow('reference_no'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.reference_no}
            disabled={isChildReadOnly(row, 'reference_no') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'reference_no', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired(
          'instruction_name',
          '指令命名',
          <Tooltip title="可选,为指令添加一个命名,便于后续查询">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'instruction_name',
        width: 160,
        hidden: !shouldShow('instruction_name'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.instruction_name}
            disabled={isChildReadOnly(row, 'instruction_name') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'instruction_name', e.target.value)}
          />
        ),
      },
      {
        title: titleWithRequired(
          'remarks',
          '指令备注',
          <Tooltip title="可在此处备注其他要求.">
            <InfoCircleOutlined style={{ color: '#fa541c' }} />
          </Tooltip>,
        ),
        dataIndex: 'remarks',
        width: 220,
        hidden: !shouldShow('remarks'),
        render: (_: string, row: DropshipRow) => (
          <Input
            allowClear
            value={row.remarks}
            disabled={isChildReadOnly(row, 'remarks') || !row.warehouse || !row.project}
            onChange={(e) => updateRowCell(setter, row.key, 'remarks', e.target.value)}
          />
        ),
      },
      {
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>物流运单</span>
            <span style={{ color: '#ff4d4f' }}>*</span>
            <Tooltip
              title={
                <div style={{ width: 420, lineHeight: 1.5 }}>
                  <div>必须提交</div>
                  <div>文件类型选项：pdf png jpg jpeg</div>
                  <div>每个指令只能上传一张物流运单</div>
                  <div>每个运单文件中只能包含一页pdf或图片</div>
                  <div>请尽量使用pdf文件格式</div>
                  <div>系统会直接使用4×6英寸热敏打印机打印整张运单,不做任何手动截图</div>
                  <div>请保证运单文件中不存在过多的非运单区域,如空白区域或者说明性文字,否则打印出的运单可能过小而造成条码不可扫描</div>
                  <div>请确保上传的运单和填入的物流承运商及物流跟踪号一致</div>
                  <div>附件文件前的红色叹号(!)表示此附件可能不满足审核要求</div>
                </div>
              }
              overlayStyle={{ maxWidth: 460 }}
            >
              <InfoCircleOutlined style={{ color: '#fa541c' }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'waybill_attachment',
        width: 240,
        hidden: hideWaybillColumns || !shouldShow('waybill_attachment'),
        render: (_: string, row: DropshipRow) => {
          if (row.is_child) return <span style={{ color: '#999' }}>-</span>;
          const serial = String(row.serial_no || '').trim();
          const rowKey = String(row.key || '').trim();
          const canUpload = !!row.warehouse && !!row.project;
          const hasTracking = !!String(row.tracking_no || '').trim();
          const files = waybillFilesByRowKey[rowKey] || [];
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload
                  accept=".pdf,.png,.jpg,.jpeg"
                  multiple={false}
                  fileList={files}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    if (!hasTracking) {
                      message.warning('请先填写物流跟踪号后再上传运单');
                      return Upload.LIST_IGNORE;
                    }
                    if (files.length >= 1) {
                      message.warning('每个指令只能上传一张物流运单');
                      return Upload.LIST_IGNORE;
                    }
                    const ext = getFileExt(file.name);
                    const allowed = new Set(['pdf', 'png', 'jpg', 'jpeg']);
                    if (!allowed.has(ext)) {
                      message.warning('物流运单仅允许上传 pdf/png/jpg/jpeg');
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                  onChange={async ({ fileList }) => {
                    let next = fileList || [];
                    next = next.filter((f) => {
                      const ext = getFileExt(String(f.name || ''));
                      return ['pdf', 'png', 'jpg', 'jpeg'].includes(ext);
                    });
                    if (next.length > 1) {
                      message.warning('每个指令只能上传一张物流运单');
                      next = next.slice(-1);
                    }
                    setWaybillFilesByRowKey((prev) => ({ ...prev, [rowKey]: next }));
                    if (next.length > 0 && waybillAuditOn) {
                      const tracking = String(row.tracking_no || '').trim();
                      await validateWaybillUploads(next, tracking);
                    }
                  }}
                >
                  <Tooltip title={!canUpload || !hasTracking ? '请先选择仓库-项目,并填写物流跟踪号再上传运单' : ''}>
                    <Button size="small" style={{ background: 'transparent' }} disabled={!serial || !canUpload || !hasTracking}>上传</Button>
                  </Tooltip>
                </Upload>
                {renderUploadToggle(
                  files,
                  !!waybillExpandedByRowKey[rowKey],
                  () => setWaybillExpandedByRowKey((prev) => ({ ...prev, [rowKey]: !prev[rowKey] })),
                )}
              </div>
              {renderFileList(
                files,
                !!waybillExpandedByRowKey[rowKey],
                () => setWaybillExpandedByRowKey((prev) => ({ ...prev, [rowKey]: !prev[rowKey] })),
                (file) => {
                  setWaybillFilesByRowKey((prev) => ({
                    ...prev,
                    [rowKey]: (prev[rowKey] || []).filter((f) => f.uid !== file.uid),
                  }));
                  const uid = String((file as any)?.uid || file?.name || '');
                  if (!uid) return;
                  setWaybillAuditWarningByUid((prev) => {
                    if (!prev[uid]) return prev;
                    const next = { ...prev };
                    delete next[uid];
                    return next;
                  });
                },
                waybillAuditWarningByUid,
                true,
              )}
            </div>
          );
        },
      },
      {
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>其他附件</span>
            <Tooltip
              title={
                <div style={{ width: 420, lineHeight: 1.5 }}>
                  <div>可选提交</div>
                  <div>文件类型选项：pdf xlsx csv png jpg jpeg gif doc docx</div>
                  <div>可上传其他发货文件,如装箱单/发票信息/报关信息/外箱标识等</div>
                  <div>仓库会按照文件的页面大小使用4×6英寸热敏纸或A4纸激光打印</div>
                  <div>可能产生附加费用</div>
                </div>
              }
              overlayStyle={{ maxWidth: 460 }}
            >
              <InfoCircleOutlined style={{ color: '#fa541c' }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'other_attachment',
        width: 240,
        hidden: !shouldShow('other_attachment'),
        render: (_: string, row: DropshipRow) => {
          if (row.is_child) return <span style={{ color: '#999' }}>-</span>;
          const serial = String(row.serial_no || '').trim();
          const rowKey = String(row.key || '').trim();
          const canUpload = !!row.warehouse && !!row.project;
          const files = otherFilesByRowKey[rowKey] || [];
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload
                  accept=".pdf,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.doc,.docx"
                  multiple
                  fileList={files}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    if (files.length >= 50) {
                      message.warning('其他附件最多允许上传50个文件');
                      return Upload.LIST_IGNORE;
                    }
                    const ext = getFileExt(file.name);
                    const allowed = new Set(['pdf', 'xlsx', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx']);
                    if (!allowed.has(ext)) {
                      message.warning('其他附件仅允许上传 pdf/xlsx/csv/png/jpg/jpeg/gif/doc/docx');
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                  onChange={({ fileList }) => {
                    let next = fileList || [];
                    next = next.filter((f) => {
                      const ext = getFileExt(String(f.name || ''));
                      return ['pdf', 'xlsx', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx'].includes(ext);
                    });
                    if (next.length > 50) {
                      message.warning('其他附件最多允许上传50个文件');
                      next = next.slice(0, 50);
                    }
                    const prevFiles = otherFilesByRowKey[rowKey] || [];
                    const prevUids = new Set(prevFiles.map((f) => f.uid));
                    const newlyAdded = next.filter((f) => !prevUids.has(f.uid));
                    setOtherFilesByRowKey((prev) => ({ ...prev, [rowKey]: next }));
                    if (otherAttachmentPrintable && newlyAdded.length > 0) {
                      setOtherPrintConfigModalFiles(newlyAdded);
                    }
                  }}
                >
                  <Tooltip title={canUpload ? '' : '请先选择仓库和项目再上传附件'}>
                    <Button size="small" style={{ background: 'transparent' }} disabled={!serial || !canUpload}>上传</Button>
                  </Tooltip>
                </Upload>
                {renderUploadToggle(
                  files,
                  !!otherExpandedByRowKey[rowKey],
                  () => setOtherExpandedByRowKey((prev) => ({ ...prev, [rowKey]: !prev[rowKey] })),
                )}
              </div>
              {renderFileList(
                files,
                !!otherExpandedByRowKey[rowKey],
                () => setOtherExpandedByRowKey((prev) => ({ ...prev, [rowKey]: !prev[rowKey] })),
                (file) => {
                  setOtherFilesByRowKey((prev) => ({
                    ...prev,
                    [rowKey]: (prev[rowKey] || []).filter((f) => f.uid !== file.uid),
                  }));
                  setOtherAttachPrintConfigMap((p) => {
                    const next = { ...p };
                    delete next[String(file.uid || '')];
                    return next;
                  });
                },
                undefined,
                true,
                otherAttachmentPrintable,
                otherAttachmentPrintable ? (f) => setOtherPrintConfigModalFiles([f]) : undefined,
              )}
            </div>
          );
        },
      },
      {
        title: titleWithRequired('actions', '操作'),
        dataIndex: 'actions',
        fixed: 'right' as const,
        width: 340,
        render: (_: string, row: DropshipRow) => (
          <span style={{ display: 'inline-flex', gap: 8 }}>
            {!row.is_child && (
              <Button size="small" onClick={() => onAddInventoryUnit(row)}>
                添加库存单元
              </Button>
            )}
            {!row.is_child && (
              <Button
                size="small"
                onClick={() => openCopyModal({
                  row,
                  setter,
                  keyPrefix,
                  keySeqRef,
                  serialPrefix,
                  serialSeqRef,
                })}
              >
                复制生成指令
              </Button>
            )}
            <Button danger size="small" onClick={() => removeRow(setter, row, serialPrefix, serialSeqRef)}>
              删除
            </Button>
          </span>
        ),
      },
    ];
    return columns
      .filter((col: any) => !col.hidden)
      .map((col: any) => {
        const originalRender = col.render;
        return {
          ...col,
          render: (value: any, row: DropshipRow, index: number) => {
            if (row.is_child && !visibleChildColumns.has(col.dataIndex)) {
              return <span style={{ color: '#bfbfbf' }}>-</span>;
            }
            return originalRender ? originalRender(value, row, index) : value;
          },
          onCell: (record: DropshipRow) => {
            if (col.onCell) return col.onCell(record);
            const dataKey = col.dataIndex;
            if (typeof dataKey !== 'string') return {};
            const raw = (record as any)[dataKey];
            if (raw === undefined || raw === null) return {};
            const text = typeof raw === 'string' ? raw : (typeof raw === 'number' ? String(raw) : '');
            if (!text) return {};
            return { title: text };
          },
        };
      });
  };

  const immediateRequiredKeys: Array<keyof DropshipRow> = [
    'warehouse',
    'serial_no',
    'project',
    'inventory_unit_tag',
    'available_qty',
    'inventory_unit',
    'quantity',
    'carrier',
    'tracking_no',
    'waybill_attachment',
  ];
  const afterPackRequiredKeys: Array<keyof DropshipRow> = [
    'warehouse',
    'serial_no',
    'project',
    'inventory_unit_tag',
    'available_qty',
    'inventory_unit',
    'quantity',
  ];
  const warehouseRequiredKeys: Array<keyof DropshipRow> = [
    'warehouse',
    'serial_no',
    'project',
    'inventory_unit_tag',
    'available_qty',
    'inventory_unit',
    'quantity',
    'recipient',
    'address_line_1',
    'city',
    'state',
    'postal_code',
    'country',
    'need_signature',
    'need_insurance',
    // insurance_amount 仅当 need_insurance 为 true 时在 handleBatchCreate 中校验
  ];

  const immediateColumns = useMemo(
    () => buildColumns(
      immediateRows,
      immediateRequiredKeys,
      immediateRequiredOnly,
      setImmediateRows,
      (row) => appendInventoryUnitRow(setImmediateRows, 'immediate', immediateKeySeq, row),
      'immediate',
      immediateKeySeq,
      'IM',
      immediateSerialSeq,
      immediateWaybillAuditOn,
    ),
    [
      immediateRows,
      immediateRequiredOnly,
      immediateWaybillAuditOn,
      waybillAuditWarningByUid,
      warehouseOptions,
      projectOptions,
      providerOptions,
      liuOptionsByKey,
      waybillFilesByRowKey,
      otherFilesByRowKey,
      waybillExpandedByRowKey,
      otherExpandedByRowKey,
      otherAttachPrintConfigMap,
      otherAttachmentPrintable,
    ],
  );
  const afterPackColumns = useMemo(
    () => buildColumns(
      afterPackRows,
      afterPackRequiredKeys,
      afterPackRequiredOnly,
      setAfterPackRows,
      (row) => appendInventoryUnitRow(setAfterPackRows, 'after-pack', afterPackKeySeq, row),
      'after-pack',
      afterPackKeySeq,
      'AP',
      afterPackSerialSeq,
      afterPackWaybillAuditOn,
    ),
    [
      afterPackRows,
      afterPackRequiredOnly,
      afterPackWaybillAuditOn,
      waybillAuditWarningByUid,
      warehouseOptions,
      projectOptions,
      providerOptions,
      liuOptionsByKey,
      waybillFilesByRowKey,
      otherFilesByRowKey,
      waybillExpandedByRowKey,
      otherExpandedByRowKey,
      otherAttachPrintConfigMap,
      otherAttachmentPrintable,
    ],
  );
  const warehouseColumns = useMemo(
    () => buildColumns(
      warehouseRows,
      warehouseRequiredKeys,
      warehouseRequiredOnly,
      setWarehouseRows,
      (row) => appendInventoryUnitRow(setWarehouseRows, 'warehouse', warehouseKeySeq, row),
      'warehouse',
      warehouseKeySeq,
      'WH',
      warehouseSerialSeq,
      warehouseWaybillAuditOn,
    ),
    [
      warehouseRows,
      warehouseRequiredOnly,
      warehouseWaybillAuditOn,
      waybillAuditWarningByUid,
      warehouseOptions,
      projectOptions,
      providerOptions,
      liuOptionsByKey,
      waybillFilesByRowKey,
      otherFilesByRowKey,
      waybillExpandedByRowKey,
      otherExpandedByRowKey,
      otherAttachPrintConfigMap,
      otherAttachmentPrintable,
    ],
  );

  return (
    <div style={{ padding: 24 }}>
      <Modal
        open={waybillAuditRunning}
        footer={null}
        closable={false}
        centered
        width={420}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="waybill-audit-spinner" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div>运单审核运行中...</div>
        </div>
      </Modal>
      <Modal
        open={batchCreateProgressOpen}
        footer={null}
        closable={false}
        centered
        width={420}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <Spin size="small" />
          <div>正在批量创建指令...</div>
        </div>
        <div style={{ color: '#595959' }}>
          已完成 {batchCreateProgress.done} / {batchCreateProgress.total}，失败 {batchCreateProgress.failed}
        </div>
      </Modal>
      <Modal
        open={waybillAuditModalOpen}
        title="物流运单解析审核 风险项提示"
        centered
        width={720}
        okText="知道了"
        onOk={() => setWaybillAuditModalOpen(false)}
        onCancel={() => setWaybillAuditModalOpen(false)}
      >
        <div style={{ marginBottom: 8 }}>
          {waybillAuditFileName ? `文件：${waybillAuditFileName}` : ''}
        </div>
        {(waybillAuditIssues || []).map((i, idx) => (
          <div key={`${i.code}-${idx}`} style={{ marginBottom: 6 }}>
            <div style={{ fontWeight: 600 }}>{i.label}</div>
            <div style={{ whiteSpace: 'pre-line', color: '#595959' }}>{i.message}</div>
          </div>
        ))}
      </Modal>
      <Modal
        open={addCountOpen}
        title="新增指令数量"
        okText="确定新增"
        cancelText="取消"
        onOk={confirmAddCount}
        onCancel={() => setAddCountOpen(false)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>新增数量</span>
          <InputNumber
            min={1}
            precision={0}
            step={1}
            value={addCountValue}
            onChange={(v) => setAddCountValue(Number(v || 0))}
            style={{ width: 160 }}
          />
        </div>
      </Modal>
      <Modal
        open={copyModalOpen}
        title="复制生成指令"
        okText="确定复制"
        cancelText="取消"
        onOk={confirmCopy}
        onCancel={() => setCopyModalOpen(false)}
        width={720}
      >
        <div style={{ marginBottom: 12, color: '#595959' }}>
          请选择需要复制的字段
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          {copyFieldGroups.map((group) => {
            const options = copyFieldOptions
              .filter((opt) => group.keys.includes(opt.value))
              .map((opt) => (opt.value === 'need_pack' ? { ...opt, disabled: true } : opt));
            const selected = copyFieldKeys.filter((key) => group.keys.includes(key));
            return (
              <div key={group.title}>
                <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 600 }}>{group.title}</div>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => updateCopyFieldKeysForGroup(group.keys, group.keys)}
                  >
                    全选
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => updateCopyFieldKeysForGroup(group.keys, [])}
                  >
                    取消全选
                  </Button>
                </div>
                <Checkbox.Group
                  value={selected as string[]}
                  options={options}
                  onChange={(values) => updateCopyFieldKeysForGroup(group.keys, values as Array<CopyFieldKey>)}
                />
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>复制份数</span>
          <InputNumber
            min={1}
            precision={0}
            step={1}
            value={copyCount}
            onChange={(v) => setCopyCount(Number(v || 0))}
            style={{ width: 160 }}
          />
        </div>
      </Modal>
      <Modal
        open={batchEditOpen}
        title={(
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
            <span style={{ textAlign: 'left' }}>批量赋值/修改</span>
            <span style={{ textAlign: 'center', color: '#8c8c8c', fontSize: 14 }}>
              所选记录包含{batchEditConfig ? getSelectedWarehouseProjectCounts(batchEditConfig.rows, batchEditConfig.selectedKeys).warehouseCount : 0}个仓库和{batchEditConfig ? getSelectedWarehouseProjectCounts(batchEditConfig.rows, batchEditConfig.selectedKeys).projectCount : 0}个项目
            </span>
            <span />
          </div>
        )}
        okText="确定"
        cancelText="取消"
        onOk={confirmBatchEdit}
        onCancel={() => setBatchEditOpen(false)}
        width={960}
        okButtonProps={{
          disabled: (() => {
            const hasValidFields = batchEditActiveKeys.length === 0 || !batchEditActiveKeys.some((key) => {
              const config = batchFieldConfigMap[key];
              if (!config) return true;
              const value = (batchEditValues as any)[key];
              if (config.type === 'switch' || config.type === 'boolean_select') return !(value === true || value === false);
              if (config.type === 'number') return !Number.isFinite(Number(value));
              return !String(value || '').trim();
            });
            const hasValidPrintConfig = !batchEditPrintConfigEnabled
              || !batchEditPrintConfig.print
              || (typeof batchEditPrintConfig.copies === 'number' && batchEditPrintConfig.copies >= 1
                && !!batchEditPrintConfig.paper_size && !!batchEditPrintConfig.placement);
            const canProceed = hasValidFields && hasValidPrintConfig;
            const hasWork = batchEditActiveKeys.length > 0 || batchEditPrintConfigEnabled;
            return !hasWork || !canProceed;
          })(),
        }}
      >
        <div style={{ marginBottom: 12, color: '#595959' }}>
          请选择需要统一赋值/修改的字段，并输入要设置的值
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '260px 520px', gap: 8 }}>
          {batchEditFields.map((key) => {
            const config = batchFieldConfigMap[key];
            if (!config) return null;
            const active = batchEditActiveKeys.includes(key);
            const value = (batchEditValues as any)[key];
            const liuBlocked = !batchEditLiuAllowed && (key === 'inventory_unit' || key === 'inventory_unit_tag');
            const liuMutualDisabled = key === 'inventory_unit'
              ? batchEditActiveKeys.includes('inventory_unit_tag')
              : key === 'inventory_unit_tag'
              ? batchEditActiveKeys.includes('inventory_unit')
              : false;
            const warehouseProjectActive = batchEditActiveKeys.includes('warehouse') || batchEditActiveKeys.includes('project');
            const liuActive = batchEditActiveKeys.includes('inventory_unit') || batchEditActiveKeys.includes('inventory_unit_tag');
            const crossGroupDisabled = (key === 'inventory_unit' || key === 'inventory_unit_tag')
              ? warehouseProjectActive
              : (key === 'warehouse' || key === 'project')
              ? liuActive
              : false;
            const canEditQuantity = batchEditConfig
              ? selectedRowsHaveUnitAndTag(batchEditConfig.rows, batchEditConfig.selectedKeys)
              : false;
            const quantityMutualDisabled = (key === 'quantity')
              ? (liuActive || warehouseProjectActive)
              : (key === 'inventory_unit' || key === 'inventory_unit_tag' || key === 'warehouse' || key === 'project')
              ? batchEditActiveKeys.includes('quantity')
              : false;
            const quantityBlocked = key === 'quantity' && !canEditQuantity;
            const insuranceAmountBlocked = key === 'insurance_amount'
              && !(batchEditActiveKeys.includes('need_insurance') && !!(batchEditValues as any).need_insurance);
            const isDisabled = !active
              || liuBlocked
              || liuMutualDisabled
              || crossGroupDisabled
              || quantityBlocked
              || quantityMutualDisabled
              || insuranceAmountBlocked;
            return (
              <React.Fragment key={String(key)}>
                <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  <Checkbox
                    checked={active}
                    disabled={liuBlocked || liuMutualDisabled || crossGroupDisabled || quantityBlocked || quantityMutualDisabled || insuranceAmountBlocked}
                    onChange={(e) => toggleBatchEditKey(key, e.target.checked)}
                  >
                    {config.label}
                  </Checkbox>
                </div>
                <div>
                  {config.type === 'input' && (
                    <Input
                      allowClear
                      value={value}
                      disabled={isDisabled}
                      style={{ maxWidth: 520 }}
                      onChange={(e) => updateBatchEditValue(key, e.target.value)}
                    />
                  )}
                  {config.type === 'number' && (
                    <InputNumber
                      min={key === 'insurance_amount' ? 100 : 1}
                      max={key === 'insurance_amount'
                        ? 1000
                        : key === 'quantity'
                        ? getSelectedMinAvailableQty(batchEditConfig?.rows || [], batchEditConfig?.selectedKeys || [])
                        : undefined}
                      precision={0}
                      step={1}
                      value={value}
                      disabled={isDisabled}
                      style={{ width: '100%', maxWidth: 520 }}
                      onChange={(v) => updateBatchEditValue(key, v ?? undefined)}
                    />
                  )}
                  {key === 'quantity' ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      只有所选记录都包含有效的库存单元和库存单元标识,才能批量修改发货数量,且不能超过所有记录的最小可用库存
                    </div>
                  ) : null}
                  {key === 'quantity' && canEditQuantity ? (
                    <div style={{ marginTop: 2, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      当前所选记录最小可用库存{getSelectedMinAvailableQty(batchEditConfig?.rows || [], batchEditConfig?.selectedKeys || []) ?? '-'},发货数量不可超过该值
                    </div>
                  ) : null}
                  {key === 'insurance_amount' ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      最低$100,最高$1000
                    </div>
                  ) : null}
                  {config.type === 'select' && (key === 'inventory_unit' || key === 'inventory_unit_tag') && (
                    <Select
                      allowClear
                      showSearch
                      options={getBatchFieldOptions(key)}
                      value={value}
                      disabled={isDisabled}
                      notFoundContent={key === 'inventory_unit'
                        ? '当前仓库和项目中无可用库存单元'
                        : '当前仓库和项目中无可用库存单元标识'}
                      optionFilterProp="label"
                      style={{ width: '100%', maxWidth: 520 }}
                      onChange={(v) => updateBatchEditValue(key, v)}
                    />
                  )}
                  {config.type === 'select' && key !== 'inventory_unit' && key !== 'inventory_unit_tag' && (
                    <Select
                      allowClear
                      showSearch
                      options={getBatchFieldOptions(key)}
                      value={value}
                      disabled={isDisabled}
                      optionFilterProp="label"
                      style={{ width: '100%', maxWidth: 520 }}
                      dropdownMatchSelectWidth={!(key === 'preferred_carrier' || key === 'carrier')}
                      dropdownStyle={(key === 'preferred_carrier' || key === 'carrier') ? { minWidth: 400 } : undefined}
                      onChange={(v) => updateBatchEditValue(key, v)}
                    />
                  )}
                  {config.type === 'boolean_select' && (
                    <Select
                      allowClear
                      options={[
                        { value: true, label: '是' },
                        { value: false, label: '否' },
                      ]}
                      value={value}
                      disabled={isDisabled}
                      style={{ width: '100%', maxWidth: 200 }}
                      onChange={(v) => updateBatchEditValue(key, v)}
                    />
                  )}
                  {key === 'warehouse' ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      仓库 和 库存单元/库存单元标识不能同时修改,如需修改请分两次执行
                    </div>
                  ) : null}
                  {key === 'project' ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      项目 和 库存单元/库存单元标识不能同时修改,如需修改请分两次执行
                    </div>
                  ) : null}
                  {key === 'inventory_unit' ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      只有所选记录仅包括1个仓库和1个项目时,才可以批量修改,仅显示可选库存单元
                    </div>
                  ) : null}
                  {key === 'inventory_unit_tag' ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                      只有所选记录仅包括1个仓库和1个项目时,才可以批量修改,仅显示可选库存单元标识
                    </div>
                  ) : null}
                </div>
                {key === 'project' ? (
                  <>
                    <div style={{ gridColumn: '1 / span 2', borderBottom: '1px solid #f0f0f0', margin: '6px 0' }} />
                  </>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
        {otherAttachmentPrintable && batchEditConfig && (() => {
          const mainRowKeys = new Set<string>();
          batchEditConfig.selectedKeys.forEach((k) => {
            const row = batchEditConfig.rows.find((r) => r.key === k);
            if (row) {
              const mainKey = row.is_child ? row.parent_key : row.key;
              if (mainKey) mainRowKeys.add(String(mainKey));
            }
          });
          return Array.from(mainRowKeys).some((rk) => (otherFilesByRowKey[rk] || []).length > 0);
        })() ? (
          <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>其他附件打印需求</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Checkbox
                checked={batchEditPrintConfigEnabled}
                onChange={(e) => setBatchEditPrintConfigEnabled(e.target.checked)}
              >
                统一设置打印需求
              </Checkbox>
              {batchEditPrintConfigEnabled ? (
                <>
                  <span style={{ fontSize: 12 }}>是否打印</span>
                  <Switch
                    checked={!!batchEditPrintConfig.print}
                    size="small"
                    onChange={(v) => setBatchEditPrintConfig((p) => ({ ...p, print: v, copies: v ? p.copies : 0, paper_size: v ? p.paper_size : '', placement: v ? p.placement : '' }))}
                  />
                  <Tooltip title="该附件需打印的份数">
                    <InputNumber
                      size="small"
                      min={1}
                      value={batchEditPrintConfig.print ? batchEditPrintConfig.copies : undefined}
                      disabled={!batchEditPrintConfig.print}
                      onChange={(v) => setBatchEditPrintConfig((p) => ({ ...p, copies: Math.max(1, Number(v) || 1) }))}
                      style={{ width: 90 }}
                      placeholder="打印份数"
                    />
                  </Tooltip>
                  <Tooltip title="选择打印使用的纸张尺寸">
                    <Select
                      size="small"
                      value={batchEditPrintConfig.print ? (batchEditPrintConfig.paper_size || undefined) : undefined}
                      disabled={!batchEditPrintConfig.print}
                      onChange={(v) => setBatchEditPrintConfig((p) => ({ ...p, paper_size: v || '' }))}
                      options={PRINT_PAPER_OPTIONS}
                      style={{ width: 160 }}
                      placeholder="选择打印纸"
                      allowClear
                    />
                  </Tooltip>
                  <Tooltip title="打印后放置在包裹内或包裹外">
                    <Select
                      size="small"
                      value={batchEditPrintConfig.print ? (batchEditPrintConfig.placement || undefined) : undefined}
                      disabled={!batchEditPrintConfig.print}
                      onChange={(v) => setBatchEditPrintConfig((p) => ({ ...p, placement: v || '' }))}
                      options={PRINT_PLACEMENT_OPTIONS}
                      style={{ width: 120 }}
                      placeholder="放置位置"
                      allowClear
                    />
                  </Tooltip>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>
      <Modal
        open={trackingBatchOpen}
        title={(() => {
          const rows = trackingBatchConfig
            ? getSelectedMainRows(trackingBatchConfig.rows, trackingBatchConfig.selectedKeys)
            : [];
          const inputCount = trackingBatchText
            .split('\n')
            .map((l) => String(l || '').trim())
            .filter(Boolean).length;
          const canSubmit = rows.length > 0 && rows.length === inputCount;
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto 1fr', gap: 8, alignItems: 'center' }}>
              <span style={{ textAlign: 'left' }}>批量录入物流跟踪号</span>
              <span style={{ textAlign: 'center', color: '#595959', fontSize: 12 }}>
                已选定指令数量{rows.length} | 已输入物流跟踪号{inputCount}
              </span>
              <span
                style={{
                  justifySelf: 'center',
                  fontSize: 12,
                  padding: '2px 8px',
                  borderRadius: 4,
                  border: `1px solid ${canSubmit ? '#52c41a' : '#ff4d4f'}`,
                  background: canSubmit ? '#f6ffed' : '#fff1f0',
                  color: canSubmit ? '#389e0d' : '#cf1322',
                }}
              >
                {canSubmit ? '可执行录入' : '不可执行录入'}
              </span>
              <span />
            </div>
          );
        })()}
        okText="确定"
        cancelText="取消"
        onOk={confirmTrackingBatch}
        onCancel={() => setTrackingBatchOpen(false)}
        width={720}
        okButtonProps={{
          disabled: (() => {
            const rows = trackingBatchConfig
              ? getSelectedMainRows(trackingBatchConfig.rows, trackingBatchConfig.selectedKeys)
              : [];
            const inputCount = trackingBatchText
              .split('\n')
              .map((l) => String(l || '').trim())
              .filter(Boolean).length;
            return !(rows.length > 0 && rows.length === inputCount);
          })(),
        }}
      >
        <div style={{ marginTop: 16 }} />
        {trackingBatchConfig ? (() => {
          const rows = getSelectedMainRows(trackingBatchConfig.rows, trackingBatchConfig.selectedKeys);
          const existingRows = rows.filter((row) => String(row.tracking_no || '').trim());
          if (existingRows.length === 0) return null;
          return (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6, color: '#595959' }}>
                以下{existingRows.length}条记录已存在物流跟踪号,请取消选定,否则新录入的值将覆盖原值.
              </div>
              <div style={{ maxHeight: 160, overflow: 'auto', background: '#fafafa', padding: 8, borderRadius: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 160px 1fr', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
                  <span>指令编号</span>
                  <span>物流承运商</span>
                  <span>物流跟踪号</span>
                </div>
                {existingRows.map((row, idx) => (
                  <div key={`${row.key}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '120px 160px 1fr', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ justifySelf: 'start' }}>
                      <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                        {row.serial_no || '-'}
                      </span>
                    </span>
                    <span>{row.carrier || '-'}</span>
                    <span>{row.tracking_no || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })() : null}
        <div style={{ borderTop: '1px dashed #e5e5e5', marginTop: 16, paddingTop: 16 }} />
        <div style={{ margin: '20px 0 8px', background: '#fffbe6', border: '1px solid #ffe58f', padding: 10, borderRadius: 6, color: '#595959', whiteSpace: 'pre-line' }}>
          {`每行一个物流跟踪号,系统将按顺序依次填入选定的指令记录.
系统将自动忽略空行和首尾空格.
只有当选定的指令记录数量和此处输入的物流跟踪号记录数量一致时,才能执行批量录入.`}
        </div>
        <Input.TextArea
          rows={8}
          value={trackingBatchText}
          onChange={(e) => setTrackingBatchText(e.target.value)}
          placeholder="一行一个物流跟踪号"
        />
      </Modal>
      <Modal
        open={waybillBatchOpen}
        title="批量上传物流运单"
        onCancel={() => setWaybillBatchOpen(false)}
        footer={(() => {
          const rows = waybillBatchConfig
            ? getSelectedMainRows(waybillBatchConfig.rows, waybillBatchConfig.selectedKeys)
            : [];
          const missingTracking = rows.filter((row) => !String(row.tracking_no || '').trim());
          return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                运单审核
                <Switch checked={waybillBatchAuditOn} onChange={setWaybillBatchAuditOn} />
                <HelpHintButton onClick={() => setWaybillBatchAuditInfoOpen(true)}>说明</HelpHintButton>
              </span>
              <span style={{ display: 'inline-flex', gap: 8 }}>
                <Button onClick={() => setWaybillBatchOpen(false)}>取消</Button>
                <Button
                  type="primary"
                  onClick={confirmWaybillBatch}
                  disabled={waybillBatchMatchedRowKeySet.size === 0}
                >
                  确定
                </Button>
              </span>
            </div>
          );
        })()}
        width={1360}
      >
        <div style={{ marginTop: 16 }} />
        {waybillBatchConfig ? (() => {
          const rows = getSelectedMainRows(waybillBatchConfig.rows, waybillBatchConfig.selectedKeys);
          const existingWaybillRows = waybillBatchExistingRowsSnapshot.filter((row) =>
            rows.some((r) => r.key === row.key),
          );
          return (
            <>
              {existingWaybillRows.length > 0 ? (
                <div style={{ marginBottom: 24, paddingBottom: 16 }}>
                  <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#595959' }}>
                    <span>以下{existingWaybillRows.length}条记录已上传运单附件,请取消选定,否则新上传的运单附件将覆盖原附件.</span>
                    <Button
                      size="small"
                      onClick={() =>
                        copyWaybillWarningText(
                          existingWaybillRows,
                          `以下${existingWaybillRows.length}条记录已上传运单附件,请取消选定,否则新上传的运单附件将覆盖原附件.`,
                          true,
                        )
                      }
                    >
                      复制信息
                    </Button>
                  </div>
                  <div style={{ maxHeight: 140, overflow: 'auto', background: '#fafafa', padding: 8, borderRadius: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 160px 1fr 1fr 80px', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
                      <span>指令编号</span>
                      <span>物流承运商</span>
                      <span>物流跟踪号</span>
                      <span>运单附件</span>
                      <span>预览</span>
                    </div>
                    {existingWaybillRows.map((row, idx) => (
                      <div key={`${row.key}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '120px 160px 1fr 1fr 80px', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                        <span style={{ justifySelf: 'start' }}>
                          <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                            {row.serial_no || '-'}
                          </span>
                        </span>
                        <span>{row.carrier || '-'}</span>
                        <span>{row.tracking_no || '-'}</span>
                        <span>{(waybillFilesByRowKey[row.key] || []).map((f) => String((f as any)?.name || '')).filter(Boolean).join(', ') || '-'}</span>
                        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                          {(waybillFilesByRowKey[row.key] || []).map((f, fIdx) => (
                            <Tooltip key={`${row.key}-preview-${fIdx}`} title={String((f as any)?.name || '') || '运单附件'}>
                              <EyeOutlined
                                style={{ color: '#1890ff', cursor: 'pointer' }}
                                onClick={() => openWaybillPreview(f as UploadFile)}
                              />
                            </Tooltip>
                          ))}
                          {(waybillFilesByRowKey[row.key] || []).length === 0 ? '-' : null}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div style={{ marginBottom: 16, background: '#fffbe6', border: '1px solid #ffe58f', padding: 10, borderRadius: 6, color: '#595959', whiteSpace: 'pre-line' }}>
                {`请首先在右侧运单文件列表点击"添加运单文件",支持两种上传模式:原文件上传模式可一次上传多个文件,每个文件不做处理(如多页pdf保持原样不拆分);多页pdf自动拆分模式每次只能上传一个pdf文件,将自动按照页码顺序拆分为多个独立的pdf文件.
当运单文件准备完成,可选择匹配模式执行自动匹配,鼠标悬停匹配模式按钮可查看匹配模式说明.
可在左侧指令列表的"已匹配运单文件"列取消匹配.
可在右侧运单文件列表的操作列执行"手动匹配-单独为此运单文件匹配指令; 按住拖拽-任意调整显示顺序".
当匹配完成,点击底部右侧"确定"按钮将匹配结果写入正式记录.
底部左侧"运单审核"开关用于控制写入正式记录后是否执行运单自动审核,审核项请点击右侧"说明"按钮查看.`}
              </div>
            </>
          );
        })() : null}
        <div style={{ marginTop: 12, paddingTop: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.25fr', gap: 16 }}>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
            <div style={{ marginBottom: 6, fontWeight: 600, color: '#595959', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>已选定指令列表</span>
              <span style={{ fontWeight: 400, fontSize: 12, color: '#8c8c8c' }}>
                共{waybillBatchSelectedRows.length}个指令 | 已匹配{waybillBatchLeftMatchedCount}个 未匹配{waybillBatchSelectedRows.length - waybillBatchLeftMatchedCount}个
              </span>
            </div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              仅显示未匹配记录
              <Switch checked={waybillBatchLeftUnmatchedOnly} onChange={setWaybillBatchLeftUnmatchedOnly} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 120px 1.35fr 200px', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
              <span>指令编号</span>
              <span>物流承运商</span>
              <span>物流跟踪号</span>
              <span>已匹配运单文件</span>
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto' }}>
              {waybillBatchLeftRowsToShow.map((row, idx) => (
                <div key={`${row.key}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '90px 120px 1.35fr 200px', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ justifySelf: 'start' }}>
                    <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                      {row.serial_no || '-'}
                    </span>
                  </span>
                  <span>{row.carrier || '-'}</span>
                  <Tooltip title={row.tracking_no || '-'}>
                    <span style={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.tracking_no || '-'}
                    </span>
                  </Tooltip>
                  {(() => {
                    const matchedFile = waybillBatchRowKeyToFile.get(row.key);
                    const matchedName = matchedFile ? String(matchedFile.name || '-') : '-';
                    if (!matchedFile) {
                      return (
                        <Tooltip title={matchedName}>
                          <span style={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {matchedName}
                          </span>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={matchedName}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%', overflow: 'hidden' }}>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {matchedName}
                          </span>
                          <Tooltip title="预览">
                            <EyeOutlined
                              style={{ color: '#1890ff', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openWaybillPreview(matchedFile);
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="取消匹配">
                            <DeleteOutlined
                              style={{ color: '#ff4d4f', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelWaybillMatchForRow(row.key);
                              }}
                            />
                          </Tooltip>
                        </span>
                      </Tooltip>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
            <div style={{ marginBottom: 6, fontWeight: 600, color: '#595959', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>运单文件列表</span>
              <span style={{ fontWeight: 400, fontSize: 12, color: '#8c8c8c' }}>
                共{waybillBatchFiles.length}个文件 | 已匹配{waybillBatchRightMatchedCount}个 未匹配{waybillBatchFiles.length - waybillBatchRightMatchedCount}个
              </span>
            </div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload
                  multiple={waybillBatchUploadMode !== 'split'}
                  accept={waybillBatchUploadMode === 'split' ? '.pdf' : '.pdf,.png,.jpg,.jpeg'}
                  fileList={waybillBatchFiles}
                  showUploadList={false}
                beforeUpload={(file) => {
                  if (waybillBatchUploadMode === 'split') {
                    void handleWaybillBatchSplitFile(file as File, undefined);
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={({ fileList }) => {
                  handleWaybillBatchUploadChange(fileList);
                }}
                >
                  <Button>添加运单文件</Button>
                </Upload>
                <Segmented
                  size="small"
                  className="waybill-upload-segmented"
                  options={[
                    { label: '原文件上传模式', value: 'normal' },
                    { label: '多页pdf自动拆分模式', value: 'split' },
                  ]}
                  value={waybillBatchUploadMode}
                  onChange={(val) => setWaybillBatchUploadMode(val as 'normal' | 'split')}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button
                  size="small"
                  danger
                  onClick={() => {
                    setWaybillBatchFiles([]);
                    setWaybillBatchMatchedByFileUid({});
                    setWaybillBatchDuplicateFileUidSet({});
                  }}
                  style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
                >
                  清空列表
                </Button>
                仅显示未匹配记录
                <Switch checked={waybillBatchRightUnmatchedOnly} onChange={setWaybillBatchRightUnmatchedOnly} />
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
              <span style={{ color: '#fa8c16', fontSize: 12 }}>请点击匹配模式执行自动匹配:</span>
              <Tooltip title="将按照运单文件列表显示顺序和指令列表显示顺序对应匹配">
                <Button size="small" onClick={handleMatchByListOrder} disabled={waybillBatchRightFilesToShow.length === 0}>
                  按列表显示顺序匹配
                </Button>
              </Tooltip>
              <Tooltip title="将按照运单文件名(移除首尾空格和文件类型扩展名)和指令编号(移除前缀&quot;IM&quot;等,仅保留数字部分)对应匹配,运单文件&quot; 99.pdf&quot;将自动匹配指令编号&quot;IM99&quot;">
                <Button size="small" onClick={handleMatchBySerialFileName} disabled={waybillBatchRightFilesToShow.length === 0}>
                  按指令编号-文件名匹配
                </Button>
              </Tooltip>
              <Tooltip title="将按照运单文件名(移除首尾空格和文件类型扩展名)和指令的物流跟踪号(移除首尾空格,不考虑大小写)对应匹配,运单文件&quot; 920012345.pdf&quot;将自动匹配物流跟踪号为&quot;920012345&quot;的指令">
                <Button size="small" onClick={handleMatchByTrackingFileName} disabled={waybillBatchRightFilesToShow.length === 0}>
                  按物流跟踪号-文件名匹配
                </Button>
              </Tooltip>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '260px 120px 200px', fontWeight: 600, fontSize: 12, marginTop: 8, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
              <span>运单文件</span>
              <span>已匹配指令编号</span>
              <span>操作</span>
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto' }}>
              <DndContext
                sensors={waybillBatchRightSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleWaybillFileDragEnd}
              >
                <SortableContext
                  items={waybillBatchRightFilesToShow.map((file) => file.uid)}
                  strategy={verticalListSortingStrategy}
                >
                  {waybillBatchRightFilesToShow.map((file, idx) => (
                    <SortableWaybillFileRow key={file.uid} file={file} idx={idx} />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={otherBatchOpen}
        title="批量新增其他附件"
        onCancel={() => setOtherBatchOpen(false)}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setOtherBatchOpen(false)}>取消</Button>
            <Button type="primary" onClick={confirmOtherBatch} disabled={otherBatchMatchedRowKeySet.size === 0}>确定</Button>
          </div>
        )}
        width={1360}
      >
        <div style={{ marginTop: 16 }} />
        {otherBatchConfig ? (() => {
          const rowsAll = otherBatchConfig.rows || [];
          const rows = getSelectedMainRows(rowsAll, otherBatchConfig.selectedKeys);
          const existingOtherRows = rows.filter((row) => (otherFilesByRowKey[row.key] || []).length > 0);
          return (
            <>
              {existingOtherRows.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6, color: '#595959', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      以下{existingOtherRows.length}条记录已上传其他附件,新上传的附件将追加到原附件中.同名附件不会被追加.
                    </span>
                    <Button
                      size="small"
                      onClick={() =>
                        copyOtherAttachmentWarningText(
                          existingOtherRows,
                          `以下${existingOtherRows.length}条记录已上传其他附件,新上传的附件将追加到原附件中.同名附件不会被追加.`,
                        )
                      }
                    >
                      复制信息
                    </Button>
                  </div>
                  <div style={{ maxHeight: 140, overflow: 'auto', background: '#fafafa', padding: 8, borderRadius: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
                      <span>指令编号</span>
                      <span>其他附件</span>
                    </div>
                    {existingOtherRows.map((row, idx) => (
                      <div key={`${row.key}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ justifySelf: 'start' }}>
                          <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                            {row.serial_no || '-'}
                          </span>
                        </span>
                        <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                          {(otherFilesByRowKey[row.key] || []).map((f, fIdx) => (
                            <span key={`${row.key}-other-${fIdx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span>{String((f as any)?.name || '') || '-'}</span>
                              <Tooltip title="预览">
                                <EyeOutlined
                                  style={{ color: '#1890ff', cursor: 'pointer', fontSize: 12 }}
                                  onClick={() => openWaybillPreview(f as UploadFile)}
                                />
                              </Tooltip>
                            </span>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div style={{ marginBottom: 16, background: '#fffbe6', border: '1px solid #ffe58f', padding: 10, borderRadius: 6, color: '#595959', whiteSpace: 'pre-line' }}>
                {`请首先在右侧"其他附件文件列表"点击"添加附件文件"
当附件文件准备完成,可选择匹配模式执行自动匹配,鼠标悬停匹配模式按钮可查看匹配模式说明.
可在左侧指令列表的"已匹配附件"列取消匹配.
可在右侧附件文件列表的操作列执行"手动匹配-单独为此运单文件匹配指令; 按住拖拽-任意调整显示顺序".
当匹配完成,点击底部右侧"确定"按钮将匹配结果写入正式记录.`}
              </div>
            </>
          );
        })() : null}
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.4fr', gap: 16 }}>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
            <div style={{ marginBottom: 6, fontWeight: 600, color: '#595959', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>已选定指令列表</span>
              <span style={{ fontWeight: 400, fontSize: 12, color: '#8c8c8c' }}>
                共{otherBatchSelectedRows.length}个指令 | 已匹配{otherBatchLeftMatchedCount}个 未匹配{otherBatchSelectedRows.length - otherBatchLeftMatchedCount}个
              </span>
            </div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              仅显示未匹配记录
              <Switch checked={otherBatchLeftUnmatchedOnly} onChange={setOtherBatchLeftUnmatchedOnly} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1.6fr 1.2fr 1fr', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
              <span>指令编号</span>
              <span>产品信息</span>
              <span>物流跟踪号</span>
              <span style={{ justifySelf: 'start' }}>已匹配附件</span>
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto' }}>
              {otherBatchLeftRowsToShow.map((row, idx) => {
                const matchedFiles = otherBatchRowKeyToFiles.get(row.key) || [];
                const serial = String(row.serial_no || '').trim();
                const info = otherBatchSerialInfoMap.get(serial) || { products: [], tracking: '' };
                return (
                  <div key={`${row.key}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '90px 1.6fr 1.2fr 1fr', fontSize: 12, color: '#595959', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ justifySelf: 'start' }}>
                      <span style={{ background: '#fff7b0', color: '#fa541c', padding: '0 4px', borderRadius: 4, display: 'inline-block' }}>
                        {row.serial_no || '-'}
                      </span>
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {(info.products || []).map((item, pIdx) => (
                        <span key={`${serial}-p-${pIdx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <Tooltip title={item.tag || '-'}>
                            <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                              {item.tag || ''}
                            </span>
                          </Tooltip>
                          <span>× {item.qty || '-'}</span>
                        </span>
                      ))}
                    </span>
                    <Tooltip title={info.tracking || '-'}>
                      <span style={{ display: 'block', maxWidth: 220, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {info.tracking}
                      </span>
                    </Tooltip>
                    {matchedFiles.length === 0 ? (
                      <span style={{ justifySelf: 'start' }}>-</span>
                    ) : (
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 6, justifySelf: 'start', width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                        {matchedFiles.map((f, fIdx) => (
                          <span key={`${row.key}-match-${fIdx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
                            <Tooltip title={String((f as any)?.name || '') || '-'}>
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {String((f as any)?.name || '') || '-'}
                              </span>
                            </Tooltip>
                            <Tooltip title="预览">
                              <EyeOutlined
                                style={{ color: '#1890ff', cursor: 'pointer', fontSize: 12 }}
                                onClick={() => openWaybillPreview(f)}
                              />
                            </Tooltip>
                            <Tooltip title="删除">
                              <DeleteOutlined
                                style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 12 }}
                                onClick={() =>
                                  setOtherBatchMatchedByFileUid((prev) => {
                                    const next = { ...prev };
                                    delete next[f.uid];
                                    return next;
                                  })
                                }
                              />
                            </Tooltip>
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
            <div style={{ marginBottom: 6, fontWeight: 600, color: '#595959', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>其他附件文件列表</span>
              <span style={{ fontWeight: 400, fontSize: 12, color: '#8c8c8c' }}>
                共{otherBatchFiles.length}个文件 | 已匹配{otherBatchRightMatchedCount}个 未匹配{otherBatchFiles.length - otherBatchRightMatchedCount}个
              </span>
            </div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload
                  multiple
                  accept=".pdf,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.doc,.docx"
                  fileList={otherBatchFiles}
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={({ fileList }) => {
                    let next = fileList || [];
                    next = next.filter((f) => {
                      const ext = getFileExt(String(f.name || '')).toLowerCase();
                      return ['pdf', 'xlsx', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx'].includes(ext);
                    });
                    if (next.length !== (fileList || []).length) {
                      message.warning('其他附件仅允许上传 pdf/xlsx/csv/png/jpg/jpeg/gif/doc/docx');
                    }
                    const seen = new Set<string>();
                    const nextDedup = next.filter((f) => {
                      const rawName = String(f.name || '').trim();
                      const ext = getFileExt(rawName).toLowerCase();
                      const baseName = rawName.replace(/\.[^/.]+$/, '').trim().toLowerCase();
                      const key = `${baseName}__${ext}`;
                      if (seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });
                    const prevUids = new Set(otherBatchFiles.map((f) => f.uid));
                    const newlyAdded = nextDedup.filter((f) => !prevUids.has(f.uid));
                    setOtherBatchFiles(nextDedup);
                    if (otherAttachmentPrintable && newlyAdded.length > 0) {
                      setOtherPrintConfigModalFiles(newlyAdded);
                    }
                  }}
                >
                  <Button>添加附件文件</Button>
                </Upload>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button
                  size="small"
                  danger
                  onClick={() => {
                    setOtherBatchFiles([]);
                    setOtherBatchMatchedByFileUid({});
                  }}
                  style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
                >
                  清空列表
                </Button>
                仅显示未匹配记录
                <Switch checked={otherBatchRightUnmatchedOnly} onChange={setOtherBatchRightUnmatchedOnly} />
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
              <span style={{ color: '#fa8c16', fontSize: 12 }}>请点击匹配模式执行自动匹配:</span>
              <Tooltip title="将按照附件文件列表显示顺序和指令列表显示顺序对应匹配">
                <Button
                  size="small"
                  onClick={handleOtherMatchByListOrder}
                  disabled={otherBatchRightFilesToShow.length === 0}
                >
                  按列表显示顺序匹配
                </Button>
              </Tooltip>
              <Tooltip
                title={(
                  <div style={{ maxWidth: 620, whiteSpace: 'pre-line' }}>
                    {`将按文件名(移除首尾空格和扩展名)从头部取数字,直到遇到第一个"-",并与指令编号数字部分匹配.例如文件" 11-theFileName .pdf"将匹配指令"IM11".若文件名不以数字开头或数字与"-"之间存在空格/其他字符(如" a11-theFileName .pdf"或" 11 -theFileName .pdf"),将视为无效不参与匹配.
一句话描述:请使用某个指令的数字编号加"-"作为附件文件名前缀实现自动匹配,如9-fileName1.jpg  9-fileName2.png  9-fileName3.xls都会匹配到IM9这个指令.`}
                  </div>
                )}
              >
                <Button
                  size="small"
                  onClick={handleOtherMatchBySerialFileName}
                  disabled={otherBatchRightFilesToShow.length === 0}
                >
                  按指令编码-文件名匹配
                </Button>
              </Tooltip>
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 140px 260px', fontWeight: 600, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #e8e8e8', paddingBottom: 4, alignItems: 'center' }}>
                <span>其他附件文件</span>
                <span style={{ justifySelf: 'start', display: 'flex', alignItems: 'center' }}>已匹配指令编号</span>
                <span>操作</span>
              </div>
              <div style={{ maxHeight: 220, overflow: 'auto' }}>
                <DndContext
                  sensors={waybillBatchRightSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleOtherFileDragEnd}
                >
                  <SortableContext
                    items={otherBatchRightFilesToShow.map((file) => file.uid)}
                    strategy={verticalListSortingStrategy}
                  >
                    {otherBatchRightFilesToShow.map((file, idx) => (
                      <SortableOtherFileRow key={file.uid} file={file} idx={idx} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={otherPrintConfigModalFiles.length > 0}
        title={otherPrintConfigModalFiles.length > 1 ? `打印需求设置（共${otherPrintConfigModalFiles.length}个文件）` : `打印需求设置 - ${otherPrintConfigModalFiles[0] ? String((otherPrintConfigModalFiles[0] as any)?.name || '附件') : ''}`}
        onCancel={() => setOtherPrintConfigModalFiles([])}
        zIndex={2000}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={() => setOtherPrintConfigModalFiles([])}>完成</Button>
          </div>
        )}
      >
        <div style={{ padding: '8px 0', maxHeight: 400, overflow: 'auto' }}>
          {otherPrintConfigModalFiles.map((file, idx) => {
            const uid = String((file as any)?.uid || idx);
            const cfg = otherAttachPrintConfigMap[uid] || {};
            return (
              <div key={uid} style={{ padding: '12px 0', borderBottom: idx < otherPrintConfigModalFiles.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>{String((file as any)?.name || `附件${idx + 1}`)}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 12 }}>是否打印</span>
                  <Switch checked={!!cfg.print} size="small" onChange={(v) => setOtherAttachPrintConfig(uid, v ? { print: true, explicit_no_print: false } : { print: false, explicit_no_print: true })} />
                  <Tooltip title="该附件需打印的份数">
                    <InputNumber size="small" min={1} value={cfg.print ? cfg.copies : undefined} disabled={!cfg.print} onChange={(v) => setOtherAttachPrintConfig(uid, { copies: Math.max(1, Number(v) || 1) })} style={{ width: 90 }} placeholder="打印份数" />
                  </Tooltip>
                  <Tooltip title="选择打印使用的纸张尺寸">
                    <Select size="small" value={cfg.print ? (cfg.paper_size || undefined) : undefined} disabled={!cfg.print} onChange={(v) => setOtherAttachPrintConfig(uid, { paper_size: v || '' })} options={PRINT_PAPER_OPTIONS} style={{ width: 160 }} placeholder="选择打印纸" allowClear />
                  </Tooltip>
                  <Tooltip title="打印后放置在包裹内或包裹外">
                    <Select size="small" value={cfg.print ? (cfg.placement || undefined) : undefined} disabled={!cfg.print} onChange={(v) => setOtherAttachPrintConfig(uid, { placement: v || '' })} options={PRINT_PLACEMENT_OPTIONS} style={{ width: 120 }} placeholder="放置位置" allowClear />
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
      <Modal
        open={otherManualMatchOpen}
        title="手动匹配指令编号"
        onCancel={() => {
          setOtherManualMatchOpen(false);
          setOtherManualMatchFile(null);
          setOtherManualMatchValue('');
        }}
        onOk={() => {
          if (!otherManualMatchFile) {
            setOtherManualMatchOpen(false);
            return;
          }
          const input = String(otherManualMatchValue || '').trim();
          const digits = input.replace(/\D+/g, '');
          if (!digits) {
            message.warning('请输入指令编号的数字部分');
            return;
          }
          const target = otherBatchSelectedRows.find(
            (row) => getWaybillSerialMatchKey(String(row.serial_no || '')) === digits,
          );
          if (!target) {
            message.warning('未找到对应的指令编号');
            return;
          }
          setOtherBatchMatchedByFileUid((prev) => ({
            ...prev,
            [otherManualMatchFile.uid]: target.key,
          }));
          setOtherManualMatchOpen(false);
          setOtherManualMatchFile(null);
          setOtherManualMatchValue('');
        }}
        okText="匹配"
      >
        <div style={{ marginBottom: 8, color: '#595959', whiteSpace: 'pre-line' }}>
          {`手动将附件文件匹配到指令,当前附件文件 ${String(otherManualMatchFile?.name || '') || '-'}.
请在下方输入需要匹配的指令编号的数字部分,如指令编号IM11,只需输入"11"即可.
若指令已有匹配的附件文件,此次操作不会替换原附件,而是新增附件.`}
        </div>
        <Input
          value={otherManualMatchValue}
          onChange={(e) => setOtherManualMatchValue(e.target.value)}
          placeholder="请输入指令编号的数字部分，例如 11"
          maxLength={32}
        />
      </Modal>
      <Modal
        open={waybillPreviewOpen}
        title={waybillPreviewTitle || '运单文件预览'}
        width={920}
        footer={null}
        onCancel={() => {
          setWaybillPreviewOpen(false);
          if (waybillPreviewUrlRef.current) {
            URL.revokeObjectURL(waybillPreviewUrlRef.current);
            waybillPreviewUrlRef.current = '';
          }
          setWaybillPreviewUrl('');
          setWaybillPreviewTitle('');
          setWaybillPreviewType('none');
          setWaybillPreviewHtml('');
          setWaybillPreviewTable(null);
          setWaybillPreviewLoading(false);
        }}
        destroyOnClose
      >
        <div style={{ minHeight: 420 }}>
          {waybillPreviewLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Spin size="small" />
              <div>预览加载中...</div>
            </div>
          ) : waybillPreviewType === 'image' && waybillPreviewUrl ? (
              <img
                src={waybillPreviewUrl}
                alt={waybillPreviewTitle}
                style={{ maxWidth: '100%', maxHeight: 720, display: 'block', margin: '0 auto' }}
              />
          ) : (waybillPreviewType === 'pdf' || waybillPreviewType === 'file') && waybillPreviewUrl ? (
            <iframe
              src={waybillPreviewUrl}
              title={waybillPreviewTitle}
              style={{ width: '100%', height: 720, border: 'none' }}
            />
          ) : waybillPreviewType === 'table' && waybillPreviewTable ? (
            <div style={{ maxHeight: 720, overflow: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${waybillPreviewTable.headers.length || 1}, minmax(120px, 1fr))`, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #e8e8e8', paddingBottom: 4 }}>
                {(waybillPreviewTable.headers.length > 0 ? waybillPreviewTable.headers : ['']).map((h, idx) => (
                  <span key={`h-${idx}`} style={{ padding: '2px 6px' }}>{h}</span>
                ))}
              </div>
              {waybillPreviewTable.rows.map((row, rIdx) => (
                <div key={`r-${rIdx}`} style={{ display: 'grid', gridTemplateColumns: `repeat(${waybillPreviewTable.headers.length || 1}, minmax(120px, 1fr))`, fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
                  {row.map((cell, cIdx) => (
                    <span key={`c-${rIdx}-${cIdx}`} style={{ padding: '2px 6px', color: '#595959' }}>{cell}</span>
                  ))}
                </div>
              ))}
            </div>
          ) : waybillPreviewType === 'html' && waybillPreviewHtml ? (
            <div style={{ maxHeight: 720, overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: waybillPreviewHtml }} />
          ) : (
            <div style={{ color: '#999', textAlign: 'center' }}>暂无可预览内容</div>
          )}
        </div>
      </Modal>
      <Modal
        open={waybillManualMatchOpen}
        title="手动匹配指令编号"
        onCancel={() => {
          setWaybillManualMatchOpen(false);
          setWaybillManualMatchFile(null);
          setWaybillManualMatchValue('');
        }}
        onOk={confirmWaybillManualMatch}
        okText="匹配"
      >
        <div style={{ marginBottom: 8, color: '#595959', whiteSpace: 'pre-line' }}>
          {`手动将运单文件匹配到指令,当前运单文件 ${String(waybillManualMatchFile?.name || '') || '-' }.
请在下方输入需要匹配的指令编号的数字部分,如指令编号IM11,只需输入"11"即可.
若指令已有匹配的运单文件,此次操作将替换原运单文件.`}
        </div>
        <Input
          value={waybillManualMatchValue}
          onChange={(e) => setWaybillManualMatchValue(e.target.value)}
          placeholder="请输入指令编号的数字部分，例如 11"
          maxLength={32}
        />
      </Modal>
      <Modal
        open={waybillBatchAuditOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
            <span>物流运单解析审核 风险项汇总</span>
            <Button size="small" onClick={copyWaybillBatchAudit}>复制</Button>
          </div>
        )}
        centered
        width={760}
        okText="知道了"
        onOk={() => {
          applyWaybillBatchAuditWarnings();
          setWaybillBatchAuditOpen(false);
        }}
        onCancel={() => {
          applyWaybillBatchAuditWarnings();
          setWaybillBatchAuditOpen(false);
        }}
      >
        {(waybillBatchAuditResults || []).map((item, idx) => (
          <div key={`${item.serial}-${idx}`} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 600 }}>
              指令:{item.serial || '-'} | 文件:{item.fileName || '-'}
            </div>
            <div style={{ color: '#595959' }}>{item.label}</div>
            <div style={{ whiteSpace: 'pre-line', color: '#595959' }}>{item.message}</div>
          </div>
        ))}
      </Modal>
      <Modal
        open={waybillBatchAuditInfoOpen}
        title="运单审核说明"
        onCancel={() => setWaybillBatchAuditInfoOpen(false)}
        footer={<Button onClick={() => setWaybillBatchAuditInfoOpen(false)}>知道了</Button>}
        width={640}
      >
        <div style={{ color: '#595959' }}>
          <div>运单审核打开时将执行以下审核:</div>
          <div>文件类型:pdf png jpg jpeg</div>
          <div>每个指令只能上传一张物流运单</div>
          <div>每个运单文件中只能包含一页pdf或图片</div>
          <div>系统会直接使用4×6英寸热敏打印机打印整张运单,不做任何手动截图</div>
          <div>运单文件中不存在过多的非运单区域,如空白区域或者说明性文字,否则打印出的运单可能过小而造成条码不可扫描</div>
          <div>上传的运单和填入物流跟踪号一致</div>
          <div style={{ marginTop: 20 }}>若文件较多,运单审核会耗时较久,请耐心等候</div>
        </div>
      </Modal>
      <Modal
        open={filterOpen}
        title="筛选与搜索"
        onCancel={() => setFilterOpen(false)}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#595959' }}>
              当前筛选结果：
              {countSerials(currentFilterKey === 'IM' ? immediateFilteredRows : currentFilterKey === 'AP' ? afterPackFilteredRows : warehouseFilteredRows)} 条指令
            </div>
            <div style={{ display: 'inline-flex', gap: 8 }}>
              <Button
                onClick={() =>
                  setCurrentFilters({
                    warehouses: [],
                    needPack: 'all',
                    carriers: [],
                    orderType: 'all',
                    searchProject: '',
                    searchInventoryUnit: '',
                    searchInventoryTag: '',
                    searchRecipient: '',
                    searchTracking: '',
                    searchRefNotes: '',
                  })
                }
              >
                重置
              </Button>
              <Button type="primary" onClick={() => setFilterOpen(false)}>完成</Button>
            </div>
          </div>
        )}
        width={720}
      >
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {currentFilters.warehouses.map((wh) => (
            <Tag key={`wh-${wh}`} closable onClose={() => setCurrentFilters((prev) => ({ ...prev, warehouses: prev.warehouses.filter((v) => v !== wh) }))}>
              仓库:{wh}
            </Tag>
          ))}
          {currentFilters.needPack !== 'all' ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, needPack: 'all' }))}>
              是否需打包:{currentFilters.needPack === 'yes' ? '需要' : '无需'}
            </Tag>
          ) : null}
          {currentFilterKey === 'IM' && currentFilters.carriers.map((c) => (
            <Tag key={`carrier-${c}`} closable onClose={() => setCurrentFilters((prev) => ({ ...prev, carriers: prev.carriers.filter((v) => v !== c) }))}>
              承运商:{c}
            </Tag>
          ))}
          {currentFilters.orderType !== 'all' ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, orderType: 'all' }))}>
              订单类型:{currentFilters.orderType === 'single_one' ? '单款单件' : currentFilters.orderType === 'single_multi' ? '单款多件' : '多款多件'}
            </Tag>
          ) : null}
          {currentFilters.searchProject ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, searchProject: '' }))}>
              项目:{currentFilters.searchProject}
            </Tag>
          ) : null}
          {currentFilters.searchInventoryUnit ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, searchInventoryUnit: '' }))}>
              库存单元:{currentFilters.searchInventoryUnit}
            </Tag>
          ) : null}
          {currentFilters.searchInventoryTag ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, searchInventoryTag: '' }))}>
              库存单元标识:{currentFilters.searchInventoryTag}
            </Tag>
          ) : null}
          {currentFilters.searchRecipient ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, searchRecipient: '' }))}>
              收件人:{currentFilters.searchRecipient}
            </Tag>
          ) : null}
          {currentFilterKey === 'IM' && currentFilters.searchTracking ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, searchTracking: '' }))}>
              物流跟踪号:{currentFilters.searchTracking}
            </Tag>
          ) : null}
          {currentFilters.searchRefNotes ? (
            <Tag closable onClose={() => setCurrentFilters((prev) => ({ ...prev, searchRefNotes: '' }))}>
              参考号/命名/备注:{currentFilters.searchRefNotes}
            </Tag>
          ) : null}
          {(currentFilters.warehouses.length > 0
            || currentFilters.needPack !== 'all'
            || currentFilters.carriers.length > 0
            || currentFilters.orderType !== 'all'
            || currentFilters.searchProject
            || currentFilters.searchInventoryUnit
            || currentFilters.searchInventoryTag
            || currentFilters.searchRecipient
            || currentFilters.searchTracking
            || currentFilters.searchRefNotes) ? (
            <Button size="small" onClick={() =>
              setCurrentFilters({
                warehouses: [],
                needPack: 'all',
                carriers: [],
                orderType: 'all',
                searchProject: '',
                searchInventoryUnit: '',
                searchInventoryTag: '',
                searchRecipient: '',
                searchTracking: '',
                searchRefNotes: '',
              })
            }>
              一键清空
            </Button>
          ) : null}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 12, columnGap: 12 }}>
          <div style={{ color: '#595959' }}>仓库</div>
          <Select
            mode="multiple"
            allowClear
            value={currentFilters.warehouses}
            options={warehouseOptions}
            onChange={(value) => setCurrentFilters((prev) => ({ ...prev, warehouses: value }))}
            placeholder="请选择仓库"
          />

          <div style={{ color: '#595959' }}>是否需打包</div>
          <Select
            value={currentFilters.needPack}
            options={[
              { value: 'all', label: '全部' },
              { value: 'yes', label: '需要打包' },
              { value: 'no', label: '无需打包' },
            ]}
            onChange={(value) => setCurrentFilters((prev) => ({ ...prev, needPack: value }))}
          />

          {currentFilterKey === 'IM' && (
            <>
              <div style={{ color: '#595959' }}>运单所属物流承运商</div>
              <Select
                mode="multiple"
                allowClear
                value={currentFilters.carriers}
                options={providerOptions}
                onChange={(value) => setCurrentFilters((prev) => ({ ...prev, carriers: value }))}
                placeholder="请选择物流承运商"
              />
            </>
          )}

          <div style={{ color: '#595959' }}>订单类型</div>
          <Select
            value={currentFilters.orderType}
            options={[
              { value: 'all', label: '全部' },
              { value: 'single_one', label: '单款单件' },
              { value: 'single_multi', label: '单款多件' },
              { value: 'multi', label: '多款多件' },
            ]}
            onChange={(value) => setCurrentFilters((prev) => ({ ...prev, orderType: value }))}
          />

          <div style={{ color: '#595959' }}>项目</div>
          <Input
            allowClear
            value={currentFilters.searchProject}
            onChange={(e) => setCurrentFilters((prev) => ({ ...prev, searchProject: e.target.value }))}
            placeholder="按项目模糊搜索"
          />

          <div style={{ color: '#595959' }}>库存单元</div>
          <Input
            allowClear
            value={currentFilters.searchInventoryUnit}
            onChange={(e) => setCurrentFilters((prev) => ({ ...prev, searchInventoryUnit: e.target.value }))}
            placeholder="按库存单元编码搜索"
          />

          <div style={{ color: '#595959' }}>库存单元标识</div>
          <Input
            allowClear
            value={currentFilters.searchInventoryTag}
            onChange={(e) => setCurrentFilters((prev) => ({ ...prev, searchInventoryTag: e.target.value }))}
            placeholder="按库存单元标识搜索"
          />

          <div style={{ color: '#595959' }}>收件人姓名</div>
          <Input
            allowClear
            value={currentFilters.searchRecipient}
            onChange={(e) => setCurrentFilters((prev) => ({ ...prev, searchRecipient: e.target.value }))}
            placeholder="按收件人姓名搜索"
          />

          {currentFilterKey === 'IM' && (
            <>
              <div style={{ color: '#595959' }}>物流跟踪号</div>
              <Input
                allowClear
                value={currentFilters.searchTracking}
                onChange={(e) => setCurrentFilters((prev) => ({ ...prev, searchTracking: e.target.value }))}
                placeholder="按物流跟踪号搜索"
              />
            </>
          )}

          <div style={{ color: '#595959' }}>参考号/指令命名/备注</div>
          <Input
            allowClear
            value={currentFilters.searchRefNotes}
            onChange={(e) => setCurrentFilters((prev) => ({ ...prev, searchRefNotes: e.target.value }))}
            placeholder="按参考号、指令命名或备注搜索"
          />
        </div>
      </Modal>
      <style>
        {`
          .serial-bg-0 td { background: #ffe7ba; }
          .serial-bg-1 td { background: #bae7ff; }
          .serial-bg-0 .ant-table-cell-fix-right { background: #ffe7ba !important; }
          .serial-bg-1 .ant-table-cell-fix-right { background: #bae7ff !important; }
          .ant-table-row-selected .ant-table-cell-fix-right { background: #e6f7ff !important; }
          .ellipsis-input input { text-overflow: ellipsis; }
          .waybill-upload-segmented.ant-segmented .ant-segmented-item {
            background: #f5f5f5 !important;
            border: 1px solid #d9d9d9 !important;
            transition: background 0.2s ease, border-color 0.2s ease;
          }
          .waybill-upload-segmented.ant-segmented .ant-segmented-item .ant-segmented-item-label {
            color: rgba(0, 0, 0, 0.25) !important;
            font-size: 12px;
            transition: color 0.2s ease;
          }
          .waybill-upload-segmented.ant-segmented .ant-segmented-item-selected {
            background: #52c41a !important;
            border-color: #52c41a !important;
            box-shadow: inset 0 0 0 1px #237804;
            transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          }
          .waybill-upload-segmented.ant-segmented .ant-segmented-item-selected .ant-segmented-item-label {
            color: #ffffff !important;
            font-size: 12px;
            transition: color 0.2s ease;
          }
          .bulk-main-tabs .ant-tabs-ink-bar,
          .bulk-sub-tabs .ant-tabs-ink-bar {
            display: none !important;
          }
          .bulk-main-tabs .ant-tabs-nav::before,
          .bulk-sub-tabs .ant-tabs-nav::before {
            border-bottom: none !important;
          }
          .bulk-main-tabs .ant-tabs-tab,
          .bulk-sub-tabs .ant-tabs-tab {
            margin: 0 !important;
            padding: 0 !important;
          }
          .bulk-main-tabs .ant-tabs-tab + .ant-tabs-tab,
          .bulk-sub-tabs .ant-tabs-tab + .ant-tabs-tab {
            margin-left: 8px !important;
          }
          .waybill-audit-spinner {
            position: relative;
            width: 22px;
            height: 22px;
            animation: waybill-audit-rotate 1.1s linear infinite;
          }
          .waybill-audit-spinner span {
            position: absolute;
            width: 6px;
            height: 6px;
            background: #1677ff;
            border-radius: 50%;
          }
          .waybill-audit-spinner span:nth-child(1) { top: 0; left: 50%; transform: translate(-50%, 0); }
          .waybill-audit-spinner span:nth-child(2) { right: 0; top: 50%; transform: translate(0, -50%); }
          .waybill-audit-spinner span:nth-child(3) { bottom: 0; left: 50%; transform: translate(-50%, 0); }
          .waybill-audit-spinner span:nth-child(4) { left: 0; top: 50%; transform: translate(0, -50%); }
          @keyframes waybill-audit-rotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ marginBottom: 12, fontSize: 17, color: '#595959' }}>
        请选择批量创建的指令类型
      </div>
      <Tabs
        className="bulk-main-tabs"
        activeKey={activeMainTab}
        onChange={(k) => setActiveMainTab(k as 'dropship' | 'destroy')}
        items={[
          {
            key: 'dropship',
            label: (
              <span style={buildTabPillStyle(activeMainTab === 'dropship', { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff' })}>
                <span>批量创建一件代发指令</span>
                <Badge count={dropshipMainCount} showZero style={{ backgroundColor: activeMainTab === 'dropship' ? '#1890ff' : '#bfbfbf' }} />
              </span>
            ),
            children: (
              <Card
                title={(
                  <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                    请按照物流运单提供方式和运单上传时机选择不同的页面批量创建指令,也可使用右侧的“使用excel批量创建”按钮(可先下载'excel批量创建模板').
                  </span>
                )}
                extra={
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      onClick={() => {
                        const importPath = location.pathname.replace(/\/instructions\/bulk-create$/, '/instructions/import');
                        const params = new URLSearchParams();
                        params.set('scenario', activeTabKey);
                        navigate(`${importPath}?${params.toString()}`);
                      }}
                    >
                      使用excel批量创建
                    </Button>
                  </div>
                }
              >
                <Tabs
                  className="bulk-sub-tabs"
                  activeKey={activeTabKey}
                  onChange={(key) => setActiveTabKey(key as typeof activeTabKey)}
                  items={[
                    {
                      key: 'upload_immediate',
                      label: (
                        <span style={buildTabPillStyle(activeTabKey === 'upload_immediate', { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff' }, true)}>
                          <span>由客户提供运单-立即上传</span>
                          <Badge count={immediateSerialCount} showZero style={{ backgroundColor: activeTabKey === 'upload_immediate' ? '#1890ff' : '#bfbfbf' }} />
                        </span>
                      ),
                      children: (
                        <>
                          <div style={{ marginBottom: 12, color: '#8c8c8c', background: '#fff7e6', padding: 12, borderRadius: 6 }}>
                            <div>适用于由客户提供物流运单,并在创建指令时提交运单附件.</div>
                            <div>对于需要打包的代发指令(一单一件且无物流包装 或者 一单多件),请自行预估包裹重量和尺寸,仓库默认使用免费包材打包.</div>
                            <div>必须提交附件:物流运单. 可打开"运单审核"开关,当上传运单时会自动审核运单附件风险并提示.</div>
                            <div>可执行多种批量操作:批量修改/赋值,批量录入物流跟踪号,批量上传物流运单,批量新增其他附件,批量删除.</div>
                            <div>上传"其他附件"后,可以使用"批量赋值/修改"功能为这些附件批量设置打印需求,如打印装箱单/发票并放入包裹内或打印箱标并贴在包裹外等.</div>
                            <div>请使用操作列"复制生成指令"功能,一次性复制生成多个发货产品相同的指令,适用于爆款产品.</div>
                            <div>指令列表编辑完成后,请点击右上角"开始批量创建"按钮生成正式指令.</div>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                              <Button
                                style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
                                onClick={() =>
                                  openAddCountModal({
                                    setter: setImmediateRows,
                                    keyPrefix: 'immediate',
                                    keySeqRef: immediateKeySeq,
                                    serialPrefix: 'IM',
                                    serialSeqRef: immediateSerialSeq,
                                    defaults: {
                                      warehouse: defaultWarehouseCode,
                                      quantity: 1,
                                      waybill_source: '上传运单',
                                      waybill_submit_mode: '立即上传',
                                      country: 'USA',
                                    },
                                  })
                                }
                              >
                                新增指令
                              </Button>
                            </span>
                            <span style={{ marginLeft: 4, flex: 1, textAlign: 'center', display: 'inline-flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                style={{
                                  border: '1px solid #91d5ff',
                                  background: '#e6f7ff',
                                  color: '#096dd9',
                                  fontSize: 15,
                                  padding: '2px 10px',
                                }}
                              >
                                统计: {immediateStats.rowCount}行 | {immediateStats.warehouseCount}个仓库 | {immediateStats.projectCount}个项目 | {immediateStats.unitKindCount}种库存单元
                              </Button>
                              <Button
                                style={{
                                  border: '1px solid #ffd591',
                                  background: '#fff7e6',
                                  color: '#d46b08',
                                  fontSize: 15,
                                  padding: '2px 10px',
                                }}
                              >
                                指令属性: 单款单件 {immediateStats.singleOneOrders}单{immediateStats.singleOneQty}件 | 单款多件 {immediateStats.singleMultiOrders}单{immediateStats.singleMultiQty}件 | 多款多件 {immediateStats.multiOrders}单{immediateStats.multiQty}件
                              </Button>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <Button
                                style={{
                                  border: '1px solid #b7eb8f',
                                  background: '#f6ffed',
                                  color: '#389e0d',
                                  fontSize: 16,
                                  padding: '2px 10px',
                                }}
                              >
                                共{immediateStats.orderCount}个指令
                              </Button>
                              <Button
                                style={{
                                  border: '1px solid #ffa39e',
                                  background: '#fff1f0',
                                  color: '#cf1322',
                                  fontSize: 16,
                                  padding: '2px 10px',
                                }}
                              >
                                共{immediateStats.totalQty}件产品
                              </Button>
                              <Button
                                type="primary"
                                loading={batchCreateLoading}
                                disabled={immediateSerialCount === 0 || batchCreateLoading}
                                onClick={() => handleBatchCreate(immediateRows, immediateRequiredKeys)}
                              >
                                开始批量创建
                              </Button>
                            </span>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              仅显示必填项
                              <Switch
                                checked={immediateRequiredOnly}
                                onChange={setImmediateRequiredOnly}
                              />
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              运单审核
                              <Switch
                                checked={immediateWaybillAuditOn}
                                onChange={setImmediateWaybillAuditOn}
                              />
                              <span style={{ color: '#bfbfbf', margin: '0 20px' }}>|</span>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                disabled={immediateSelectedKeys.length === 0}
                                onClick={() =>
                                  openBatchEditModal({
                                    setter: setImmediateRows,
                                    selectedKeys: immediateSelectedKeys,
                                    rows: immediateRows,
                                    requiredOnly: immediateRequiredOnly,
                                    requiredKeys: immediateRequiredKeys,
                                  })
                                }
                              >
                                批量赋值/修改
                              </Button>
                              <Button
                                disabled={immediateSelectedKeys.length === 0}
                                onClick={() =>
                                  openTrackingBatchModal({
                                    setter: setImmediateRows,
                                    selectedKeys: immediateSelectedKeys,
                                    rows: immediateRows,
                                  })
                                }
                              >
                                批量录入物流跟踪号
                              </Button>
                              <Button
                                disabled={immediateSelectedKeys.length === 0}
                                onClick={() =>
                                  openWaybillBatchModal({
                                    selectedKeys: immediateSelectedKeys,
                                    rows: immediateRows,
                                  })
                                }
                              >
                                批量上传物流运单
                              </Button>
                              <Button
                                disabled={immediateSelectedKeys.length === 0}
                                onClick={() =>
                                  openOtherBatchModal({
                                    selectedKeys: immediateSelectedKeys,
                                    rows: immediateRows,
                                  })
                                }
                              >
                                批量新增其他附件
                              </Button>
                              <Button
                                danger
                                disabled={immediateSelectedKeys.length === 0}
                                onClick={() =>
                                  removeSelectedRows(
                                    setImmediateRows,
                                    immediateSelectedKeys,
                                    setImmediateSelectedKeys,
                                    'IM',
                                    immediateSerialSeq,
                                  )
                                }
                              >
                                批量删除
                              </Button>
                            </span>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Button onClick={() => setFilterOpen(true)}>筛选与搜索</Button>
                            {hasAnyFilter(filtersByTab.IM) ? (
                              <Button size="small" onClick={() => setFiltersByTab((prev) => ({ ...prev, IM: { ...defaultFilters } }))}>
                                清空所有筛选
                              </Button>
                            ) : null}
                            <span style={{ color: '#595959' }}>当前筛选条件:</span>
                            {hasAnyFilter(filtersByTab.IM)
                              ? renderFilterTags(filtersByTab.IM, (updater) =>
                                setFiltersByTab((prev) => ({
                                  ...prev,
                                  IM: typeof updater === 'function' ? (updater as (v: FilterState) => FilterState)(prev.IM) : updater,
                                }))
                              )
                              : <span style={{ color: '#bfbfbf' }}>无</span>}
                          </div>
                          <Table
                            bordered
                            size="small"
                            pagination={{
                              current: immediatePage,
                              pageSize: immediatePageSize,
                              total: immediateFilteredRows.length,
                              showSizeChanger: true,
                              pageSizeOptions: ['20', '50', '100', '200'],
                              showQuickJumper: true,
                              showTotal: (total, range) => `第 ${range[0]} - ${range[1]} 条， 共 ${total} 条记录`,
                              onChange: (page, pageSize) => {
                                setImmediatePage(page);
                                setImmediatePageSize(pageSize);
                              },
                            }}
                            tableLayout="fixed"
                            dataSource={immediateFilteredRows}
                            columns={immediateColumns as any}
                            scroll={{ x: 1600 }}
                            rowClassName={(row: DropshipRow) =>
                              immediateSerialClassMap.get(String(row.serial_no || '').trim()) || ''
                            }
                            rowSelection={{
                              selectedRowKeys: immediateSelectedKeys,
                              onChange: setImmediateSelectedKeys,
                            }}
                          />
                        </>
                      ),
                    },
                    {
                      key: 'upload_after_pack',
                      label: (
                        <span style={buildTabPillStyle(activeTabKey === 'upload_after_pack', { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff' }, true)}>
                          <span>由客户提供运单-先申请包装数据</span>
                          <Badge count={afterPackSerialCount} showZero style={{ backgroundColor: activeTabKey === 'upload_after_pack' ? '#1890ff' : '#bfbfbf' }} />
                        </span>
                      ),
                      children: (
                        <>
                          <div style={{ marginBottom: 12, color: '#8c8c8c', background: '#fff7e6', padding: 12, borderRadius: 6 }}>
                            <div>创建代发指令时无需提交运单附件,仓库将在打包后回填包裹精确重量和尺寸(可至指令详情页查看结果),然后由客户依据包装结果追加出库指令并同时上传运单.</div>
                            <div>对于需要打包的代发指令(一单一件且无物流包装 或者 一单多件),请选定包装偏好,若未选择包装偏好,仓库默认使用免费包材打包.</div>
                            <div>可执行多种批量操作:批量修改/赋值,批量新增其他附件,批量删除.</div>
                            <div>上传"其他附件"后,可以使用"批量赋值/修改"功能为这些附件批量设置打印需求,如打印装箱单/发票并放入包裹内或打印箱标并贴在包裹外等.</div>
                            <div>请使用操作列"复制生成指令"功能,一次性复制生成多个发货产品相同的指令,适用于爆款产品.</div>
                            <div>指令列表编辑完成后,请点击右上角"开始批量创建"按钮生成正式指令.</div>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                              <Button
                                style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
                                onClick={() =>
                                  openAddCountModal({
                                    setter: setAfterPackRows,
                                    keyPrefix: 'after-pack',
                                    keySeqRef: afterPackKeySeq,
                                    serialPrefix: 'AP',
                                    serialSeqRef: afterPackSerialSeq,
                                    defaults: {
                                      warehouse: defaultWarehouseCode,
                                      quantity: 1,
                                      waybill_source: '上传运单',
                                      waybill_submit_mode: '先申请包装数据',
                                      country: 'USA',
                                    },
                                  })
                                }
                              >
                                新增指令
                              </Button>
                            </span>
                            <span style={{ marginLeft: 4, flex: 1, textAlign: 'center', display: 'inline-flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                style={{
                                  border: '1px solid #91d5ff',
                                  background: '#e6f7ff',
                                  color: '#096dd9',
                                  fontSize: 15,
                                  padding: '2px 10px',
                                }}
                              >
                                统计: {afterPackStats.rowCount}行 | {afterPackStats.warehouseCount}个仓库 | {afterPackStats.projectCount}个项目 | {afterPackStats.unitKindCount}种库存单元
                              </Button>
                              <Button
                                style={{
                                  border: '1px solid #ffd591',
                                  background: '#fff7e6',
                                  color: '#d46b08',
                                  fontSize: 15,
                                  padding: '2px 10px',
                                }}
                              >
                                指令属性: 单款单件 {afterPackStats.singleOneOrders}单{afterPackStats.singleOneQty}件 | 单款多件 {afterPackStats.singleMultiOrders}单{afterPackStats.singleMultiQty}件 | 多款多件 {afterPackStats.multiOrders}单{afterPackStats.multiQty}件
                              </Button>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <Button
                                style={{
                                  border: '1px solid #b7eb8f',
                                  background: '#f6ffed',
                                  color: '#389e0d',
                                  fontSize: 16,
                                  padding: '2px 10px',
                                }}
                              >
                                共{afterPackStats.orderCount}个指令
                              </Button>
                              <Button
                                style={{
                                  border: '1px solid #ffa39e',
                                  background: '#fff1f0',
                                  color: '#cf1322',
                                  fontSize: 16,
                                  padding: '2px 10px',
                                }}
                              >
                                共{afterPackStats.totalQty}件产品
                              </Button>
                              <Button
                                type="primary"
                                loading={batchCreateLoading}
                                disabled={afterPackSerialCount === 0 || batchCreateLoading}
                                onClick={() => handleBatchCreate(afterPackRows, afterPackRequiredKeys)}
                              >
                                开始批量创建
                              </Button>
                            </span>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              仅显示必填项
                              <Switch
                                checked={afterPackRequiredOnly}
                                onChange={setAfterPackRequiredOnly}
                              />
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                disabled={afterPackSelectedKeys.length === 0}
                                onClick={() =>
                                  openBatchEditModal({
                                    setter: setAfterPackRows,
                                    selectedKeys: afterPackSelectedKeys,
                                    rows: afterPackRows,
                                    requiredOnly: afterPackRequiredOnly,
                                    requiredKeys: afterPackRequiredKeys,
                                  })
                                }
                              >
                                批量赋值/修改
                              </Button>
                              <Button
                                disabled={afterPackSelectedKeys.length === 0}
                                onClick={() =>
                                  openOtherBatchModal({
                                    selectedKeys: afterPackSelectedKeys,
                                    rows: afterPackRows,
                                  })
                                }
                              >
                                批量新增其他附件
                              </Button>
                              <Button
                                danger
                                disabled={afterPackSelectedKeys.length === 0}
                                onClick={() =>
                                  removeSelectedRows(
                                    setAfterPackRows,
                                    afterPackSelectedKeys,
                                    setAfterPackSelectedKeys,
                                    'AP',
                                    afterPackSerialSeq,
                                  )
                                }
                              >
                                批量删除
                              </Button>
                            </span>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Button onClick={() => setFilterOpen(true)}>筛选与搜索</Button>
                            {hasAnyFilter(filtersByTab.AP) ? (
                              <Button size="small" onClick={() => setFiltersByTab((prev) => ({ ...prev, AP: { ...defaultFilters } }))}>
                                清空所有筛选
                              </Button>
                            ) : null}
                            <span style={{ color: '#595959' }}>当前筛选条件:</span>
                            {hasAnyFilter(filtersByTab.AP)
                              ? renderFilterTags(filtersByTab.AP, (updater) =>
                                setFiltersByTab((prev) => ({
                                  ...prev,
                                  AP: typeof updater === 'function' ? (updater as (v: FilterState) => FilterState)(prev.AP) : updater,
                                }))
                              )
                              : <span style={{ color: '#bfbfbf' }}>无</span>}
                          </div>
                          <Table
                            bordered
                            size="small"
                            pagination={{
                              current: afterPackPage,
                              pageSize: afterPackPageSize,
                              total: afterPackFilteredRows.length,
                              showSizeChanger: true,
                              pageSizeOptions: ['20', '50', '100', '200'],
                              showQuickJumper: true,
                              showTotal: (total, range) => `第 ${range[0]} - ${range[1]} 条， 共 ${total} 条记录`,
                              onChange: (page, pageSize) => {
                                setAfterPackPage(page);
                                setAfterPackPageSize(pageSize);
                              },
                            }}
                            tableLayout="fixed"
                            dataSource={afterPackFilteredRows}
                            columns={afterPackColumns as any}
                            scroll={{ x: 900 }}
                            rowClassName={(row: DropshipRow) =>
                              afterPackSerialClassMap.get(String(row.serial_no || '').trim()) || ''
                            }
                            rowSelection={{
                              selectedRowKeys: afterPackSelectedKeys,
                              onChange: setAfterPackSelectedKeys,
                            }}
                          />
                        </>
                      ),
                    },
                    {
                      key: 'warehouse_provide',
                      label: (
                        <span style={buildTabPillStyle(activeTabKey === 'warehouse_provide', { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff' }, true)}>
                          <span>由仓库提供运单</span>
                          <Badge count={warehouseSerialCount} showZero style={{ backgroundColor: activeTabKey === 'warehouse_provide' ? '#1890ff' : '#bfbfbf' }} />
                        </span>
                      ),
                      children: (
                        <>
                          <div style={{ marginBottom: 12, color: '#8c8c8c', background: '#fff7e6', padding: 12, borderRadius: 6 }}>
                            <div>适用于由仓库购买运单并上传.会产生物流相关费用,如运费 保险费等.</div>
                            <div>仓库将打包(如需)并根据客户提供的收件人地址信息购买运单并发货.</div>
                            <div>若未指定偏好物流商,仓库默认将选择最低运费物流渠道发货.</div>
                            <div>可执行多种批量操作:批量修改/赋值,批量新增其他附件,批量删除.</div>
                            <div>上传"其他附件"后,可以使用"批量赋值/修改"功能为这些附件批量设置打印需求,如打印装箱单/发票并放入包裹内或打印箱标并贴在包裹外等.</div>
                            <div>请使用操作列&quot;复制生成指令&quot;功能,一次性复制生成多个发货产品相同的指令,适用于爆款产品.</div>
                            <div>指令列表编辑完成后,请点击右上角&quot;开始批量创建&quot;按钮生成正式指令.</div>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                              <Button
                                style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
                                onClick={() =>
                                  openAddCountModal({
                                    setter: setWarehouseRows,
                                    keyPrefix: 'warehouse',
                                    keySeqRef: warehouseKeySeq,
                                    serialPrefix: 'WH',
                                    serialSeqRef: warehouseSerialSeq,
                                    defaults: {
                                      warehouse: defaultWarehouseCode,
                                      quantity: 1,
                                      waybill_source: '仓库提供',
                                      waybill_submit_mode: '',
                                      country: 'USA',
                                    },
                                  })
                                }
                              >
                                新增指令
                              </Button>
                            </span>
                            <span style={{ marginLeft: 4, flex: 1, textAlign: 'center', display: 'inline-flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                style={{
                                  border: '1px solid #91d5ff',
                                  background: '#e6f7ff',
                                  color: '#096dd9',
                                  fontSize: 15,
                                  padding: '2px 10px',
                                }}
                              >
                                统计: {warehouseStats.rowCount}行 | {warehouseStats.warehouseCount}个仓库 | {warehouseStats.projectCount}个项目 | {warehouseStats.unitKindCount}种库存单元
                              </Button>
                              <Button
                                style={{
                                  border: '1px solid #ffd591',
                                  background: '#fff7e6',
                                  color: '#d46b08',
                                  fontSize: 15,
                                  padding: '2px 10px',
                                }}
                              >
                                指令属性: 单款单件 {warehouseStats.singleOneOrders}单{warehouseStats.singleOneQty}件 | 单款多件 {warehouseStats.singleMultiOrders}单{warehouseStats.singleMultiQty}件 | 多款多件 {warehouseStats.multiOrders}单{warehouseStats.multiQty}件
                              </Button>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <Button
                                style={{
                                  border: '1px solid #b7eb8f',
                                  background: '#f6ffed',
                                  color: '#389e0d',
                                  fontSize: 16,
                                  padding: '2px 10px',
                                }}
                              >
                                共{warehouseStats.orderCount}个指令
                              </Button>
                              <Button
                                style={{
                                  border: '1px solid #ffa39e',
                                  background: '#fff1f0',
                                  color: '#cf1322',
                                  fontSize: 16,
                                  padding: '2px 10px',
                                }}
                              >
                                共{warehouseStats.totalQty}件产品
                              </Button>
                              <Button
                                type="primary"
                                loading={batchCreateLoading}
                                disabled={warehouseSerialCount === 0 || batchCreateLoading}
                                onClick={() => handleBatchCreate(warehouseRows, warehouseRequiredKeys)}
                              >
                                开始批量创建
                              </Button>
                            </span>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              仅显示必填项
                              <Switch
                                checked={warehouseRequiredOnly}
                                onChange={setWarehouseRequiredOnly}
                              />
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                disabled={warehouseSelectedKeys.length === 0}
                                onClick={() =>
                                  openBatchEditModal({
                                    setter: setWarehouseRows,
                                    selectedKeys: warehouseSelectedKeys,
                                    rows: warehouseRows,
                                    requiredOnly: warehouseRequiredOnly,
                                    requiredKeys: warehouseRequiredKeys,
                                  })
                                }
                              >
                                批量赋值/修改
                              </Button>
                              <Button
                                disabled={warehouseSelectedKeys.length === 0}
                                onClick={() =>
                                  openOtherBatchModal({
                                    selectedKeys: warehouseSelectedKeys,
                                    rows: warehouseRows,
                                  })
                                }
                              >
                                批量新增其他附件
                              </Button>
                              <Button
                                danger
                                disabled={warehouseSelectedKeys.length === 0}
                                onClick={() =>
                                  removeSelectedRows(
                                    setWarehouseRows,
                                    warehouseSelectedKeys,
                                    setWarehouseSelectedKeys,
                                    'WH',
                                    warehouseSerialSeq,
                                  )
                                }
                              >
                                批量删除
                              </Button>
                            </span>
                          </div>
                          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Button onClick={() => setFilterOpen(true)}>筛选与搜索</Button>
                            {hasAnyFilter(filtersByTab.WH) ? (
                              <Button size="small" onClick={() => setFiltersByTab((prev) => ({ ...prev, WH: { ...defaultFilters } }))}>
                                清空所有筛选
                              </Button>
                            ) : null}
                            <span style={{ color: '#595959' }}>当前筛选条件:</span>
                            {hasAnyFilter(filtersByTab.WH)
                              ? renderFilterTags(filtersByTab.WH, (updater) =>
                                setFiltersByTab((prev) => ({
                                  ...prev,
                                  WH: typeof updater === 'function' ? (updater as (v: FilterState) => FilterState)(prev.WH) : updater,
                                }))
                              )
                              : <span style={{ color: '#bfbfbf' }}>无</span>}
                          </div>
                          <Table
                            bordered
                            size="small"
                            pagination={{
                              current: warehousePage,
                              pageSize: warehousePageSize,
                              total: warehouseFilteredRows.length,
                              showSizeChanger: true,
                              pageSizeOptions: ['20', '50', '100', '200'],
                              showQuickJumper: true,
                              showTotal: (total, range) => `第 ${range[0]} - ${range[1]} 条， 共 ${total} 条记录`,
                              onChange: (page, pageSize) => {
                                setWarehousePage(page);
                                setWarehousePageSize(pageSize);
                              },
                            }}
                            tableLayout="fixed"
                            dataSource={warehouseFilteredRows}
                            columns={warehouseColumns as any}
                            scroll={{ x: 1500 }}
                            rowClassName={(row: DropshipRow) =>
                              warehouseSerialClassMap.get(String(row.serial_no || '').trim()) || ''
                            }
                            rowSelection={{
                              selectedRowKeys: warehouseSelectedKeys,
                              onChange: setWarehouseSelectedKeys,
                            }}
                          />
                        </>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'destroy',
            label: (
              <span style={buildTabPillStyle(activeMainTab === 'destroy', { color: '#fa8c16', border: '#ffd591', bg: '#fff7e6' })}>
                <span>批量创建销毁指令</span>
                <Badge count={0} showZero style={{ backgroundColor: activeMainTab === 'destroy' ? '#fa8c16' : '#bfbfbf' }} />
              </span>
            ),
            children: (
              <Card>
                <div style={{ color: '#595959' }}>占位页面，后续补充批量创建功能。</div>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default InstructionBulkCreatePage;
export {};
